import { AdviceMode } from './modes/AdviceMode.js';
import { DiscoveryMode } from './modes/DiscoveryMode.js';
import { TreatmentMode } from './modes/TreatmentMode.js';
import { OrientationMode } from './modes/OrientationMode.js';
import { IntakeMode } from './modes/IntakeMode.js';
import { ActionRouter, DURABLE_PROPOSAL_GROUP_IDS } from './ActionRouter.js';
import { detectSessionIntent } from '../utils/intentDetection.js';
import { sessionManager } from '../core/SessionManager.js';
import { API, API_URL, fetchWithTimeout } from '../core/api.js';
import page from '../vendor/page.js';
import { resolveFoundationIntro } from './foundationIntros.js';
import {
  resolveFoundationCompletionPreArtifact,
  resolveFoundationCompletionPostArtifact,
  resolveFoundationCompletionButtons,
  resolvePathCompletionButtons,
  resolvePathCompletionOptionsList,
  resolveSharingInvitationMessage,
  resolveSharingAcknowledgement,
  resolveSharingDeclineMessage,
  resolveSharingAbandonMessage
} from './foundationCompletion.js';
// NOT_ACTIONABLE_STATES is IMPORTED, never re-declared: phase 2's readiness
// check and the launcher's own click-time gate must reject exactly the same
// states, or phase 2 mounts an offer the launcher then refuses with a "still
// preparing" notice.
import { destroySharingCeremony, NOT_ACTIONABLE_STATES } from '../chat/sharingCeremonyLauncher.js';
import {
  ledgerFromStatus,
  gestaltGroupId,
  needsReviewCount,
  queueScope
} from '../sections/partnerSharing/sharingLedgerModel.js';
import { goToUpgrade } from '../utils/upgradeNavigation.js';
import { buildGuideAnnouncementBlock } from '../components/guideAnnouncementCard.js';
import { buildSharingInvitationCardBlock } from '../components/sharingInvitationCard.js';
import { applyAdviceCreateMenu, refreshAdviceCreateMenu } from '../core/adviceCreateMenu.js';

/**
 * In-input button group that owns the chat input while a questionnaire is being
 * offered, per mode. Opening the questionnaire panel tears this group down so the
 * input collapses back to its single-row height under the disabled overlay.
 *
 * Modes absent from this map offer questionnaires without an in-input group, so
 * there is nothing to tear down.
 * @type {Object<string, string>}
 */
const QUESTIONNAIRE_INPUT_GROUP_IDS = {
  DISCOVERY: 'questionnaire-actions',
  ORIENTATION: 'orientation-questionnaire-actions'
};

/**
 * Cap on phase 2's live readiness read. `DiscoveryAPI` uses raw `fetch` with
 * no AbortController, so a hung request would otherwise hold the ceremony open
 * forever — and phase 3 with it. On timeout the decision beat is skipped
 * outright (D1: no "yes → sorry, not ready" beat) and the user gets their
 * completion actions.
 * @type {number}
 */
const SHARING_READINESS_TIMEOUT_MS = 3000;

/**
 * Mode-aware orchestration layer for chat system
 * Manages operational modes, message routing, and response generation
 *
 * Integrates with SessionManager for backend session state
 */
export class ModeOrchestrator {
  /**
   * Config table for "Discuss X" launches from a library card (pattern,
   * framework) — an artifact-discussion entry. Identity only crosses; the
   * backend reloads the item server-side (see `AdviceStrategy`'s per-entry
   * context builders). One data-driven navigation branch below reads this
   * table, so a new artifact type is one row here, not new branch logic.
   * @type {Object<string, {entryType: string, openerTemplate: (sourceItem: Object) => string, metadataFields: (itemId: string) => Object}>}
   */
  static ARTIFACT_DISCUSSION_ENTRIES = {
    discuss_pattern: {
      entryType: 'discuss_pattern',
      openerTemplate: (sourceItem) => `I'd like to explore this pattern: "${sourceItem?.name}"`,
      metadataFields: (itemId) => ({ source: 'discuss_pattern', itemId })
    },
    discuss_framework: {
      entryType: 'discuss_framework',
      // "assessment result" is the plain-English noun for a questionnaire —
      // the retired INSIGHT `framework` variant's INSIGHT_VARIANT_NOUNS
      // mapping used the same noun so nothing leaks as internal jargon.
      openerTemplate: (sourceItem) => `I'd like to explore this assessment result: "${sourceItem?.title}"`,
      metadataFields: (itemId) => ({ source: 'discuss_framework', itemId })
    }
  };

  /**
   * @param {ChatManager} chatManager - Chat presentation manager (public API)
   * @param {EventBus} eventBus - Global event bus
   * @param {string} userId - Current user ID
   * @param {string|null} coupleId - Current couple ID (null if user not in couple)
   * @param {HeaderContentService|null} headerContentService - Header content service (optional)
   *
   * Note: sessionId is NOT passed - retrieved dynamically from SessionManager
   */
  constructor(chatManager, eventBus, userId, coupleId = null, headerContentService = null) {
    this.chatManager = chatManager;
    this.messageList = chatManager.messageList; // For legacy compatibility
    this.eventBus = eventBus;
    this.userId = userId;
    this.coupleId = coupleId;

    // Mode registry (uppercase - matches backend)
    this.modes = new Map([
      ['ADVICE', new AdviceMode()],
      ['DISCOVERY', new DiscoveryMode(coupleId)],
      ['TREATMENT', new TreatmentMode()],
      ['ORIENTATION', new OrientationMode()],
      ['INTAKE', new IntakeMode()]
    ]);

    // Current state
    this.currentMode = null;
    this.modeHistory = [];
    this._adviceIntentRecoveryFiredKeys = new Set();

    // Sub-components
    this.actionRouter = new ActionRouter(this);
    this.headerContentService = headerContentService;

    // Initialize mode state without creating session
    // Session will be created lazily on first user action
    // Default to null - mode will be determined by intent detection on first message
    this.currentMode = null;

    // Setup event listeners
    this.setupEventListeners();

    // Discovery entry tracking
    this._currentDiscoveryPathId = null;

    /**
     * Phase 3 of the completion ceremony — the "what's next" options bubble
     * and the completion button group — deferred while the phase-2 sharing
     * decision owns the input region. Held, never consumed: input-mounted
     * groups use `replaceChildren`, so re-mounting is idempotent, and a
     * one-shot clear would strand the user who declines ("Not now" mounts
     * phase 3), then clicks the still-live card (`ActionRouter` tears the
     * group down), then resolves against a null phase. Cleared ONLY by
     * {@link ModeOrchestrator#clearSharingDecisionPhase}.
     * @type {{optionsText: string|null, buttons: Array<Object>|null, groupId: string,
     *   groupData: Object, allowTextEntry: boolean, textEntryLabel: string|null,
     *   partnerName: string|null}|null}
     */
    this._completionPhase = null;
    /** @type {string|null} Consent-group id the sharing decision offers (envelope-carried). */
    this._sharingCeremonyGroupId = null;
    /**
     * Supersession identity for the phase-2 run, bumped by every teardown
     * ({@link ModeOrchestrator#clearSharingDecisionPhase}) and by each new
     * run. Re-checked after every await inside
     * {@link ModeOrchestrator#_runSharingDecisionPhase} — mirroring
     * `launchSharingCeremony`'s `launchToken`, which exists because
     * `switchMode` tears the old chat down BEFORE the new session id exists,
     * so page and session both still read unchanged at that instant. Without
     * it, a readiness fetch resolving after the user clicked "Continue to Next
     * Path" streams an invitation into the NEXT session's chat.
     * @type {number}
     */
    this._sharingPhaseToken = 0;
    /**
     * Cleanup handle for the mounted phase-2 button row.
     * @type {{hide?: Function}|HTMLElement|null}
     */
    this._sharingDecisionButtonsHandle = null;
    /**
     * In-flight guard for check-in entry (switchMode + opener send). Two rapid
     * "Report back" clicks would otherwise create two `force:true` sessions and
     * either a 404'd opener or two greetings. Same pattern as
     * `practiceBuildLauncher.launchInFlight`.
     * @type {boolean}
     */
    this._checkinEntryInFlight = false;
    /**
     * Whether the phase-2 decision is still unanswered. True from the moment
     * the binary row mounts until phase 3 mounts (approve / decline), and true
     * again after an abandoned run re-offers it. `clearModeDisplay` reads it:
     * that exit re-offers the decision, and re-offering one the user already
     * settled would replace their completion actions with a second ask.
     * @type {boolean}
     */
    this._sharingDecisionOpen = false;
    /**
     * The live `<sharing-invitation-card>` element phase 2 just rendered, so
     * {@link ModeOrchestrator#resolveSharingDecision} and
     * {@link ModeOrchestrator#setSharingInvitationCardState} flip the SAME
     * card the user clicked rather than guessing at one via a DOM query (a
     * long-lived transcript can hold more than one invitation card across
     * completions). SURVIVES a non-terminal resolution (a panel closed without
     * a submit) so the re-offered decision still drives it; null whenever no
     * card was spliced, once the card completes, or on a navigation teardown
     * (`clearSharingDecisionPhase`).
     * @type {HTMLElement|null}
     */
    this._sharingInvitationCardEl = null;
  }

  /**
   * Wrap event handler with error handling
   * Prevents silent failures in event listeners
   * @private
   * @param {Function} handler - Async handler function
   * @param {string} eventName - Event name for logging
   * @returns {Function} Wrapped handler with error handling
   */
  _safeHandler(handler, eventName) {
    return async (data) => {
      try {
        await handler(data);
      } catch (error) {
        console.error(`[ModeOrchestrator] Error in ${eventName} handler:`, error);
        // Show user-friendly error message
        if (this.chatManager) {
          this.chatManager.displayError('An error occurred. Please try again.');
        }
      }
    };
  }

  /**
   * Single chokepoint for `currentMode` state transitions. ALWAYS use this
   * instead of direct `this.currentMode =` assignment so listeners that derive
   * UI/analytics from mode state (header label, input placeholder, button
   * visibility, analytics) stay in sync.
   *
   * `switchMode()` and `restoreMode()` set the field directly and emit later
   * because their inner lifecycle hooks reference `this.currentMode` between
   * the write and the emit. Every other site goes through this helper.
   *
   * @private
   * @param {BaseMode|null} mode - New mode instance, or null when clearing
   * @param {{name: string, payload?: Object}|null} [eventSpec] - Event to
   *   emit. Pass `null` to skip emission (only valid for the documented
   *   init-only `setInitialModeState` case). Defaults to mode:changed.
   */
  _setCurrentMode(mode, eventSpec = { name: 'mode:changed' }) {
    this.currentMode = mode;
    if (eventSpec === null) return;
    this.eventBus.emit(eventSpec.name, { mode, ...(eventSpec.payload || {}) });
  }

  /**
   * Set initial mode state without creating backend session.
   * Used during app initialization to set default mode.
   *
   * Unlike switchMode(), this:
   * - Does NOT create a backend session
   * - Does NOT call lifecycle hooks (onExit/onEnter)
   * - Does NOT emit mode:changed event
   *
   * @param {string} modeName - Initial mode name
   */
  setInitialModeState(modeName) {
    const mode = this.modes.get(modeName);
    if (!mode) {
      console.error('[ModeOrchestrator] Unknown initial mode:', modeName);
      return;
    }

    this._setCurrentMode(mode, null);
    console.log('[ModeOrchestrator] Initial mode state set:', modeName, '(no session created)');
  }

  setupEventListeners() {
    // User sends text message
    // Mode resolution funnels through switchMode() so all listeners that
    // depend on mode events (header label, input placeholder, analytics,
    // button visibility) stay in sync. The prior implementation set
    // `this.currentMode` inline inside `handleUserMessage` for dashboard
    // freeform sends, which silently desynced every mode-event consumer.
    this.eventBus.on('message:send', this._safeHandler(
      async (data) => {
        // When submitting from the dashboard, always start fresh:
        // - Reset the session ID so a new backend session is created
        // - Clear old chat messages so the user sees a clean slate
        // - Silently null currentMode so switchMode runs as a cold entry:
        //   no onExit, no "Starting Session" divider injected into the new
        //   chat, and the mode:changed event reports `from='none'` rather
        //   than a stale prior mode. Silent (no event) because switchMode's
        //   immediate emit is the one that should drive listener updates —
        //   emitting mode:cleared here would cause a visible label flicker.
        //   This replaces the `force: true` no-op-guard workaround, which
        //   would otherwise drag the prior mode through onExit/transition
        //   feedback even though the user perceives a fresh start.
        const isOnDashboard = window.app?.modules?.pageManager?.isDashboardVisible();
        // P10 pre-arm: consumed exactly once, here, at the moment a dashboard
        // submit is confirmed — NOT via the generic "leaving dashboard" hygiene
        // listener (PrivacyModeManager/page:changed), which would race this
        // same synchronous message:send dispatch. Only ever true when armed
        // from the dashboard's own privacy button.
        const privacyArmed = isOnDashboard && sessionManager.consumePendingPrivacyArm();
        if (isOnDashboard) {
          sessionManager.resetSession();
          this.chatManager.clearChat();
          this._setCurrentMode(null, null);
        }

        // Resolve target mode. Deferred input modes are dashboard state; once
        // a chat mode is active, a stale pending mode must not hijack the
        // current session (notably Discovery L2 follow-up replies).
        const hasExplicitMode = !!data.mode;
        const shouldHonorExplicitMode = hasExplicitMode && (
          isOnDashboard ||
          !this.currentMode ||
          data.metadata?.source === 'autocomplete'
        );
        const targetMode = shouldHonorExplicitMode
          ? data.mode
          : (isOnDashboard ? detectSessionIntent(data.message) : null);

        if (hasExplicitMode && !shouldHonorExplicitMode) {
          console.debug('[ModeOrchestrator] Ignoring stale message mode during active chat', {
            submittedMode: data.mode,
            currentMode: this.currentMode?.name || null
          });
        }
        if (targetMode) {
          const switchOpts = { skipWelcome: true };
          if (targetMode === 'TREATMENT') switchOpts.subMode = 'build';
          // Privacy pre-arm only applies to ADVICE (the only privacy-eligible
          // mode reachable from a dashboard submit) — a detected DISCOVERY
          // intent, or an explicit TREATMENT card, must never silently inherit
          // an armed-but-unrelated privacy intent.
          if (privacyArmed && targetMode === 'ADVICE') switchOpts.privacyMode = true;
          await this.switchMode(targetMode, switchOpts);
        }

        // Optimistic voice-correction-pill clear (advice-unified-terminus B1.5):
        // a genuine typed reply supersedes a live correction pill. Remove it from
        // the live row now; the backend's durable window expiry (B1.3) is
        // authoritative for refresh/resume and the next-response re-derivation
        // restores it if the window is still open (responses 1→2).
        if (this.currentMode?.name === 'ADVICE') {
          const pillRow = window.app?.modules?.pillRow;
          if (pillRow?.removePill && Array.isArray(pillRow.agentPills)) {
            const CORRECTION_GROUPS = new Set(['voice-correction-explore', 'voice-correction-act']);
            for (const pill of [...pillRow.agentPills]) {
              if (CORRECTION_GROUPS.has(pill.groupId)) {
                pillRow.removePill(pill.id, { animate: true });
              }
            }
          }
        }

        // Process the message (with metadata and images if provided).
        // `message:send` is emitted ONLY by the chat input (ui/Input.js), so
        // this is the one place genuine typed/selected content enters. Stamp
        // `userAuthored: true` here — the positive signal the backend engagement
        // gate keys on. Every programmatic/synthetic send calls handleUserMessage
        // directly and is therefore never tagged (non-authored by default).
        await this.handleUserMessage(data.message, {
          event: 'user_message',
          metadata: {
            ...data.metadata,
            userAuthored: true,
            // Only set when switchMode above did NOT already run (freeform
            // dashboard text with no detected mode) — read by handleUserMessage's
            // own lazy-creation fallback, which always defaults to ADVICE.
            ...(privacyArmed && !targetMode ? { privacyModeArm: true } : {})
          },
          images: data.images
        });
      },
      'message:send'
    ));

    // User clicks button/action
    this.eventBus.on('action:button-click', this._safeHandler(
      (data) => this.actionRouter.executeAction(data.actionId, data.actionData, { source: 'button-click' }),
      'action:button-click'
    ));

    // Chat-input Create menu selection. Input owns the panel; the dispatch and
    // streaming semantics (queue-until-idle, acceptance boundary) belong to
    // ActionRouter, exactly as they do for a pill.
    this.eventBus.on('advice:create-menu-select', this._safeHandler(
      ({ item } = {}) => this.actionRouter.dispatchCreateMenuItem(item),
      'advice:create-menu-select'
    ));

    // Re-read the Create menu's backend projection at every ADVICE turn boundary.
    // There is no per-turn state channel for it (nor for `adviceInputRearm`), so
    // `GET /sessions/:id/info` is the single source of truth on resume AND live —
    // which is what makes membership self-reconciling (a taken artifact, a
    // dissolved couple, a pending intent all retract the menu on the next turn).
    this.eventBus.on('stream:complete', this._safeHandler(
      () => {
        if (this.currentMode?.name !== 'ADVICE') return;
        refreshAdviceCreateMenu(this._getSessionId());
      },
      'stream:complete'
    ));

    // LLM triggers action via marker
    // Adapter: Convert parsed format (type, value) to button format (full action string)
    this.eventBus.on('action:llm-triggered', this._safeHandler(
      (action) => {
        // Reconstruct full action string for ActionRouter (e.g., "switch_mode:DISCOVERY")
        const fullAction = `${action.type}:${action.value}`;
        this.actionRouter.executeAction(action.type, {
          action: fullAction,
          input: action.input
        });
      },
      'action:llm-triggered'
    ));

    // Navigation requested - central handler for all router-initiated navigation
    // Router handles page visibility, ModeOrchestrator handles mode logic
    // This separation ensures single source of truth for mode transitions
    this.eventBus.on('navigation:requested', this._safeHandler(
      async (navData) => {
        await this.handleNavigation(navData);
      },
      'navigation:requested'
    ));

    // Discovery path selected - handles IN-APP triggers only
    // Router-initiated discovery navigation goes through navigation:requested instead
    const handleDiscoveryPathSelected = this._safeHandler(
      async (data) => {
        const pathId = data?.pathId;
        const pathMetadata = data?.pathMetadata;
        if (!pathId) {
          console.error('[ModeOrchestrator] discovery:path-selected missing pathId');
          return;
        }

        // If already in DISCOVERY mode, just load the questionnaire (no mode switch needed)
        if (this.currentMode?.name === 'DISCOVERY') {
          console.log('[ModeOrchestrator] Already in DISCOVERY mode, loading path directly');
          await this._loadDiscoveryPath(pathId, pathMetadata);
          return;
        }

        // Coming from another mode - do full navigation flow
        await this.handleNavigation({
          mode: 'DISCOVERY',
          pathId,
          pathMetadata
        });
      },
      'discovery:path-selected'
    );
    this.eventBus.on('discovery:path-selected', handleDiscoveryPathSelected);

    // Discovery topic selected — /discovery page topic card click kicks the
    // single-topic loop. No walker — user completes the topic and returns to
    // /discovery (no auto-advance).
    this.eventBus.on('discovery:topic-selected', this._safeHandler(
      async (data) => {
        const topicId = data?.topicId;
        if (!topicId) {
          console.error('[ModeOrchestrator] discovery:topic-selected missing topicId');
          return;
        }
        if (this.currentMode?.name !== 'DISCOVERY') {
          await this.switchMode('DISCOVERY', { skipWelcome: true });
        }
        await this._runTopicLoop(topicId);
      },
      'discovery:topic-selected'
    ));

    // Questionnaire completed - update DiscoveryMode state
    this.eventBus.on('questionnaire:completed', this._safeHandler(
      (data) => {
        if (this.currentMode?.name === 'DISCOVERY') {
          this.currentMode.questionnaireActive = false;
          this.currentMode.currentQuestionnaireId = null;
          console.log('[ModeOrchestrator] Questionnaire completed, state updated');
        }
      },
      'questionnaire:completed'
    ));

    // Questionnaire opened in side panel — update header + hide the in-input buttons
    this.eventBus.on('questionnaire:opened', this._safeHandler(
      (data) => {
        const modeName = this.currentMode?.name;
        if (modeName === 'DISCOVERY') {
          this._discoveryMode()?.setQuestionnaireStage(data.questionnaireName, data.estimatedMinutes);
        }
        // Panel is opening → hide the in-input buttons; the launcher's input:disabled
        // overlay takes over the input region (owner-driven; the overlay sits above
        // the buttons region via z-index regardless, but we hide so the input can
        // collapse back to its single-row height — `.chat-input__buttons` is in-flow
        // and is what keeps the input tall while the textarea wrapper is hidden).
        //
        // Hidden by explicit groupId per mode, never by "whatever owns the input":
        // `questionnaire:opened` fires from all four QuestionnaireLauncher call sites,
        // and an owner-blind teardown would nuke unrelated groups that have no restore
        // path. The groupId guard makes each call a no-op unless that group is mounted.
        const questionnaireGroupId = QUESTIONNAIRE_INPUT_GROUP_IDS[modeName];
        if (questionnaireGroupId) {
          window.app?.modules?.input?.hideButtons?.({ groupId: questionnaireGroupId });
        }
      },
      'questionnaire:opened'
    ));

    // Questionnaire closed early (paused) — revert header + re-show input buttons
    this.eventBus.on('questionnaire:closed', this._safeHandler(
      async (data) => {
        if (this.currentMode?.name !== 'DISCOVERY') return;

        console.log('[ModeOrchestrator] Questionnaire closed:', data.questionnaireId);

        // Transition header to "Paused on <name>" — launcher card is in its
        // paused/resume state, user is deciding whether to reopen or move on.
        this._discoveryMode()?.setQuestionnairePausedStage();

        const skippable = this._currentQuestionnaireSkippable;

        // Owner-driven re-show: the launcher leaving 'open' fired input:enabled, which
        // only cleared the overlay. We explicitly re-mount the input buttons as
        // Resume + (Skip | Exit discovery). No remember-and-restore race.
        this._showQuestionnaireInputButtons({ resumed: true, skippable });

        if (skippable) return; // Skip is the explicit exit; no extra prompt needed.

        // Non-skippable (mandatory) questionnaire: there is no skip — the user can
        // only resume it or exit discovery. Show a brief, location-neutral note.
        this.chatManager.displayAssistantMessage(
          'It looks like you closed the questionnaire. No problem — your progress is saved. You can resume it whenever you\'re ready, or exit discovery to come back later.'
        );
      },
      'questionnaire:closed'
    ));

    // React to session lifecycle events
    this.eventBus.on('session:ended', this._safeHandler(
      async () => {
        console.log('[ModeOrchestrator] Session ended, returning to dashboard');
        this.returnToDashboard();
      },
      'session:ended'
    ));
  }

  /**
   * Get current session ID from SessionManager
   * @private
   * @returns {string|null} Current session ID or null if no session
   */
  _getSessionId() {
    return sessionManager.getCurrentSessionId();
  }

  /**
   * At submit time, decide the fate of a stashed button group when the resolved
   * routing strategy is NOT `'hybrid'` (Discovery/Orientation own their own stash
   * lifecycle and may re-mount the same group under the stashed groupId once the
   * escalation resolves). Every other strategy just sent the user's text as a
   * normal message.
   *
   * For most stashed groups there's nothing left to "go back" to, so the stash
   * drops (the "Back to buttons" pill can't linger past the submit that made it
   * meaningless). But a DURABLE-proposal group (`partner-memo-review` /
   * `practice-proposal-decision`) still has live server-side state
   * (`memoProposalId` / `practiceProposalState`) awaiting a decision — dropping
   * the stash there would orphan that draft with no UI affordance back to it. For
   * those, the stash survives as a state carrier (Phase 3.6's auto-restore reads
   * it) and only the pill is suppressed, so the input looks completely normal
   * while the user is mid-detour (app-tools plan Phase 3.5, decision #8).
   * @private
   * @param {string} strategy - The routing strategy resolved for this submit.
   */
  _applyStashDropGuard(strategy) {
    const input = window.app?.modules?.input;
    if (strategy === 'hybrid' || !input?.hasStashedButtons?.()) return;

    const stashedGroupId = input.getOwningButtonGroupId?.();
    if (DURABLE_PROPOSAL_GROUP_IDS.has(stashedGroupId)) {
      input.suppressStashedButtonsPill?.();
    } else {
      input.hideButtons();
    }
  }

  /**
   * Main message handling entry point
   * Routes message through current mode's pipeline
   *
   * @param {string} message - User message text
   * @param {Object} contextData - Additional context (events, metadata)
   */
  async handleUserMessage(message, contextData = {}) {
    // Any new send supersedes prior failed-turn UI. Retry banners are bound to
    // their originating job, but clearing them here prevents a stale action
    // from surviving while the user continues the conversation.
    this.chatManager?.clearRetryBanners?.();

    // Implicit "No" during L2 validation: if the validation buttons are
    // active and the user typed a message instead of clicking, treat the
    // message as a reopen request. Their message becomes the first turn of
    // the clarification round — no canned user bubble is inserted.
    // Silent/system triggers (e.g. '[resume]') do not count as real user turns.
    if (
      this._l2ValidationState?.awaitingValidation
      && !contextData.metadata?.silent
      && message?.trim()
    ) {
      await this._reopenL2Session();
    }

    // Reveal the chat page when the user submits from the dashboard. Mode
    // resolution and header-content activation happen upstream in the
    // `message:send` handler via `switchMode()`; this block keeps the page
    // transition for direct callers of handleUserMessage that don't go
    // through the `message:send` pipeline.
    const isOnDashboard = window.app?.modules?.pageManager?.isDashboardVisible();
    if (isOnDashboard && message?.trim() && window.app?.modules?.pageManager) {
      window.app.modules.pageManager.showPage('chat');
      window.history.pushState({}, '', '/chat');
    }

    // 1. Display user message via ChatManager public API
    // Skip if silent (system-generated triggers that shouldn't appear as user bubbles)
    if (!contextData.metadata?.silent) {
      this.chatManager.displayUserMessage(message, {
        images: contextData.images
      });
    } else {
      // Explicitly clear the optimistic-bubble pointer for silent sends.
      this.chatManager.displayUserMessage('', {});
    }

    // 1b. Increment L2 progress bar on real user turns during conversation
    if (!contextData.metadata?.silent && this._discoveryMode()?.currentStage === 'conversation') {
      this._discoveryMode().incrementL2Turn();
    }

    // 2. Show thinking indicator in the waiting phase (logo mark + cycling text)
    // Skip if mode manages its own indicator (e.g., ORIENTATION). This is a genuine
    // LLM reply-wait that streams — waiting flips to shimmer bars on first block.
    const modeManagesIndicator = this.currentMode?.constructor.managesOwnThinkingIndicator;
    if (!modeManagesIndicator) {
      // During L2 conversation, show contextual thinking text in header
      const enteredThinking = this._discoveryMode()?.enterThinkingDuringConversation('l2_thinking');
      if (enteredThinking) {
        this._l2ThinkingRevertHandler = this._revertL2ThinkingStage.bind(this);
        this.eventBus.on('stream:chunk', this._l2ThinkingRevertHandler);
      }

      // REVERT to shimmer-bars-only: drop `undefined, { waiting: true }` here and at
      // the retry site below. See frontend-user/docs/thinking-indicator-revert.md.
      //
      // New-session opener: the FIRST waiting phrase of a fresh session's first
      // message names the beginning instead of a generic "Thinking". Two
      // triggers: the one-shot flag armed by switchMode's startSession (the
      // normal entry path — the session already exists before the first send),
      // or no session id here, which means the lazy-creation branch below will
      // run. One phrase only — cycling proceeds normally from the first
      // advance. ORIENTATION (the one sessionless mode) never reaches this arm
      // site: it manages its own indicator.
      const isNewSession = this._pendingNewSessionOpener === true || !this._getSessionId();
      this._pendingNewSessionOpener = false; // one-shot: only the session's first turn
      this.chatManager.activateThinkingIndicator(undefined, {
        waiting: true,
        ...(isNewSession && { initialPhrase: 'Beginning conversation' })
      });
    }

    try {
      // 3. Ensure session exists (lazy creation via SessionManager)
      // Skip for frontend-only modes that don't need backend sessions (e.g., ORIENTATION)
      // See switchMode() for the same pattern and detailed documentation
      const skipSessionModes = ['ORIENTATION'];
      let sessionId = this._getSessionId();

      // Defensive fallback. After the `message:send` refactor, every regular
      // entry path resolves the mode upstream via switchMode(). Hitting this
      // means an unaudited caller invoked handleUserMessage with no mode set;
      // route through the helper so listeners stay in sync rather than
      // silently mutating the field.
      if (!this.currentMode) {
        console.warn('[ModeOrchestrator] handleUserMessage called with no currentMode — defaulting to ADVICE');
        this._setCurrentMode(this.modes.get('ADVICE'));
      }

      if (!sessionId && !skipSessionModes.includes(this.currentMode.name)) {
        console.log(`[ModeOrchestrator] No session exists, creating lazily for mode: ${this.currentMode.name}`);

        try {
          // SessionManager creates session with current mode (determined by intent detection)
          // P10 pre-arm: the message:send handler stamps `privacyModeArm` when a
          // dashboard privacy pre-arm consumed here landed on this exact fallback
          // (freeform text with no detected mode, always defaulting to ADVICE).
          const contextParams = contextData.metadata?.privacyModeArm === true
            ? { privacyMode: true }
            : {};
          await sessionManager.startSession(this.currentMode.name, contextParams);
          sessionId = this._getSessionId();

          // NOTE: Don't show welcome message here - already shown on page load in app.js
          // This avoids duplicate welcome messages

          console.log('[ModeOrchestrator] Session created:', sessionId);
        } catch (error) {
          console.error('[ModeOrchestrator] Failed to create session:', error);
          this.chatManager.displayError('Unable to start session. Please try again.');
          this.eventBus.emit('message:complete'); // Reset input state
          return;
        }
      }

      const mode = this.currentMode;

      // 4. Build context
      const context = this.buildContext(contextData);

      // 5. Preprocess message
      const { message: processedMessage, context: enrichedContext } =
        mode.preprocessMessage(message, context);

      // 6. Determine routing strategy
      const strategy = mode.getRoutingStrategy(processedMessage, enrichedContext);

      this._applyStashDropGuard(strategy);

      console.log(`[ModeOrchestrator] Routing strategy: ${strategy}`);

      // 7. Generate response based on strategy
      let response;
      switch (strategy) {
        case 'llm':
          // Thinking-Line Theater (thinking-lines-header-plan §2.1): fire-and-
          // forget, ADVICE-only, gated by the N3 predicate. Fired here (not
          // earlier) so it always has a real sessionId — the first turn of a
          // new session has none until the lazy-creation block above resolves.
          // Uses the ORIGINAL `message` (what the user actually typed), not
          // `processedMessage`, which a mode's preprocessMessage may alter.
          this._maybeFireThinkingLines(message, contextData, mode, sessionId);
          response = await this.generateLLMResponse(processedMessage, mode, enrichedContext);
          break;

        case 'programmatic':
          response = await mode.generateProgrammaticResponse(processedMessage, enrichedContext);
          break;

        case 'hybrid':
          response = await mode.generateHybridResponse(processedMessage, enrichedContext);
          break;

        default:
          throw new Error(`Unknown routing strategy: ${strategy}`);
      }

      // 8. Handle response based on type
      if (response.type === 'unified') {
        // Unified response - use ResponseHandler for all content processing
        if (!window.responseHandler) {
          throw new Error('ResponseHandler not initialized');
        }
        await window.responseHandler.handleResponse(response.response);
        // ResponseHandler emits response:complete when done
      } else {
        // Instant/programmatic response
        // Note: Thinking indicator deactivation handled by MessageRenderer.finish()

        // Post-process response
        // Use ?? (nullish coalescing) to only fall back if text is null/undefined
        // Empty string '' is valid and should not trigger fallback
        const processed = mode.postprocessResponse(response.text ?? response, enrichedContext);

        // Display text message if provided
        // Pass messageType from response for action bar control (e.g., 'system' suppresses action bar)
        // Pass staggerDelay for programmatic messages that want sequential block rendering
        let animationDuration = 0;
        let hasSelfTimedHold = false;
        if (processed.text) {
          const result = this.chatManager.displayAssistantMessage(processed.text, {
            messageType: response.messageType,
            staggerDelay: response.staggerDelay,
            customClass: response.customClass
          });
          animationDuration = result.animationDuration || 0;
          hasSelfTimedHold = !!result.hasSelfTimedHold;
        }

        // A self-timed card (e.g. the memory-update card) keeps animating well
        // past its own fade-in — show shimmer bars for that stretch so the
        // wait doesn't read as dead air (matches the discovery
        // understanding-segment pattern in _serveOrchestratorQuestionnaire).
        // ORIENTATION is excluded: its demo card sits mid-bubble inside a
        // staggered step message, so bars appended below the whole bubble read
        // as a second pending message rather than the card still working. The
        // animationDuration hold below still applies there, so buttons and
        // follow-up content stay gated on the card completing.
        const showHoldShimmer = hasSelfTimedHold && mode?.name !== 'ORIENTATION';
        if (showHoldShimmer) {
          this.chatManager.activateThinkingIndicator();
        }

        // Render interactive components if provided
        // Wait for staggered text animation to complete before showing components
        if (response.components && response.components.length > 0) {
          if (animationDuration > 0) {
            await new Promise(resolve => setTimeout(resolve, animationDuration));
          }
          if (showHoldShimmer) {
            this.chatManager.deactivateThinkingIndicator();
          }
          await this.renderComponents(response.components, enrichedContext);
        } else if (showHoldShimmer && animationDuration > 0) {
          setTimeout(() => this.chatManager.deactivateThinkingIndicator(), animationDuration);
        }

        // Emit message:complete to clear Input.isSubmitting (which was set
        // when the user typed and submitted). The LLM path emits this from
        // ChatManager._finalizeStream after the SSE stream finishes;
        // programmatic/hybrid responses must do it explicitly here, since
        // displayAssistantMessage only toggles chatBusy via the activity
        // refcount and doesn't touch isSubmitting. Animation-aware so the
        // input doesn't unlock mid-fade-in.
        // (For OrientationMode deviations where text is empty,
        // ChatManager.finalizeStreamingMessage also emits message:complete;
        // resetSubmitState is idempotent so the duplicate is harmless.)
        if (animationDuration > 0) {
          setTimeout(() => this.eventBus.emit('message:complete'), animationDuration);
        } else {
          this.eventBus.emit('message:complete');
        }
      }

      return true;

    } catch (error) {
      console.error('[ModeOrchestrator] Error handling message:', error);
      // Clean up L2 thinking stage if it was set
      if (this._l2ThinkingRevertHandler) {
        this.eventBus.off('stream:chunk', this._l2ThinkingRevertHandler);
        this._discoveryMode()?.exitThinkingToConversation();
        this._l2ThinkingRevertHandler = null;
      }

      // Closed-session handoff: the session this tab held was closed (the user
      // started a newer chat elsewhere, or it aged out after 7 days). Don't show
      // a generic error or a dead-end Retry — keep the conversation on screen and
      // offer to continue it (summary-based) or head to the dashboard.
      if (error?.code === 'SESSION_NOT_FOUND') {
        // Late 404: the server names the session it rejected
        // (`metadata.sessionId`, routes/chat.js). When that is absent or is not
        // the session this tab currently holds, the send targeted a session the
        // tab has already let go of (e.g. the empty check-in discard's DELETE
        // beat the opener POST) — rendering the handoff would offer to continue
        // a conversation the user already left.
        const currentSessionId = this._getSessionId();
        const rejectedSessionId = error.serverMetadata?.sessionId ?? null;
        if (rejectedSessionId && rejectedSessionId === currentSessionId) {
          this._showClosedSessionHandoff({
            closedSessionId: currentSessionId,
            closedBy: error.serverMetadata?.closedBy ?? null,
            attemptedMessage: message
          });
        }
        this.eventBus.emit('message:complete'); // release the input lock
        return false;
      }

      // Guest limits are not failures to retry — they are the moment to offer an
      // account. Without this branch they land in the generic error above, whose
      // Retry can only ever fail again.
      if (this._handleGuestLimitError(error)) {
        this.eventBus.emit('message:complete'); // release the input lock
        return false;
      }

      // The daily message limit is a conversion surface, not a failure — no
      // red error toast, no Retry that can only 429 again. The unsent text
      // goes back to the composer and the server's upgrade-framed copy lands
      // in-chat; message:complete then lets UsageService (which already
      // received the 429 usage snapshot in generateLLMResponse) show its
      // deferred lock overlay.
      if (this._handleDailyLimitError(error)) {
        this.eventBus.emit('message:complete'); // release the input lock
        return false;
      }

      this.chatManager.displayError('Failed to process message. Please try again.');
      return false;
    }
  }

  /**
   * Route a guest-limit / guest-gate error into the account-creation overlay.
   *
   * This is the universal guest gate: every account-only surface answers with
   * one of these codes, and each is a conversion trigger rather than an error.
   * `GUEST_LIMIT_UNAVAILABLE` is the fail-closed case (the cap couldn't be
   * checked) — an account is still the way through, so it routes the same way
   * with softer copy.
   *
   * @param {Error & {code?: string}} error
   * @returns {boolean} True if the error was consumed here
   * @private
   */
  _handleGuestLimitError(error) {
    const CONTEXTS = {
      GUEST_MESSAGE_LIMIT: 'guest_message_limit',
      GUEST_LIMIT_UNAVAILABLE: 'guest_message_limit',
      GUEST_ACCOUNT_REQUIRED: 'default'
    };
    const context = CONTEXTS[error?.code];
    if (!context || window.app?.isGuest !== true) return false;

    console.log(`[ModeOrchestrator] Guest gate: ${error.code}`);
    window.app.showLoginOverlayForConversion(context);
    return true;
  }

  /**
   * Route the daily-message-limit 429 into the upgrade surface instead of the
   * generic error toast. UsageService already received the 429 usage snapshot
   * (generateLLMResponse feeds it before throwing) and owns the lock overlay
   * and banner, so this handler only does the chat-side half: remove the
   * optimistic user bubble, return the typed text (and images) to the
   * composer, and voice the server's upgrade-framed message as a system turn.
   *
   * @param {Error & {code?: string, serverMessage?: string}} error
   * @returns {boolean} True if the error was consumed here
   * @private
   */
  /**
   * Handle a path-access refusal from `GET`/`POST /discovery/paths/:pathId/next`.
   *
   * Unreachable today — `BUILT_PATH_IDS === BASIC_PATH_IDS`, so no path can
   * answer `PREMIUM_REQUIRED`, and withheld cards are unclickable. It becomes
   * reachable the moment a path ships as Premium, via the two routes that
   * bypass the card: a stale listing (entitlement changed between render and
   * click) and a bookmarked deep link.
   *
   * Like the daily-message wall, this is a conversion surface rather than a
   * failure — the server's own copy lands in-chat instead of a red error
   * toast. Presentation beyond that is deliberately minimal; the designed
   * treatment is launch-eve work (`plans/premium-gates-launch-eve.md`).
   *
   * @private
   * @param {Object} error - Rejection from `discoveryApi._request`
   * @returns {boolean} True when handled and the caller should stop
   */
  _handlePathAccessError(error) {
    const code = error?.code;
    if (code !== 'PREMIUM_REQUIRED' && code !== 'PATH_NOT_AVAILABLE') return false;

    const fallback = code === 'PREMIUM_REQUIRED'
      ? 'This path is available with Premium.'
      : 'This path is not available yet.';
    this.chatManager?.displayAssistantMessage?.(error.error || fallback, { messageType: 'system' });

    // Only PREMIUM_REQUIRED has somewhere to go — an unbuilt path has nothing
    // to sell, so it must not route to /upgrade.
    if (code === 'PREMIUM_REQUIRED') {
      goToUpgrade(this.eventBus, 'pathEntry');
    }
    return true;
  }

  _handleDailyLimitError(error) {
    if (error?.code !== 'DAILY_MESSAGE_LIMIT') return false;

    this.chatManager.restoreUnsentSend?.();
    const text = error.serverMessage
      || "You've reached today's message limit. Upgrade to Premium for unlimited messages, or come back tomorrow.";
    this.chatManager.displayAssistantMessage(text, { messageType: 'system' });
    return true;
  }

  /**
   * Render the closed-session handoff: a warm in-chat notice + two buttons
   * ("Continue this conversation" → session_continuation; "Back to dashboard").
   * Does NOT clear the chat — the prior conversation stays on screen.
   *
   * @param {Object} opts
   * @param {string|null} opts.closedSessionId - The session that was closed
   * @param {string|null} opts.closedBy - Closure reason ('mode_switch' | 'stale_cleanup' | legacy 'orphan_cleanup')
   * @param {string} [opts.attemptedMessage] - The un-sent message, carried into the continuation
   * @private
   */
  _showClosedSessionHandoff({ closedSessionId, closedBy, attemptedMessage } = {}) {
    const archived = closedBy === 'stale_cleanup' || closedBy === 'orphan_cleanup';
    // The check-in/build arc closes itself at its terminus, so this is the user
    // typing instead of taking one of the two wrap-up buttons. Neither of the
    // other copies fits — they didn't start a newer chat, and nothing aged out.
    const terminus = closedBy === 'checkin_terminus';
    const text = terminus
      ? "We wrapped that up — I've saved it to your experiments and history. Want to keep going from here, or head back to your dashboard?"
      : archived
        ? "It's been a while since this conversation — I've saved it to your history. Want to pick it back up, or head to your dashboard?"
        : "This conversation has wrapped up — it looks like you started a newer chat since you were last here. I've saved everything we covered. Want to pick this thread back up, or head back to your dashboard?";

    this.chatManager.displayAssistantMessage(text, { messageType: 'system' });

    if (!window.actionButtons) {
      console.error('[ModeOrchestrator] ActionButtons not initialized — cannot render closed-session handoff');
      return;
    }

    const buttons = [
      {
        label: 'Continue this conversation',
        action: 'continue_closed_session',
        className: 'action-btn--primary',
        data: {
          sourceSessionId: closedSessionId,
          attemptedMessage: attemptedMessage || ''
        }
      },
      { label: 'Back to dashboard', action: 'return_to_dashboard' }
    ];
    const group = window.actionButtons.createButtonGroup(buttons, {
      groupId: 'closed-session-handoff'
    });
    this.chatManager.messageList.addInteractiveComponent(group, {
      type: 'action-buttons',
      renderLocation: 'input',
      allowTextEntry: true,
      data: { groupId: 'closed-session-handoff' }
    });
  }

  /**
   * Generate LLM response via API
   */
  async generateLLMResponse(message, mode, context) {
    // Build mode-specific context
    const messageHistory = this.messageList.getMessageHistory();
    const llmContext = await mode.buildContext(this.userId, message, messageHistory);

    // Get sessionId dynamically
    const sessionId = this._getSessionId();

    // Generate an idempotency key for this submit so network retries and
    // double-submits dedupe server-side. The key is stashed on the pending
    // retry record so a user-initiated Retry can use a fresh key and create
    // a new job.
    //
    // A caller MAY supply a DETERMINISTIC key via `context.idempotencyKey`
    // (e.g. the pending-intent recovery turn passes
    // `advice-intent-recovery:{sessionId}:{intentId}`) so a refresh or a second
    // tab firing the same recovery dedupes to the same ChatJob server-side
    // (ChatJob.idempotencyKey is UNIQUE). The dedup path returns the existing
    // job's streamUrl/jobId in the SAME shape — including an already-COMPLETED
    // job, whose stream simply replays — so nothing downstream needs to branch.
    const idempotencyKey = context?.idempotencyKey
      || (crypto.randomUUID && crypto.randomUUID())
      || `ik-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    // Stash the submit so we can retry it on failure without re-displaying
    // the user bubble. Only the LLM path is retryable — programmatic/hybrid
    // paths don't fail in the same way.
    this._lastLLMSubmit = { message, mode, context, sessionId };

    // Call LLM API (via existing chat job system)
    // Note: Backend gets mode from session.type, but we send mode.name for logging/debugging
    const jobResponse = await fetchWithTimeout(`${API_URL}/chat/message`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        sessionId: sessionId,
        message: message,
        images: context?.images,
        mode: mode.name, // For debugging - backend uses session.type
        context: llmContext,
        // Message metadata (e.g., source: 'autosuggestion')
        metadata: context?.metadata || {},
        idempotencyKey
      })
    });

    if (!jobResponse.ok) {
      // Parse the JSON body for EVERY non-OK response (not just 429) so the
      // server's error `code`/`metadata` reach the caller. Without this, a 404
      // SESSION_NOT_FOUND can't be distinguished from a generic failure and the
      // closed-session handoff can't fire. Throw a typed error carrying it.
      const body = await jobResponse.json().catch(() => null);

      // 429 + DAILY_MESSAGE_LIMIT: feed the server-provided usage snapshot to
      // UsageService. applyServerUsage picks up the exhausted state and emits
      // the lock-overlay event itself (no stream is in flight on a 429, so the
      // overlay appears immediately).
      if (jobResponse.status === 429 && body?.code === 'DAILY_MESSAGE_LIMIT' && body?.metadata) {
        window.app?.services?.usage?.applyServerUsage(body.metadata);
      }

      const err = new Error(`API error: ${jobResponse.status} ${jobResponse.statusText}`);
      err.status = jobResponse.status;
      err.code = body?.code ?? null;
      err.serverMessage = body?.error ?? null;
      err.serverMetadata = body?.metadata ?? null;
      throw err;
    }

    const job = await jobResponse.json();

    // Forward usage metadata to UsageService. This read happens before
    // API.handleResponse() would strip metadata, so no shared-helper changes.
    if (job?.metadata?.usage) {
      window.app?.services?.usage?.applyServerUsage(job.metadata.usage);
    }

    // Return response for ResponseHandler (single pipeline format)
    return {
      type: 'unified',
      response: job
    };
  }

  /**
   * N3 gate (thinking-lines-header-plan §2.1): true only for a genuinely
   * typed ADVICE turn. Mirrors the server backstop
   * (`^\[(User selected|Action):` in routes/chat.js POST /chat/thinking-lines)
   * from the client side, plus the client-only signals the server can't see.
   *
   * `metadata.userAuthored` is the load-bearing check, not `metadata.silent`
   * alone: `userAuthored: true` is stamped ONLY by the `message:send` handler
   * (the one place genuine typed/selected content enters — ModeOrchestrator.js
   * `_registerEventHandlers`); every programmatic/synthetic call goes straight
   * to `handleUserMessage` and is therefore never tagged. `source ===
   * 'autocomplete'` is excluded separately because autocomplete selections
   * ALSO go through `message:send` and so ALSO get `userAuthored: true` —
   * that flag alone is not sufficient (N3).
   *
   * @param {string} message - Raw message text as passed to handleUserMessage
   * @param {Object} contextData - handleUserMessage's contextData param
   * @param {Object} mode - Current mode instance
   * @param {string|null} sessionId
   * @returns {boolean}
   * @private
   */
  _shouldFireThinkingLines(message, contextData, mode, sessionId) {
    if (mode?.name !== 'ADVICE') return false;
    if (!sessionId) return false;
    if (!message || !message.trim()) return false;
    const metadata = contextData?.metadata || {};
    if (metadata.silent === true) return false;
    if (metadata.source === 'autocomplete') return false;
    if (metadata.userAuthored !== true) return false;
    if (window.app?.modules?.privacyModeManager?.isPrivacyMode === true) return false;
    return true;
  }

  /**
   * Fire-and-forget POST /chat/thinking-lines for a qualifying ADVICE turn.
   * Never awaited by the caller — this must not add latency to the actual
   * LLM turn. Captures {sessionId, turnToken} at fire time (N9); the resolve
   * handler drops the response silently if either has moved on by the time it
   * lands (mode switch, session switch, or a newer turn already dispatched).
   *
   * Retries (`retryLastLLMSubmit`) never reach this method — that path calls
   * `/jobs/:jobId/retry` directly, not `handleUserMessage`, so it has no
   * `pendingMessage` to send and the indicator falls back to generic pools.
   *
   * @param {string} message - Raw pending message text
   * @param {Object} contextData - handleUserMessage's contextData param
   * @param {Object} mode - Current mode instance (must be AdviceMode)
   * @param {string|null} sessionId
   * @private
   */
  _maybeFireThinkingLines(message, contextData, mode, sessionId) {
    if (!this._shouldFireThinkingLines(message, contextData, mode, sessionId)) return;

    const turnToken = mode.bumpTurnToken();
    const priorLines = mode.getPriorThinkingLines();

    fetchWithTimeout(`${API_URL}/chat/thinking-lines`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ sessionId, pendingMessage: message, priorLines })
    }, 4000).then(async (response) => {
      if (!response.ok) return;
      const envelope = await response.json().catch(() => null);
      const data = envelope?.data;
      if (!data) return;
      this._applyThinkingLinesResult(mode, sessionId, turnToken, data);
    }).catch(() => {
      // N6: network error/timeout → no-op, exactly today's (pre-thinking-lines) UX.
    });
  }

  /**
   * Apply a resolved thinking-lines response — N9 ownership guard first: the
   * {sessionId, turnToken} pair captured at fire time must still match
   * current state, and the mode must not have been switched away from,
   * otherwise the response is dropped silently.
   *
   * Also covers the "late-response invalidation" race N9 implies but
   * {sessionId, turnToken} alone can't catch: privacy mode doesn't create a
   * new session or necessarily advance the turn token (no new message was
   * sent), so a request fired just before privacy was entered could
   * otherwise resolve afterward and stash/render a real title over "Private
   * Conversation". Checked here (apply time), not just at fire time.
   *
   * Ordered lines are handed to the indicator unconditionally past the guard
   * — `ThinkingIndicator.setOrderedLines` is itself responsible for ignoring
   * lines that arrive after streaming has started or after its own overflow
   * threshold (see ThinkingIndicator.js), so no redundant "still waiting"
   * check is needed here. A title is stashed unconditionally too; if the
   * turn has already reached `stream:complete` (the header already rendered
   * off the old/absent stash), a guarded `forceUpdate` pushes the new title
   * immediately rather than waiting for the next user turn's natural trigger.
   *
   * @param {Object} mode - AdviceMode instance the request was fired for
   * @param {string} sessionId - Session id captured at fire time
   * @param {number} turnToken - Turn token captured at fire time
   * @param {Object} data - `{ title, titleChanged, lines }` from the response envelope
   * @private
   */
  _applyThinkingLinesResult(mode, sessionId, turnToken, data) {
    if (this.currentMode !== mode) return;
    if (this._getSessionId() !== sessionId) return;
    if (mode.getTurnToken() !== turnToken) return;
    if (window.app?.modules?.privacyModeManager?.isPrivacyMode === true) return;

    const { title, titleChanged, lines } = data;

    if (Array.isArray(lines) && lines.length > 0) {
      this.chatManager?.thinkingIndicator?.setOrderedLines(lines);
      mode.appendPriorThinkingLines(lines);
    }

    if (title) {
      mode.stashTitle(title, titleChanged === true);
      // Idempotent: HeaderContentService's per-block equality check
      // (`_middleBlockEqual`) no-ops this if the natural `stream:complete`/
      // `message:received` trigger already rendered the same fresh stash.
      this.headerContentService?.forceUpdate?.({ event: 'message:received' });
    }
  }

  /**
   * Retry a terminal LLM job without resending or re-rendering its user turn.
   * @param {Object} params
   * @param {string} params.jobId - Failed or stopped ChatJob ID bound to the banner
   * @param {Array<Object>} [params.images=[]] - In-memory attachments to re-send
   * @returns {Promise<void>}
   */
  async retryLastLLMSubmit({ jobId, images = [] } = {}) {
    if (!jobId) {
      console.warn('[ModeOrchestrator] No job ID to retry');
      return;
    }

    try {
      const response = await fetchWithTimeout(`${API_URL}/jobs/${jobId}/retry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ images })
      });
      const envelope = await response.json().catch(() => null);
      if (!response.ok) {
        const error = new Error(envelope?.error || 'Retry failed');
        error.status = response.status;
        error.code = envelope?.code;
        error.serverMetadata = envelope?.metadata;
        throw error;
      }

      const job = envelope?.data || envelope;
      const input = window.app?.modules?.input;
      if (input) {
        input.isSubmitting = true;
        input.updateButtonState();
      }
      this.chatManager?.removeLastFailedMessage?.();
      // Retry streams the reply — waiting phase, flips to bars on first block.
      // REVERT to bars-only: drop `undefined, { waiting: true }` (see the main-turn
      // site above + frontend-user/docs/thinking-indicator-revert.md).
      this.chatManager?.activateThinkingIndicator?.(undefined, { waiting: true });
      if (job?.streamUrl && window.responseHandler) {
        await window.responseHandler.handleResponse({ data: job });
      } else {
        this.eventBus.emit('message:complete');
      }
    } catch (error) {
      console.error('[ModeOrchestrator] Retry failed:', error);
      const status = error.serverMetadata?.status;
      if (error.status === 409 && ['QUEUED', 'PROCESSING'].includes(status)) {
        this.chatManager.displayError('The previous attempt is still working — retry shortly.');
      } else if (error.status === 409 && status === 'COMPLETED') {
        this.chatManager.displayError('That response completed. Reload the conversation to see it.');
      } else {
        this.chatManager.displayError('Retry failed. Please try again.');
      }
    }
  }

  // NOTE: handleLLMStream() removed - streaming now handled via ResponseHandler

  /**
   * Render interactive components in chat
   * Handles component-based responses from modes (questionnaires, buttons, forms, etc.)
   *
   * @param {Array} components - Array of component definitions
   * @param {Object} context - Current context
   */
  async renderComponents(components, context) {
    for (const component of components) {
      try {
        switch (component.type) {
          // NOTE: 'questionnaire' and 'recovery_options' cases removed.
          // These are now handled via the mode orchestration layer.

          case 'buttons':
            if (!window.actionButtons) {
              console.error('[ModeOrchestrator] ActionButtons not initialized');
              break;
            }

            // Component format: { type: 'buttons', buttons: [...], options: {...} }
            const buttonGroup = window.actionButtons.createButtonGroup(
              component.buttons || [],
              component.options || {}
            );

            // Add to chat as standalone interactive component
            this.chatManager.messageList.addInteractiveComponent(buttonGroup, {
              type: 'buttons',
              data: component.options || {}
            });

            console.log('[ModeOrchestrator] Rendered button group:', component.buttons?.length || 0, 'buttons');
            break;

          case 'form':
            // TODO: Implement form rendering
            console.log('[ModeOrchestrator] Form component not yet implemented:', component);
            break;

          default:
            console.warn(`[ModeOrchestrator] Unknown component type: ${component.type}`, component);
        }
      } catch (error) {
        console.error(`[ModeOrchestrator] Failed to render component:`, component, error);
        // Continue rendering other components even if one fails
      }
    }
  }

  /**
   * Switch to a different operational mode
   * In session-per-mode architecture, this creates a new backend session
   *
   * If already in the requested mode, the switch is skipped (no-op) to prevent
   * duplicate sessions and welcome messages. Use transitionData.force = true
   * to bypass this check for intentional re-entry scenarios.
   *
   * @param {string} modeName - Name of mode to switch to
   * @param {Object} transitionData - Context data for transition
   * @param {boolean} [transitionData.force] - Force mode switch even if already in this mode
   * @param {boolean} [transitionData.skipWelcome] - Skip welcome message display
   */
  async switchMode(modeName, transitionData = {}) {
    const nextMode = this.modes.get(modeName);
    if (!nextMode) {
      throw new Error(`Unknown mode: ${modeName}`);
    }


    // Skip if already in this mode (prevents duplicate sessions/welcome messages)
    // Use transitionData.force = true to bypass this check if re-entry is intentional
    if (this.currentMode?.name === modeName && !transitionData.force) {
      console.log(`[ModeOrchestrator] Already in ${modeName} mode, skipping switch`);
      return;
    }

    // Abort any in-flight LLM turn before transitioning. A user-initiated switch
    // (mode dropdown, browser back/forward) can fire mid-stream; without this,
    // stale SSE chunks render into the previous mode's bubble and the response's
    // pendingActions misroute to the new mode. Also releases the input lock so
    // the destination mode is usable (abandonStream alone leaves it set).
    this.chatManager?.cancelActiveStreamForNavigation();

    // Generic chat-owned-panel backstop. Chat is not a PageManager section, so
    // no deactivate() fires between sessions and `switchMode` had no panel
    // check of its own — a ceremony panel opened in the outgoing session would
    // otherwise orphan itself over the incoming one's chat. Runs BEFORE the new
    // session is started, which is why the launcher's supersession check needs
    // its own token: page and session id both still read as unchanged here.
    // Discarding staged consent is correct, not data loss: nothing has been
    // written and the overview's "Approve & share" is the only confirm surface. The deferred phase 3
    // goes too — this switch replaces the chat its buttons belong in.
    //
    // Cleared BEFORE the teardown, not after: navigating away is not a ceremony
    // resolution, so a teardown-driven panel close must mount nothing. The
    // launcher's forced retire already declines to fire its resolution callback;
    // clearing first makes that belt-and-braces — a null phase resolves to
    // nothing even if a close slips through by another route.
    this.clearSharingDecisionPhase();
    this._teardownChatOwnedPanels();

    // Eager signal that a transition has begun — emitted synchronously, BEFORE
    // the multi-second `startSession` await below and the terminal
    // `mode:changed` (line ~919). Lets transition-sensitive UI hide immediately
    // instead of flashing stale state while the new session loads. Always
    // followed by `mode:changed` once the switch settles, which consumers use to
    // clear the in-flight state.
    this.eventBus.emit('mode:switching', {
      to: modeName,
      from: this.currentMode?.name || 'none'
    });

    // Any mode switch cancels a pending discovery callback. Critical when a
    // user leaves L2/L3 mid-conversation — the callback closes over the prior
    // section's state and would either run against a stale session or fire
    // after the user has already navigated away.
    this._pendingDiscoveryCallback = null;

    const previousMode = this.currentMode;

    // Track whether this Discovery entry came directly from orientation advice
    this._fromOrientation = modeName === 'DISCOVERY' && !!transitionData.fromOrientation;

    // Track whether we're in an advice session spawned from orientation
    // (used to suppress message actions during the orientation advice flow).
    // CRIT-6 (phase-b): clear on ANY non-orientation entry — including a new
    // non-orientation ADVICE entry — not only when leaving ADVICE. Otherwise a stale
    // `true` from an earlier orientation entry would misroute a later normal-advice
    // practice decision (composePractice) into the orientation branch, which the
    // backend route then silently no-ops.
    if (transitionData.entryType === 'orientation_advice') {
      this._fromOrientationAdvice = true;
    } else {
      this._fromOrientationAdvice = false;
    }

    // Track the active Treatment sub-mode (build vs checkin). Read at accept time
    // by ActionRouter.accept_practice to scope the post-accept terminus to BUILD —
    // check-in proposes follow-up practices through the SAME practice-proposal-decision
    // group, so a mode-only gate would misfire the build terminus into a check-in.
    // Null for every non-Treatment mode. Mirrors the _fromOrientationAdvice pattern above.
    this._treatmentSubMode = modeName === 'TREATMENT' ? (transitionData.subMode || null) : null;

    // Track memo_entry ("discuss this note") sessions so the memo-done cascade
    // wake can suppress inside them (terminus D8: a reply-send must not start
    // the wrap-up cascade at turn ~2). Mirrors the _fromOrientationAdvice
    // pattern: set on entry, cleared by any other entry. Also re-derived on
    // resume from session metadata (resumeSession).
    this._memoEntrySession = transitionData.entryType === 'memo_entry';

    // New-session opener (thinking indicator first phrase): reset on EVERY
    // switch, set true only when this switch actually creates a fresh backend
    // session below. A resume/attach entry must not inherit a stale pending
    // opener from an earlier new-session entry the user never typed into.
    this._pendingNewSessionOpener = false;

    // Agent-surfaced pills are per-session: clear the row on EVERY mode switch
    // (incl. new_topic's forced ADVICE re-entry) so a stale pill can never fire
    // its consume against the wrong session. The new session's own pills arrive
    // via server injection (pendingActions) or resume hydration (activePills).
    this.actionRouter?.syncPillRowFromGroups?.([]);

    // Call lifecycle hooks
    if (previousMode) {
      await previousMode.onExit(nextMode, transitionData);
    }

    // Reset input's custom placeholder flag before onEnter
    // This allows new mode to set its own custom placeholder, or get default
    if (window.app?.modules?.input) {
      window.app.modules.input.clearCustomPlaceholder();
    }

    // Show mode transition feedback (placeholder)
    await this._showModeTransitionFeedback(previousMode, nextMode, transitionData);

    // Update frontend mode state
    this.currentMode = nextMode;
    this.modeHistory.push({
      mode: modeName,
      timestamp: Date.now(),
      data: transitionData
    });

    // Create new backend session (session-per-mode architecture)
    // Backend automatically closes previous session and queues summary generation
    //
    // Skip for ORIENTATION mode: it's a frontend-only mode with no backend session.
    // ORIENTATION is not a valid SessionType in the backend schema (DISCOVERY,
    // ADVICE, TREATMENT, INTAKE). Orientation's own turns are served by
    // /api/orientation-flow; the first real session is the ADVICE one this method
    // creates when TRANSITION hands off with entryType 'orientation_advice'.
    let sessionResult = null;
    const skipSessionModes = ['ORIENTATION']; // Frontend-only modes that don't need backend sessions

    // Resume path: when transitionData carries a resumeSessionId, do NOT create a
    // new backend session. The mode's onEnter is responsible for calling
    // sessionManager.attachToSession(resumeSessionId). This avoids burning the
    // sessionCreateLimiter quota and prevents an orphan session that would
    // poison the next prereq resume sweep. (Phase 1: INTAKE only.)
    if (transitionData.resumeSessionId) {
      console.log(`[ModeOrchestrator] Resume path for ${modeName} — skipping startSession, will attach to ${transitionData.resumeSessionId}`);
    } else if (!skipSessionModes.includes(modeName)) {
      try {
        sessionResult = await sessionManager.startSession(modeName, transitionData);
        // Arm the one-shot opener: the session's first user message shows
        // "Beginning conversation" as its first waiting phrase (consumed at the
        // handleUserMessage arm site).
        this._pendingNewSessionOpener = true;
        console.log(`[ModeOrchestrator] New session created for mode: ${modeName}`);
      } catch (error) {
        console.error('[ModeOrchestrator] Failed to create session:', error);
        // A guest reaching for an account-only mode gets the overlay, not an
        // error — this is the 403 the narrow front door returns for anything
        // other than the orientation demo.
        if (!this._handleGuestLimitError(error)) {
          // Permanent-payload rejections (Stage 0 item 5): retrying the same
          // navigation can never succeed, so don't show retry copy for them.
          const permanentCodes = ['INVALID_SUBMODE', 'PRACTICE_ID_REQUIRED'];
          if (permanentCodes.includes(error?.code)) {
            this.chatManager?.displayError(
              "Couldn't open that experiment conversation — try again from the Experiments page."
            );
          } else {
            // Surface failure to the user instead of silently proceeding with no
            // sessionId. (`displaySystemMessage` never existed on ChatManager, so
            // optional chaining made this a silent no-op.)
            this.chatManager?.displayError(
              "Couldn't start a session right now — please try again in a moment."
            );
          }
        }
        // Re-throw so callers (e.g. ActionRouter handlers) can abort cleanly.
        throw error;
      }
    } else {
      console.log(`[ModeOrchestrator] Skipping session creation for frontend-only mode: ${modeName}`);
    }

    // Initialize mode state before activating header strategy.
    // onEnter sets metadata (e.g., path name) that the header strategy reads
    // to generate display text. Activating the header first would show fallback text.
    const enterResult = await nextMode.onEnter(previousMode, transitionData);

    // Born-private (RB-B4): the server-echoed flag rode session:started BEFORE onEnter
    // reset the strategy and before activateMode subscribed it, so the privacy:changed
    // edge was lost. Seed it now, from the creation response, not the client request.
    if (sessionResult?.session?.privacyMode === true) nextMode.seedBornPrivate?.();

    // Activate header content strategy after mode is fully initialized
    if (this.headerContentService) {
      this.headerContentService.activateMode(nextMode);
    }

    // Emit mode:changed BEFORE rendering the new mode's welcome/onEnter content.
    // The Input safety net clears any stale in-input buttons from the *previous* mode
    // on mode:changed; emitting it AFTER the onEnter render (below) would instead tear
    // down button groups the new mode just mounted in the input (e.g. ORIENTATION step
    // buttons rendered via handleUserMessage). `this.currentMode` is already set above,
    // so listeners see the correct new mode.
    this.eventBus.emit('mode:changed', {
      mode: this.currentMode,
      from: previousMode?.name || 'none',
      to: modeName,
      data: transitionData
    });
    console.log(`[ModeOrchestrator] Mode switched: ${previousMode?.name || 'none'} → ${modeName}`);

    // Handle onEnter return value if it contains an event
    // This allows modes (e.g., ORIENTATION) to trigger programmatic responses on entry
    // by routing the event through the full handleUserMessage pipeline
    if (enterResult?.event && !transitionData.skipWelcome) {
      console.log(`[ModeOrchestrator] Processing onEnter event: ${enterResult.event}`);
      await this.handleUserMessage('', enterResult);
    }
    // Catch-all: Display generic welcome when entering ADVICE in chat window
    // without a specific entry flow (topic transition, explore question, etc.)
    // Skipped when: skipWelcome (dashboard/autocomplete), topicTransition (New Topic button),
    // or when entering from no previous mode (first load handled by dashboard)
    else if (modeName === 'ADVICE' && previousMode && !transitionData.skipWelcome && !transitionData.topicTransition) {
      await this.displayProgrammaticMessage("What's on your mind?", {
        messageType: 'system'
      });
    }
  }

  /**
   * Handle navigation requests from router
   * Central entry point for all router-initiated mode transitions
   *
   * This method is the single source of truth for navigation logic, ensuring:
   * - Session restoration is handled correctly
   * - Mode-specific flows (Discovery, Insight) use appropriate welcome suppression
   * - Generic mode switches show welcome messages
   * - Auto-prompts are sent after session creation
   *
   * @param {Object} navData - Navigation data from router
   * @param {string} navData.mode - Target mode
   * @param {string} [navData.pathId] - Discovery path ID
   * @param {Object} [navData.pathMetadata] - Discovery path metadata
* @param {boolean} [navData.restoreSession] - Whether to restore existing session
   * @param {Object} [navData.autoPrompt] - Auto-prompt to send after session creation
   * @param {string} [navData.autoPrompt.text] - Text to send as user message
   * @param {string} [navData.autoPrompt.source] - Source identifier (e.g., 'autosuggestion')
   */
  async handleNavigation(navData) {
    const {
      mode,
      pathId,
      pathMetadata,
      topicId,
      restoreSession,
      autoPrompt
    } = navData;

    console.log('[ModeOrchestrator] Handling navigation:', { mode, pathId, topicId, restoreSession, autoPrompt: !!autoPrompt });

    // Session restoration - restore mode display without creating new session.
    // No sessionMeta to pass here (this branch has no prior fetch) — for
    // TREATMENT, restoreMode() fetches it internally so getModeLabel()
    // still resolves correctly.
    if (restoreSession) {
      console.log('[ModeOrchestrator] Restoring session for mode:', mode);
      await this.restoreMode(mode);
      return;
    }

    // Discovery — three entry modes share one target state machine.
    // Entry-mode precedence: topic > path > guided. First branch to
    // match wins; all other navData keys are ignored by that branch.
    if (mode === 'DISCOVERY') {
      // Already in an active DISCOVERY session and no specific scope requested:
      // user flipped to the /discovery overlay and clicked Guided Discovery to
      // return. Chat is already visible and the session is live — preserve it
      // rather than re-running the guided loop (which would otherwise append a
      // duplicate topic-complete sequence after a just-finished topic).
      if (this.currentMode?.name === 'DISCOVERY' && !pathId && !topicId) {
        return;
      }

      // Topic-mode entry
      if (topicId) {
        await this.switchMode('DISCOVERY', { skipWelcome: true });
        try {
          await this._runTopicLoop(topicId);
        } catch (error) {
          console.error('[ModeOrchestrator] Failed to enter topic-mode Discovery:', error);
          this.chatManager.displayError('Failed to load topic. Please try again.');
        }
        return;
      }

      // Path-mode or guided-mode (existing paths).
      await this.switchMode('DISCOVERY', {
        pathId,
        metadata: pathMetadata,
        skipWelcome: true
      });
      try {
        await this._enterDiscoveryNavigation(pathId || null);
      } catch (error) {
        console.error('[ModeOrchestrator] Failed to enter Discovery navigation:', error);
        this.chatManager.displayError('Failed to load Discovery. Please try again.');
      }
      return;
    }

    // Treatment with practice context.
    if (mode === 'TREATMENT' && navData.practiceId) {
      if (this._checkinEntryInFlight) return;
      const subMode = navData.subMode || 'checkin';
      // `force: true` is REQUIRED: a cross-mode terminus (practice completion /
      // check-in wind-down) can offer a check-in on ANOTHER active practice
      // while the user is already in a TREATMENT check-in session. Without force,
      // switchMode's same-mode early-return no-ops the switch — no new session is
      // created and the report would land in the just-finished practice's
      // session. force creates the fresh check-in (backend closes the prior one).
      // Harmless for non-TREATMENT origins (the early-return never fires there).
      //
      // The check-in opens with a visible synthetic user opener; the assistant's
      // greeting carries the pinned reference card and invites the report (the
      // CHECKIN prompt's OPEN section in
      // backend/src/constants/systemPrompts/treatmentPrompt.js and
      // project-docs/components/03-mode-strategies-and-tools.md).
      //
      // No try/catch around the pair: switchMode toasts and rethrows its own
      // failures, and handleUserMessage catches and displays its own.
      this._checkinEntryInFlight = true;
      try {
        await this.switchMode('TREATMENT', {
          skipWelcome: true,
          force: true,
          practiceId: navData.practiceId,
          subMode
        });
        if (subMode === 'checkin') {
          await this.handleUserMessage(TreatmentMode.CHECKIN_OPENER, {
            metadata: { source: 'treatment_checkin' }
          });
        }
      } finally {
        this._checkinEntryInFlight = false;
      }
      return;
    }

    // Open thread entry — ADVICE session with pre-scoped context
    if (mode === 'ADVICE' && navData.openThreadId && navData.openThread) {
      const thread = navData.openThread;
      await this.switchMode('ADVICE', {
        skipWelcome: true,
        force: true,
        entryType: 'explore_question',
        openThreadId: thread.id,
        question: thread.question,
        context: thread.context,
        sourceSessionId: thread.sessionId,
        observationId: thread.observationId
      });

      // The thread is NOT marked addressed here. Clicking a thread used to
      // optimistically mark it at launch — closing it before any conversation
      // happened. The thread-agent post-processing step now owns closure: it
      // reports a resolved/progressed outcome from what the session actually
      // worked, so an abandoned click leaves the thread open.

      // Auto-send the thread question — AdviceStrategy will detect
      // entryType='explore_question' and build rich context (observation,
      // digests, source session summary) for the LLM response.
      // NOTE: this wording is display-only. The model receives a rewritten
      // framing (SonnetGenerator modelFacingContent, keyed on
      // metadata.source === 'explore_question') because Sonnet 5 reads
      // "I'd like to explore this question" as an invitation to answer the
      // quoted question in the user's first-person voice (~1/6 turns).
      const expectedMode = this.getCurrentMode();
      setTimeout(() => {
        if (this.getCurrentMode() !== expectedMode) return;
        this.handleUserMessage(`I'd like to explore this question: "${thread.question}"`, {
          metadata: {
            source: 'explore_question',
            openThreadId: thread.id
          }
        });
      }, 100);
      return;
    }

    // Guide-thread entry — a `thread` atom inside a guide. Same ADVICE
    // `explore_question` contract as an open thread, but there is no OpenThread
    // row behind it (a guide thread is authored at generation time and never
    // persisted to the ledger), so it cannot go through the branch above, which
    // requires `openThreadId` AND `openThread`. Without its own branch it fell
    // through to the generic switchMode below, which forwards `entryType` but
    // has no auto-send — leaving the user staring at an empty pre-scoped session.
    if (mode === 'ADVICE' && navData.guideThread) {
      const thread = navData.guideThread;
      await this.switchMode('ADVICE', {
        skipWelcome: true,
        force: true,
        entryType: 'explore_question',
        question: thread.question,
        context: thread.context
      });

      // Same display-only wording and the same `metadata.source` token as the
      // open-thread path. The source is load-bearing, NOT cosmetic:
      // SonnetGenerator's `modelFacingContent` rewrite is keyed on
      // `explore_question` and exists because Sonnet 5 otherwise answers the
      // quoted question in the user's own first-person voice (~1/6 turns).
      const expectedMode = this.getCurrentMode();
      setTimeout(() => {
        if (this.getCurrentMode() !== expectedMode) return;
        this.handleUserMessage(`I'd like to explore this question: "${thread.question}"`, {
          metadata: { source: 'explore_question' }
        });
      }, 100);
      return;
    }

    // Guide-insight entry — a claim the user tapped inside a guide. Unlike a
    // thread, this does NOT arrive pre-scoped: the claim is a statement about
    // the user, not a statement of what they want to work on, so the session
    // runs its own scoping-family Phase-1 arc (restate the claim, invite,
    // stop — SESSION_ARC_GUIDE_INSIGHT) and still scopes before retrieval.
    // Only the atom's identity crosses — the backend reloads the user-owned
    // READY guide and extracts the claim itself.
    if (mode === 'ADVICE' && navData.guideInsight) {
      const atom = navData.guideInsight;
      await this.switchMode('ADVICE', {
        skipWelcome: true,
        force: true,
        entryType: 'guide_insight',
        guideId: atom.guideId,
        sectionType: atom.sectionType,
        blockIndex: atom.blockIndex
      });

      // The guard compares the session id as well as the mode: a second forced
      // ADVICE navigation inside the 100 ms window keeps the mode but replaces
      // the session, and a mode-only check would drop this atom's opener into
      // the second session.
      const expectedMode = this.getCurrentMode();
      const expectedSessionId = sessionManager.getCurrentSessionId();
      setTimeout(() => {
        if (this.getCurrentMode() !== expectedMode
          || sessionManager.getCurrentSessionId() !== expectedSessionId) return;
        this.handleUserMessage(`I'd like to talk about this: "${atom.lead}"`, {
          metadata: {
            source: 'guide_insight',
            guideId: atom.guideId,
            sectionType: atom.sectionType,
            blockIndex: atom.blockIndex
          }
        });
      }, 100);
      return;
    }

    // Shared-guide-insight entry — a claim tapped on the Together part of the
    // merged reader. Same scoping-family shape as guide_insight, but the claim
    // is about BOTH partners and only one of them is in the room, so it runs
    // its own relational Phase-1 arc (SESSION_ARC_SHARED_GUIDE_INSIGHT). The
    // identity that crosses is `{ sharedGuideDefinitionId, sectionType,
    // blockIndex }` under its own key — never a `UserGuide` id — and the
    // backend resolves it through `sharedGuideService.resolveForViewer`.
    if (mode === 'ADVICE' && navData.sharedGuideInsight) {
      const atom = navData.sharedGuideInsight;
      await this.switchMode('ADVICE', {
        skipWelcome: true,
        force: true,
        entryType: 'shared_guide_insight',
        sharedGuideDefinitionId: atom.sharedGuideDefinitionId,
        sectionType: atom.sectionType,
        blockIndex: atom.blockIndex
      });

      // Same mode + session-id guard as the guide_insight branch above.
      const expectedMode = this.getCurrentMode();
      const expectedSessionId = sessionManager.getCurrentSessionId();
      setTimeout(() => {
        if (this.getCurrentMode() !== expectedMode
          || sessionManager.getCurrentSessionId() !== expectedSessionId) return;
        this.handleUserMessage(`I'd like to talk about this from our guide: "${atom.lead}"`, {
          metadata: {
            source: 'shared_guide_insight',
            sharedGuideDefinitionId: atom.sharedGuideDefinitionId,
            sectionType: atom.sectionType,
            blockIndex: atom.blockIndex
          }
        });
      }, 100);
      return;
    }

    // Artifact-discussion entry — "Discuss X" launched from a library card
    // (pattern; framework joins as another config row). Like guide_insight,
    // this does NOT arrive pre-scoped: the artifact is a statement about the
    // user, not a statement of what they want to work on, so the session
    // still runs its own scoping-family Phase-1 arc. Only the item's identity
    // crosses — the backend reloads it server-side.
    if (mode === 'ADVICE' && navData.artifactDiscussion) {
      const { discriminator, itemId, sourceItem } = navData.artifactDiscussion;
      const config = ModeOrchestrator.ARTIFACT_DISCUSSION_ENTRIES[discriminator];
      if (!config) return;

      await this.switchMode('ADVICE', {
        skipWelcome: true,
        force: true,
        entryType: config.entryType,
        itemId,
        sourceItem
      });

      const expectedMode = this.getCurrentMode();
      setTimeout(() => {
        if (this.getCurrentMode() !== expectedMode) return;
        this.handleUserMessage(config.openerTemplate(sourceItem), {
          metadata: config.metadataFields(itemId)
        });
      }, 100);
      return;
    }

    // Generic mode switch - skip welcome if autoPrompt will follow.
    // Thread session_continuation through as entryType so AdviceMode.getModeLabel()
    // resolves synchronously (backend `_resolveEntryType` derives the same value
    // from `metadata.source` on the message moments later — this just avoids
    // waiting on that round-trip for the header).
    await this.switchMode(mode, {
      ...navData,
      ...(autoPrompt?.source === 'session_continuation' ? { entryType: 'session_continuation' } : {}),
      skipWelcome: !!autoPrompt?.text
    });

    // Handle auto-prompt after session is created
    // Used by suggested prompts feature to auto-send a message
    if (autoPrompt?.text) {
      console.log('[ModeOrchestrator] Sending auto-prompt:', autoPrompt.text);
      // Small delay to ensure welcome message renders first
      const expectedMode = this.getCurrentMode();
      setTimeout(() => {
        if (this.getCurrentMode() !== expectedMode) return;
        this.handleUserMessage(autoPrompt.text, {
          metadata: {
            source: autoPrompt.source || 'autosuggestion',
            // Include sourceSessionIds for topic continuation context
            sourceSessionIds: autoPrompt.sourceSessionIds || undefined
          }
        });
      }, 100);
    }
  }

  /**
   * Set path context on DiscoveryMode and trigger header re-render.
   * Single authoritative point for updating path metadata on the mode instance.
   *
   * @private
   * @param {string} pathId - Path ID
   * @param {Object} metadata - { id, name, phase?, description? }
   */
  async _updateDiscoveryPath(pathId, metadata) {
    const mode = this._discoveryMode();
    if (!mode) return;

    mode.currentPathId = pathId;
    mode.currentPathMetadata = metadata;
    mode.percentComplete = 0;
    mode.isPathComplete = false;
    mode.totalSections = 0;
    mode.completedSections = 0;
    mode.sectionsRemaining = 0;
    mode.minutesRemaining = 0;
    mode.l2EstimatedMinutes = 5;
    // The new path owns no open section arc yet. clearStage() below cannot do
    // this — it is the mid-arc variant, which deliberately preserves it.
    mode.activeSectionName = null;

    // Fetch progress before re-rendering header
    if (mode.currentTopicId) {
      await mode._fetchTopicProgress();
    } else {
      await mode._fetchPathProgress();
    }
    mode.clearStage();
  }

  /**
   * Load discovery for a specific path via orchestrator loop.
   * Used by in-app triggers (path card clicks, load_next_path).
   *
   * @param {string} pathId - Path ID to enter discovery for
   * @param {Object} [pathMetadata] - Path metadata for header display
   * @private
   */
  async _loadDiscoveryPath(pathId, pathMetadata) {
    // In-app path card re-entry — delegate to the path walker so the user
    // steps through pathDef.topics[] in declared order.
    if (pathMetadata) {
      await this._updateDiscoveryPath(pathId, pathMetadata);
    }
    await this._runPathLoop(pathId);
  }

  /**
   * Enter guided discovery — fully automated path selection and orchestration.
   * Uses the recommendation engine to auto-select the highest-priority path/topic.
   *
   * @private
   * @param {string|null} [pathId=null] - Explicit path ID, or null for auto-select
   */
  async _enterDiscoveryNavigation(pathId = null) {
    // Header shows loading state while orchestrator initializes
    this._discoveryMode()?.setThinkingStage('loading');
    this.chatManager.activateThinkingIndicator();

    const { DiscoveryAPI } = await import('../core/discoveryApi.js');
    const discoveryAPI = new DiscoveryAPI();

    if (!pathId) {
      // Guided discovery — ranker picks the topic
      this._isGuidedMode = true;
      const loopResult = await this._runGuidedLoop(discoveryAPI);

      // Foundation not yet complete — fall through to load it as an explicit path.
      if (loopResult?.type === 'foundation_required') {
        this._isGuidedMode = false;
        pathId = loopResult.pathId;
      } else {
        return;
      }
    }

    // Explicit entry. Foundation keeps its dedicated orchestrator path;
    // every other path is walked topic-by-topic via _runPathLoop.
    this._isGuidedMode = false;

    if (pathId === 'foundation') {
      await this._enterFoundation(discoveryAPI);
      return;
    }

    await this._runPathLoop(pathId);
  }

  /**
   * Foundation-specific discovery entry. Foundation stays on the scope-keyed
   * orchestrator loop directly (scopeId === 'foundation') rather than going
   * through the path walker — it has a dedicated completion ceremony and
   * is not a standard authored path with a topics[] array to walk.
   *
   * @private
   * @param {DiscoveryAPI} discoveryAPI
   */
  async _enterFoundation(discoveryAPI, loopOptions = { initialAction: 'start_orchestration' }) {
    let pathData = null;
    try {
      const result = await discoveryAPI.getPathById('foundation');
      pathData = result?.data || null;
    } catch (error) {
      console.warn('[ModeOrchestrator] Failed to fetch Foundation metadata:', error);
    }

    // Suppress the "welcome back" intro when re-entering immediately after intake;
    // the post-intake handoff body already framed the transition.
    const isPostIntakeContinuation = loopOptions?.initialAction === 'prerequisite_completed';
    this._welcomeSignalData = (pathData && !isPostIntakeContinuation) ? {
      recommendedPath: {
        name: pathData.name,
        description: pathData.description,
        recommendationReason: null
      },
      partnerName: this._discoveryMode()?.currentPathMetadata?.partnerName
        || null,
      fromOrientation: this._fromOrientation || false
    } : null;

    this._currentDiscoveryScopeId = 'foundation';
    this._currentDiscoveryEntryMode = 'foundation';
    this._currentDiscoveryPathId = 'foundation';

    if (pathData) {
      await this._updateDiscoveryPath('foundation', {
        id: pathData.id,
        name: pathData.name,
        phase: pathData.phase,
        description: pathData.description || '',
        cardContent: pathData.cardContent
      });
    }

    await this._runOrchestratorLoop('foundation', loopOptions);
  }

  /**
   * Guided discovery loop — uses subcategory-level ranking engine.
   * Iterates: get next subcategory → run orchestrator for its sections → repeat.
   *
   * @private
   * @param {DiscoveryAPI} discoveryAPI - API instance
   * @param {Object} [options]
   * @param {boolean} [options.continuation] - true when the loop is re-entered
   *   from a continuation action (e.g. "Continue Discovery" or "Load Next Topic")
   *   rather than fresh entry. Suppresses the welcome_back intro signal.
   * @param {string} [options.excludeL3Section] - Section to exclude from L3
   *   ranking on the first iteration only. Set when re-entering after an L3
   *   close so the async narrative pipeline has time to write before that
   *   section is re-considered.
   */
  async _runGuidedLoop(discoveryAPI, { continuation = false, excludeL3Section = null } = {}) {
    // Continuation entries (e.g. "Continue Discovery" button after Foundations)
    // clear any lingering welcome signal so the intro opens with a brief bridge
    // beat rather than a stale "Welcome back" greeting. Must happen BEFORE the
    // first iteration's signal-setting block, since that block's guard keeps the
    // welcome signal if one already exists.
    if (continuation) {
      this._welcomeSignalData = null;
      this._continuationSignal = true;
    }

    let lastCompletedTopicId = null;
    let pendingExcludeL3Section = excludeL3Section;
    const pausedTopics = new Set();

    while (true) {
      let topicResult;
      try {
        topicResult = await discoveryAPI.fetchNextForGuided(
          [...pausedTopics],
          pendingExcludeL3Section ? { excludeL3Section: pendingExcludeL3Section } : {}
        );
        pendingExcludeL3Section = null; // one-shot — only applies to the first call after L3 close
      } catch (error) {
        throw error;
      }

      const data = topicResult?.data;

      // Journey complete — all topics done
      if (data?.journeyComplete) {
        this.chatManager.displayAssistantMessage(
          'You\'ve completed all available discovery topics! You can explore topics in Advice mode.',
          { messageType: 'system' }
        );
        this.eventBus.emit('message:complete');
        return;
      }

      // Foundation gate: backend signals that Foundation must be completed first
      if (data?.type === 'foundation_required') {
        return { type: 'foundation_required', pathId: data.pathId };
      }

      // L3 deep-dive gate: backend signals that all topics have been covered
      // at L1+L2 and the next best action is a deep dive on a specific section.
      // The L3 config is inlined on the same response so we don't need a second
      // round-trip to prepare the session.
      if (data?.type === 'l3_session') {
        await this._startL3Session(data);
        return; // Callback re-enters guided loop after L3 completes
      }

      if (!data?.topicId) {
        console.error('[ModeOrchestrator] Ranking engine returned no topic:', data);
        // Nothing renders on this exit — tear the indicator down explicitly.
        this.chatManager?.deactivateThinkingIndicator?.();
        return;
      }

      // Guard against re-selecting same topic (delayed SectionProgress writes)
      if (data.topicId === lastCompletedTopicId) {
        console.warn('[GuidedLoop] Ranking returned same topic, re-querying...');
        await new Promise(r => setTimeout(r, 500));
        continue;
      }

      const topicId = data.topicId;
      const mode = this._discoveryMode();
      if (mode) {
        mode.currentTopicId = topicId;
        mode.currentTopicLabel = data.label;
        mode.currentTopicDescription = data.description || '';
        mode.topicProgress = 0;
      }

      discoveryAPI.selectTopic(topicId).catch(e =>
        console.warn('[GuidedLoop] Failed to persist topic selection:', e)
      );

      // Prepare welcome signal data on first iteration of a fresh entry.
      // Continuation entries set _continuationSignal upfront (see top of loop),
      // so this block only fires for fresh-entry welcome framing.
      if (!continuation && !lastCompletedTopicId && !this._welcomeSignalData) {
        this._welcomeSignalData = {
          recommendedPath: {
            name: data.label,
            description: data.description || '',
            recommendationReason: null
          },
          partnerName: mode?.currentPathMetadata?.partnerName || null,
          lastCompletedSection: data.lastCompletedSection || null
        };
      }

      // Scope = topicId. Guided mode does not pin a path.
      this._currentDiscoveryScopeId = topicId;
      this._currentDiscoveryEntryMode = 'guided';
      this._currentDiscoveryPathId = null;

      // Run orchestrator loop for this topic's sections. start_orchestration
      // clears session-scoped declines server-side; we fold it into the loop's
      // first iteration so the same call both records the event and returns
      // the next step (no redundant poll).
      const loopResult = await this._runOrchestratorLoop(topicId, {
        targetTopic: topicId,
        initialAction: 'start_orchestration'
      });

      if (loopResult === 'paused') {
        pausedTopics.add(topicId);
        continue;
      }

      if (loopResult === 'awaiting_user') {
        return;
      }

      lastCompletedTopicId = topicId;
      // Loop continues — ranking engine picks next topic
    }
  }

  /**
   * Path-mode walker loop. Iterates `pathDef.topics[]` in declared order,
   * serving each topic via the orchestrator until the path completes. Unlike
   * guided mode, topic selection is static (from path config), not ranked.
   *
   * @private
   * @param {string} pathId - Authored path id (NOT 'foundation')
   */
  async _runPathLoop(pathId) {
    if (pathId === 'foundation') {
      console.warn('[PathLoop] Foundation is not a walker path; falling back to foundation entry.');
      const { DiscoveryAPI } = await import('../core/discoveryApi.js');
      const discoveryAPI = new DiscoveryAPI();
      await this._enterFoundation(discoveryAPI);
      return;
    }

    this._currentDiscoveryScopeId = `path:${pathId}`;
    this._currentDiscoveryEntryMode = 'path';
    this._currentDiscoveryPathId = pathId;

    const mode = this._discoveryMode();
    if (mode) {
      // Same-mode re-entry early-returns from switchMode, so onEnter/onExit never
      // run and topic/L3 labels from a prior scope can survive. They outrank
      // pathName in _buildHeaderTopRow, and a stale currentTopicId misroutes
      // _updateDiscoveryPath to topic progress. Clear path-incompatible state before
      // hydrating path metadata. The orchestrator loop repopulates currentTopicLabel
      // as it serves each topic.
      mode.currentTopicId = null;
      mode.currentTopicLabel = null;
      mode.currentTopicDescription = null;
      mode.currentL3TopicLabel = null;
      mode.currentL3SectionName = null;

      // Ensure the header shows the path's real name. Cross-mode path-card entries
      // thread metadata through switchMode → onEnter and arrive populated; id-only
      // entries (load_path button, or same-mode re-entry where onEnter never runs)
      // arrive unset, which would render the generic "Discovery" fallback — fetch it
      // here in that case. Require both a matching id and a non-empty name so partial
      // metadata doesn't slip through the dedup guard.
      if (mode.currentPathMetadata?.id !== pathId || !mode.currentPathMetadata?.name) {
        try {
          const { DiscoveryAPI } = await import('../core/discoveryApi.js');
          const result = await new DiscoveryAPI().getPathById(pathId);
          const pathData = result?.data || null;
          if (pathData) {
            await this._updateDiscoveryPath(pathId, {
              id: pathData.id,
              name: pathData.name,
              phase: pathData.phase,
              description: pathData.description || '',
              cardContent: pathData.cardContent
            });
          }
        } catch (error) {
          console.warn('[PathLoop] Failed to fetch path metadata for header:', pathId, error);
        }
      }
    }

    // Fresh entry into an authored path gets the same welcome beat Foundation
    // and guided entries already get (_enterFoundation / _runGuidedLoop). Without
    // it the first message opens cold on the section intro, because the composer
    // is explicitly told not to greet unless a welcome_back signal is present.
    // The remaining-work figures are attached at signal-composition time from the
    // step response — the authoritative read, not a second progress fetch.
    if (!this._welcomeSignalData) {
      this._welcomeSignalData = {
        recommendedPath: {
          name: this._discoveryMode()?.currentPathMetadata?.name || null,
          description: '',
          recommendationReason: null
        },
        partnerName: this._discoveryMode()?.currentPathMetadata?.partnerName || null
      };
    }

    return this._runOrchestratorLoop(pathId, {
      initialAction: 'start_orchestration'
    });
  }

  /**
   * Topic-mode direct entry loop. Serves a single topic and, on completion,
   * returns the user to /discovery rather than auto-advancing. Path-completion
   * detectors (Phase 6.4) still fire when this topic closes out a path.
   *
   * @private
   * @param {string} topicId - Topic registry id
   */
  async _runTopicLoop(topicId) {
    this._currentDiscoveryScopeId = topicId;
    this._currentDiscoveryEntryMode = 'topic';
    this._currentDiscoveryPathId = null;

    // Topic header — pull label/description from registry via API. The
    // /topics/:topicId/next endpoint will also return topicLabel in its
    // orchestrator response, so the header lands correctly after the first
    // serve; this is just a pre-render hint while the first call resolves.
    const mode = this._discoveryMode();
    if (mode) {
      mode.currentTopicId = topicId;
      mode.topicProgress = 0;
    }

    await this._runOrchestratorLoop(topicId, {
      targetTopic: topicId,
      initialAction: 'start_orchestration'
    });

    // On topic_complete, _runOrchestratorLoop returns after _handleTopicComplete
    // renders its button group. Topic mode does NOT auto-advance — user takes
    // one of the buttons (continue-next kicks guided; return-home navigates to
    // /discovery).
  }

  /**
   * Handle topic completion — celebration moment modeled on foundation completion.
   *
   * @private
   * @param {Object} topicResult - { topicId, topicLabel, pathId }
   * @param {DiscoveryAPI} discoveryAPI - API instance
   */
  async _handleTopicComplete(topicResult, discoveryAPI) {
    const mode = this._discoveryMode();
    const sessionId = this._getSessionId();
    const topicLabel = topicResult.topicLabel || 'this topic';
    const entryMode = this._currentDiscoveryEntryMode;
    const isGuidedLike = entryMode === 'guided' || entryMode === 'topic';
    const isPathFlow = entryMode === 'path' || entryMode === 'foundation';

    // Capture transient L2 state before clearing — closingText feeds the reflection
    // paragraph when the topic completed via L2 engagement.
    const closingText = this._l2ClosingText || null;
    this._l2SectionName = null;
    this._l2SessionId = null;
    this._l2ClosingText = null;

    if (isGuidedLike) {
      // Clear persisted topic selection for guided/topic flows only.
      discoveryAPI.clearTopicSelection().catch(e =>
        console.warn('[TopicComplete] Failed to clear topic selection:', e)
      );
    }

    // Set complete stage
    mode?.setCompleteStage();

    let nextTopicData = null;
    let scenario = 'journey_complete';
    if (isGuidedLike) {
      try {
        const nextResult = await discoveryAPI.getNextTopic();
        if (nextResult?.data && !nextResult.data.journeyComplete) {
          nextTopicData = nextResult.data;
          scenario = 'next_available';
        }
      } catch (_e) { /* journey complete */ }
    } else if (isPathFlow && topicResult.pathContinuing) {
      scenario = 'next_available';
      if (topicResult.nextTopicLabel) {
        nextTopicData = { label: topicResult.nextTopicLabel };
      }
    }

    // Stream topic completion via Haiku — context shape matches buildTopicCompletePrompt
    const contentPlan = [{
      type: 'llm',
      intent: 'topic_complete',
      intentContext: {
        topicLabel,
        engagedSections: topicResult.engagedSections || [],
        permanentlyDeclinedSections: topicResult.permanentlyDeclinedSections || [],
        hadL2Conversations: !!topicResult.hadL2Conversations,
        closingText,
        entryPath: topicResult.entryPath || 'after_l2',
        scenario,
        nextTopicLabel: nextTopicData?.label || null,
        partnerName: mode?.currentPathMetadata?.partnerName || null
      }
    }];

    // Topic-complete prelude: acknowledgment + optional <topic-understanding>
    // bar. Renders BEFORE the Haiku celebration so the animated bar precedes
    // the reflection text. Declined-path yields a short acknowledgment only.
    // displayAssistantMessage is synchronous (not a Promise) — awaiting it
    // directly is a no-op, so we explicitly hold for its animationDuration
    // (which includes the bar's own completion time) before moving on,
    // otherwise the Haiku celebration can render while the bar is still
    // animating.
    const preludeResult = this.chatManager.displayAssistantMessage(
      this._buildTopicCompletePrelude(topicLabel),
      { messageType: 'discovery' }
    );
    // A self-timed <topic-understanding> bar keeps animating past its own
    // fade-in — show shimmer bars for that stretch (the immediate re-arm to
    // waiting-phase below covers the network round trip that follows).
    if (preludeResult?.hasSelfTimedHold) {
      this.chatManager.activateThinkingIndicator();
    }
    if (preludeResult?.animationDuration > 0) {
      await new Promise(resolve => setTimeout(resolve, preludeResult.animationDuration));
    }

    mode?.setThinkingStage('completing');
    // REVERT: waiting-phase arming site — see frontend-user/docs/thinking-indicator-revert.md
    // Content-plan pause: every branch from here streams via handleResponse
    // (SSE), so the logo+text may cover the queue round trip + TTFB.
    this.chatManager.activateThinkingIndicator(undefined, { waiting: true });

    try {
      const job = await discoveryAPI.queueContentPlan(sessionId, contentPlan);
      await window.responseHandler.handleResponse(job);
    } catch (err) {
      // A queue/stream failure must not strand a spinning logo (live RAF clock)
      // under an error state.
      this.chatManager.deactivateThinkingIndicator();
      throw err;
    }

    // Confetti
    if (window.confetti) {
      window.confetti({ particleCount: 80, spread: 70, origin: { y: 0.7 } });
    }

    // Action buttons
    if (window.actionButtons) {
      let buttons = null;
      let buttonGroupId = null;

      if (isGuidedLike) {
        buttonGroupId = scenario === 'next_available'
          ? 'discovery-topic-complete-next'
          : 'discovery-topic-complete-done';

        try {
          const { API_URL: baseUrl } = await import('../core/api.js');
          const resp = await fetch(`${baseUrl}/button-groups/${buttonGroupId}`, { credentials: 'include' });
          const result = await resp.json();
          buttons = result?.data?.buttons;
        } catch (_e) { /* fallback below */ }
      }

      if (!buttons) {
        if (isGuidedLike) {
          buttons = scenario === 'next_available'
            ? [
                { label: `Continue to ${nextTopicData?.label || 'Next Topic'}`, action: 'load_next_topic' },
                { label: 'Choose different topic', action: 'return_to_discovery_home' }
              ]
            : [{ label: 'Back to Discovery Home', action: 'return_to_discovery_home', metadata: { recommended: true } }];
        } else if (isPathFlow) {
          const continueLabel = topicResult.pathContinuing
            ? `Continue to ${topicResult.nextTopicLabel || 'Next Topic'}`
            : 'Complete Path';

          buttons = [
            { label: continueLabel, action: 'continue_discovery_path', metadata: { recommended: true } },
            { label: 'Back to Discovery Home', action: 'return_to_discovery_home' }
          ];
        } else {
          buttons = [{ label: 'Back to Discovery Home', action: 'return_to_discovery_home' }];
        }
      } else if (scenario === 'next_available' && nextTopicData?.label) {
        const continueBtn = buttons.find(b => b.action === 'load_next_topic');
        if (continueBtn) continueBtn.label = `Continue to ${nextTopicData.label}`;
      }

      const resolvedGroupId = buttonGroupId || 'discovery-topic-complete-inline';
      const buttonGroup = window.actionButtons.createButtonGroup(buttons, { groupId: resolvedGroupId });
      this.chatManager.messageList.addInteractiveComponent(buttonGroup, {
        type: 'action-buttons',
        data: { groupId: resolvedGroupId }
      });
    }

    this.eventBus.emit('message:complete');
  }

  /**
   * Handle "Continue to Next Topic" action from topic completion buttons.
   * Called by ActionRouter's `load_next_topic` handler.
   */
  async _handleLoadNextTopic() {
    const mode = this._discoveryMode();
    const discoveryAPI = new (await import('../core/discoveryApi.js')).DiscoveryAPI();

    try {
      const nextResult = await discoveryAPI.getNextTopic();
      const data = nextResult?.data;

      if (!data || data.journeyComplete || !data.topicId) {
        window.eventBus.emit('navigate', { page: 'discovery' });
        return;
      }

      // Update topic state
      if (mode) {
        mode.currentTopicId = data.topicId;
        mode.currentTopicLabel = data.label;
        mode.currentTopicDescription = data.description || '';
        mode.topicProgress = 0;
        mode.clearStage();
      }

      // Persist selection
      discoveryAPI.selectTopic(data.topicId).catch(e =>
        console.warn('[LoadNextTopic] Failed to persist topic:', e)
      );

      // Stream topic intro via Haiku
      const sessionId = this._getSessionId();
      if (sessionId) {
        const contentPlan = [{
          type: 'llm',
          intent: 'topic_intro',
          intentContext: {
            topicLabel: data.label,
            topicDescription: data.description || ''
          }
        }];

        mode?.setThinkingStage('loading');
        this.chatManager.activateThinkingIndicator();
        const job = await discoveryAPI.queueContentPlan(sessionId, contentPlan);
        await window.responseHandler.handleResponse(job);
      }

      // Resume guided loop for this topic
      await this._runGuidedLoop(discoveryAPI, { continuation: true });
    } catch (error) {
      console.error('[LoadNextTopic] Error:', error);
      window.eventBus.emit('navigate', { page: 'discovery' });
    }
  }

  /**
   * Fetch the next discovery step for the current entry mode while preserving
   * the existing response envelope contracts.
   *
   * @private
   * @param {DiscoveryAPI} discoveryAPI
   * @param {string} entryId
   * @param {string|null} action
   * @param {Object|null} actionData
   * @returns {Promise<Object>}
   */
  async _fetchDiscoveryNextStep(discoveryAPI, entryId, action, actionData) {
    switch (this._currentDiscoveryEntryMode) {
      case 'foundation':
        return discoveryAPI.postPathNext(entryId, action, actionData || {});
      case 'path':
        return discoveryAPI.postPathNext(entryId, action, actionData || {});
      default:
        // guided / topic — entryId is the topicId; route via the bundled
        // topic POST endpoint so the orchestrator loop preserves the
        // single-round-trip + completionTrigger semantics it depends on.
        return discoveryAPI.postTopicNext(entryId, action, actionData || {});
    }
  }

  /**
   * Core orchestrator loop for a single discovery entry.
   * Uses async while-loop for questionnaires, pauses for intermediary/L2/complete.
   *
   * @private
   * @param {string} entryId - Path, topic, or foundation scope entry id
   */
  async _runOrchestratorLoop(entryId, { targetSubcategory = undefined, targetTopic = undefined, suppressTopicIntro = false, initialAction = null, initialActionData = null } = {}) {
    // Store target for re-entry paths (L2 callback, ActionRouter)
    // Only update if explicitly provided (undefined means "use existing")
    if (targetSubcategory !== undefined) {
      this._currentTargetSubcategory = targetSubcategory;
    }
    if (targetTopic !== undefined) {
      this._currentTargetTopic = targetTopic;
    }
    // Reset transition tracking at the start of each orchestrator loop
    this._lastCompletedQuestionnaireName = null;
    this._topicEntered = (this._discoveryMode()?.percentComplete || 0) > 0;
    this._topicIntroShownThisLoop = suppressTopicIntro;
    this._entryHeaderShown = false;

    const { DiscoveryAPI } = await import('../core/discoveryApi.js');
    const discoveryAPI = new DiscoveryAPI();

    // Consumed once on the first iteration. The backend bundled next endpoint
    // endpoint records the action AND returns the next step in a single call —
    // folding the action into the loop avoids the double-round-trip pattern
    // where a caller records an action then polls again (losing any
    // completion-only fields the early-return branch of getNext omits).
    let pendingAction = initialAction;
    let pendingActionData = initialActionData;

    while (true) {
      const firedAction = pendingAction;
      let result;
      try {
        result = await this._fetchDiscoveryNextStep(
          discoveryAPI,
          entryId,
          pendingAction,
          pendingActionData
        );
      } catch (error) {
        // A path-access refusal ends the loop quietly; everything else keeps
        // its existing propagation so no error path is silently swallowed.
        if (this._handlePathAccessError(error)) return;
        throw error;
      }
      pendingAction = null;
      pendingActionData = null;

      // The bundled call above just RECORDED firedAction server-side. Tier-
      // mutating actions move the path percent (declined counts as done), so
      // refresh here, at the moment the write landed — NOT after this loop
      // resolves, which for the questionnaire branch below is only when the
      // NEXT questionnaire finishes. All three actions write their tier
      // synchronously, so this single read is authoritative. Stale/late
      // responses are dropped by DiscoveryMode's progress epoch + completion pin.
      if (firedAction === 'intermediary_decision'
        || firedAction === 'section_declined'
        || firedAction === 'l2_session_completed') {
        this._refreshDiscoveryProgress();
      }

      const data = result?.data;

      // Capture transient L2 transition fields if present. The backend attaches
      // these when action='l2_session_completed' and the next step is a new
      // questionnaire — they drive the "understanding updated" progress bar on
      // the next intro. Only set when present; subsequent iterations leave
      // these untouched.
      if (data?.l2SectionName) this._l2SectionName = data.l2SectionName;
      if (data?.understanding) this._l2Understanding = data.understanding;

      if (!data?.type) {
        console.error('[ModeOrchestrator] Orchestrator returned no type:', data);
        break;
      }

      if (data.type === 'topic_complete') {
        // Guided mode: topic done — handle topic completion moment
        await this._handleTopicComplete(data, discoveryAPI);
        if (data.pathContinuing) {
          return 'awaiting_user';
        }
        return;
      }

      if (data.type === 'paused') {
        // Deferred sections remain — path stays open but nothing to serve this session.
        // On next session entry, cooldown rewind makes deferred sections available.
        // Nothing renders on this exit, so tear the indicator down explicitly.
        this.chatManager?.deactivateThinkingIndicator?.();
        return 'paused';
      }

      if (data.type === 'complete') {
        await this._handlePathComplete(entryId, data, discoveryAPI);
        return;
      }

      if (data.type === 'questions') {
        // Serve questionnaire and await completion via Promise. Handlers that
        // resume the loop can pass a `{ action, actionData }` payload through
        // resolve(); the loop's next iteration fires it via getPathNext in
        // a single call (preserving completion-only response fields that a
        // redundant second poll would drop). Handlers that just want to
        // advance with no action resolve() without arguments.
        const resumePayload = await new Promise((originalResolve) => {
          let resolved = false;
          const resolve = (payload) => {
            if (resolved) return;
            resolved = true;
            originalResolve(payload);
          };
          this._pendingQuestionnaireResolve = resolve;
          this._serveOrchestratorQuestionnaire(entryId, data, discoveryAPI, resolve);
        });
        this._pendingQuestionnaireResolve = null;
        this._discoveryMode()?.clearEscalationHistory();
        this._currentQuestionnaireButtonsHandle = null;
        this._currentQuestionnaireLauncher = null;
        this._currentSkipQuestionnaireId = null;
        this._currentSkipQuestionnaireName = null;
        this._currentQuestionnaireSkippable = false;
        if (resumePayload?.action) {
          pendingAction = resumePayload.action;
          pendingActionData = resumePayload.actionData || null;
        }
        continue;
      }

      if (data.type === 'intermediary') {
        if (!data.isL2ReServe) {
          this._welcomeSignalData = null; // Welcome moment passed — user is engaged
        }
        // An intermediary is a section mid-arc, and neither stage it passes
        // through (thinking, then resting) names one — so on a resume entry,
        // where the mode reset cleared the arc and the offer is the first thing
        // the user sees, this is the only site that can say which section the
        // popover is describing. Same name the conversation stage uses, so the
        // two agree on which row is current.
        this._discoveryMode()?.openSectionArc(
          data.questionnaireName || data.subcategory?.name || null
        );
        this._discoveryMode()?.setThinkingStage('intermediary');
        await this._handleIntermediary(entryId, data);
        this._discoveryMode()?.setIntermediaryRestingStage();
        return 'awaiting_user'; // Control resumes via action button → ActionRouter → _runOrchestratorLoop
      }

      if (data.type === 'conversation') {
        this._welcomeSignalData = null; // Welcome moment passed — user is engaged
        this._discoveryMode()?.setThinkingStage('l2_starting');
        await this._startL2Session(entryId, data);
        return 'awaiting_user'; // Control resumes via L2 end callback → _runOrchestratorLoop
      }

      if (data.type === 'session' && data.sessionType === 'INTAKE') {
        await this._handleIntakeSessionPrereq(entryId, data);
        return 'awaiting_user'; // Control resumes via close_intake → ActionRouter → _runOrchestratorLoop
      }

      console.warn('[ModeOrchestrator] Unknown orchestrator type:', data.type);
      break;
    }

    // Both `break`s above are unexpected exits that render nothing, so no
    // MessageRenderer.finish() will run to tear the indicator down — without
    // this it shimmers forever under an idle chat. Deliberately NOT a
    // try/finally around the loop: the `return 'awaiting_user'` exits hand off
    // to a live turn and must leave the indicator armed.
    this.chatManager?.deactivateThinkingIndicator?.();
  }

  /**
   * Revert L2 header from thinking stage back to conversation stage.
   * Called on first stream chunk so thinking text only shows during the waiting gap.
   * @private
   */
  _revertL2ThinkingStage() {
    this.eventBus.off('stream:chunk', this._l2ThinkingRevertHandler);
    this._discoveryMode()?.exitThinkingToConversation();
    this._l2ThinkingRevertHandler = null;
  }

  /**
   * Get the current DiscoveryMode instance if active.
   * @private
   * @returns {DiscoveryMode|null}
   */
  _discoveryMode() {
    return this.currentMode?.name === 'DISCOVERY' ? this.currentMode : null;
  }

  /**
   * Refresh the discovery mode's progress figures after a tier-mutating
   * orchestrator action (decline, intermediary decision, L2 completion).
   *
   * One read, no retry: every action that reaches here now writes its tier
   * before the `/next` response returns, so the immediate read already sees it.
   * (This used to fire a second read on a timer, back when an L2's tier was
   * written by the async discovery pipeline long after the response.)
   *
   * Safe to fire blind: the mode's progress epoch drops responses a path switch
   * has outdated, and its completion pin drops responses after the ceremony
   * locked 100%.
   */
  _refreshDiscoveryProgress() {
    void this._discoveryMode()?.refreshProgress();
  }

  /**
   * Serve a questionnaire from the orchestrator loop.
   * Fetches full questionnaire data, launches the QuestionnaireLauncher,
   * and resolves the promise when the user completes it.
   *
   * @private
   * @param {string} pathId - Current path or scope entry id
   * @param {Object} stepData - Orchestrator step data with questionnaire info
   * @param {DiscoveryAPI} discoveryAPI - API instance
   * @param {Function} resolve - Promise resolve to call when questionnaire is complete
   */
  async _serveOrchestratorQuestionnaire(pathId, stepData, discoveryAPI, resolve) {
    const questionnaireId = stepData.questionnaire?.id;
    if (!questionnaireId) {
      console.error('[ModeOrchestrator] No questionnaire ID in orchestrator step');
      resolve();
      return;
    }

    // Track current section code for decline flow (questionnaire:closed handler)
    this._currentQuestionnaireSectionCode = stepData.sectionCode || null;
    // Mirror the Skip-button gating below. When a Skip button is rendered,
    // the close handler suppresses its "Move On" prompt — the visible Skip
    // button already provides an explicit exit path, and the launcher's
    // paused state ("Resume" + "X of Y answered") signals saved progress.
    this._currentQuestionnaireSkippable = stepData.skippable !== false;

    try {
      // 1. Fetch full questionnaire data
      const result = await discoveryAPI.getFullQuestionnaire(questionnaireId);
      if (!result?.data?.questionnaire) {
        console.error('[ModeOrchestrator] Questionnaire not found:', questionnaireId);
        resolve();
        return;
      }

      const questionnaireData = result.data;
      const mode = this.currentMode;
      const pathMetadata = mode?.currentPathMetadata || {};
      const qName = questionnaireData.questionnaire?.name || questionnaireData.questionnaire?.title;
      const qDesc = questionnaireData.questionnaire?.description;

      // Path-scoped flows can cross topic boundaries within a single loop.
      // Re-key intro bookkeeping whenever the served section belongs to a new topic.
      const stepTopicId = stepData.topicId || null;
      if (!this._discoveryVisitedTopics) this._discoveryVisitedTopics = new Set();
      if (stepTopicId && this._currentIntroTopicId !== stepTopicId) {
        this._topicEntered = this._discoveryVisitedTopics.has(stepTopicId) || (stepData.completedSections || 0) > 0;
        this._topicIntroShownThisLoop = false;
        this._currentIntroTopicId = stepTopicId;
      }

      // Header style is keyed to entry mode, not to per-step topic membership.
      // Path/foundation walks keep their path-style top row; only topic
      // and guided entries surface the per-topic header. The same split gates
      // the topic_intro signal below — a path walk must not narrate the topic
      // in prose either.
      const entryMode = this._currentDiscoveryEntryMode;
      const allowTopicHeader = entryMode === 'topic' || entryMode === 'guided';
      const isPathEntry = entryMode === 'path' || entryMode === 'foundation';
      if (mode?.name === 'DISCOVERY' && stepTopicId && allowTopicHeader) {
        mode.currentTopicId = stepTopicId;
        mode.currentTopicLabel = stepData.topicLabel || mode.currentTopicLabel;
        mode.currentTopicDescription = stepData.topicDescription || mode.currentTopicDescription;
      }

      // 2. Compose signals for the discovery_intro intent (topic-based model).
      // Signal order (fixed): welcome_back → continuation → topic_intro → transition → skip
      //   → decline_bridge → conversation_complete → re_serve → resume → section
      const signals = [];

      // Welcome back signal (set by _enterDiscoveryNavigation, consumed once)
      const hadWelcomeSignal = !!this._welcomeSignalData;
      if (this._welcomeSignalData) {
        // Remaining-work figures ride the step response (path entries only), so the
        // greeting quotes the same numbers as the header rather than a second read.
        signals.push({
          type: 'welcome_back',
          ...this._welcomeSignalData,
          ...(Number.isFinite(stepData.pathSectionsRemaining)
            ? { sectionsRemaining: stepData.pathSectionsRemaining }
            : {}),
          ...(Number.isFinite(stepData.pathMinutesRemaining)
            ? { minutesRemaining: stepData.pathMinutesRemaining }
            : {})
        });
        this._welcomeSignalData = null;
      } else if (this._continuationSignal) {
        // Continuation bridge — mutually exclusive with welcome_back, fires when
        // the user resumed the guided loop in-flow (e.g. "Continue Discovery").
        signals.push({ type: 'continuation' });
        this._continuationSignal = false;
      }

      // Topic intro signal — only on the FIRST questionnaire of the orchestrator loop.
      // Full intro on fresh topic entry (isFirstSectionOfTopic=true); brief "picking back
      // up" intro when returning to an in-progress topic. Suppressed when an L2 closing or
      // decline bridge provides the framing instead, or when the user just skipped.
      //
      // Guided/topic entries only. On a path walk the topic is not the unit of work the
      // user chose — a path crosses several topics, so naming one ("let's keep going with
      // Your Inner Life") describes a container that appears nowhere on screen, and its
      // section counts are topic-wide, contradicting the path-scoped counts in the header.
      // Path walks therefore open on whatever else the message carries; with no preceding
      // paragraph the section tail leads instead (_buildSectionIntroTail's isLeadParagraph).
      if (!this._topicIntroShownThisLoop) {
        const suppressedByContext = !!this._l2SectionName || !!this._declinedSectionName;
        const hasSkip = this._lastSkippedQuestionnaireName;
        const hasTopicData = !!stepData.topicId;
        if (hasTopicData && !isPathEntry && !suppressedByContext && !hasSkip) {
          const isBrief = !stepData.isFirstSectionOfTopic;
          signals.push({
            type: 'topic_intro',
            topicLabel: stepData.topicLabel,
            topicDescription: stepData.topicDescription,
            totalSections: stepData.totalSections,
            completedSections: stepData.completedSections,
            brief: isBrief
          });
        }
        this._topicIntroShownThisLoop = true;
        if (stepTopicId) this._discoveryVisitedTopics.add(stepTopicId);
      }

      // Transition signal (acknowledges previous questionnaire completion)
      if (this._topicEntered && this._lastCompletedQuestionnaireName) {
        signals.push({
          type: 'transition',
          previousQuestionnaireName: this._lastCompletedQuestionnaireName
        });
      }

      // Skip signal (acknowledges user skipping the previous questionnaire)
      if (this._topicEntered && this._lastSkippedQuestionnaireName) {
        signals.push({
          type: 'skip',
          previousQuestionnaireName: this._lastSkippedQuestionnaireName,
          hadProgress: this._lastSkippedHadProgress || false
        });
        this._lastSkippedQuestionnaireName = null;
        this._lastSkippedHadProgress = false;
      }

      // Decline bridge signal — user chose "Not Right Now" on an L2 intermediary.
      // Set by ActionRouter via _declinedSectionName; Haiku composes the bridge sentence.
      if (this._declinedSectionName) {
        signals.push({
          type: 'decline_bridge',
          previousSectionName: this._declinedSectionName
        });
        this._declinedSectionName = null;
      }

      // L2 conversation complete — Haiku acknowledges and bridges to next section
      if (this._l2SectionName) {
        const closingText = this._l2ClosingText || null;
        signals.push({
          type: 'conversation_complete',
          sectionName: this._l2SectionName,
          closingText
        });
        this._l2SectionName = null;
        this._l2SessionId = null;
        this._l2ClosingText = null;
      }

      // Re-serve signal (section was previously declined and is being re-served after cooldown)
      if (stepData.reServe) {
        signals.push({
          type: 're_serve',
          previousQuestionnaireName: stepData.reServe.previousQuestionnaireName,
          hadProgress: stepData.reServe.hadProgress
        });
      }

      // Resume signal (saved draft in localStorage)
      const draftKey = `questionnaire_draft_${questionnaireId}`;
      try {
        const draftJson = localStorage.getItem(draftKey);
        if (draftJson) {
          const draft = JSON.parse(draftJson);
          const ageMs = Date.now() - (draft.timestamp || 0);
          const maxAgeMs = 48 * 60 * 60 * 1000; // 48 hours
          if (ageMs < maxAgeMs && draft.responses?.length > 0) {
            const totalQuestions = questionnaireData.questionCount || 0;
            signals.push({
              type: 'resume',
              answeredCount: draft.responses.length,
              totalQuestions,
              progressPercent: totalQuestions > 0 ? Math.round((draft.responses.length / totalQuestions) * 100) : 0,
              lastWorkedOn: draft.timestamp
            });
          }
        }
      } catch (e) {
        // localStorage unavailable or corrupt draft — skip resume signal
      }

      // Section signal (always present — names the section + drives the CTA tail)
      signals.push({
        type: 'section',
        name: qName,
        description: qDesc,
        numQuestions: questionnaireData.questionCount || 0,
        estimatedMinutes: questionnaireData.questionnaire?.estimatedMinutes
      });

      // 3. Determine intro path: hardcoded (direct render) or LLM-generated (via SSE)
      // Hardcoded intros bypass the content-plan/SSE pipeline and render directly via
      // displayAssistantMessage with staggerDelay — matching the OrientationMode pattern
      // (the only working precedent for staggered block rendering in the codebase).
      let hardcodedText = null;

      // Programmatic Foundation intros: exact copy, zero Haiku latency.
      // Falls through to the SSE/Haiku path below when resolveFoundationIntro() returns null
      // (non-Foundation paths, re-serves, L2 re-serves, or non-matching questionnaires).
      const introResult = resolveFoundationIntro({
        stepData,
        frontendState: {
          firstName: window.app?.currentUser?.firstName || 'there',
          hadWelcomeSignal,
          pathIntroduced: this._topicEntered,
          fromOrientation: this._fromOrientation
        }
      });
      if (introResult) {
        hardcodedText = introResult.text;
        if (introResult.clearFromOrientation) {
          this._fromOrientation = false;
        }
      }

      // 4. Show thinking indicator (both paths)
      // First questionnaire on entry shows 'loading', subsequent ones show 'next_assessment'
      this._discoveryMode()?.setThinkingStage(
        this._entryHeaderShown ? 'next_assessment' : 'loading'
      );
      this._entryHeaderShown = true;
      this.chatManager.activateThinkingIndicator();

      // 4.5. If the user just confirmed L2 takeaways, render the understanding
      // progress bar as its OWN bubble first, giving the user a clear
      // "saving → next topic" beat. Runs uniformly for both intro paths.
      const understandingShown = await this._streamPendingUnderstanding();

      // 5. Render the intro — direct path for hardcoded, SSE path for LLM-generated
      if (hardcodedText) {
        // Direct render path: bypass SSE for hardcoded content.
        // streamProgrammaticMessage feeds the renderer block-by-block on a timer,
        // mimicking real LLM streaming. Blocks are added to the DOM only when
        // about to fade in, so the thinking indicator naturally tracks position
        // (no "reserved invisible space" artifact).
        // Brief perceptual delay so the thinking indicator is visible.
        // (Skipped when we already paused 1.5s for the understanding segment.)
        if (!understandingShown) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }

        // Hardcoded Foundation intros only fire on the first questionnaire of Foundation,
        // before any intermediary has been served — no decline_bridge can be pending here.
        const finalText = hardcodedText;

        await this.chatManager.streamProgrammaticMessage(finalText, {
          chunkDelay: 400,
          messageType: 'system'
        });

        if (this._discoveryMode()) {
          this._discoveryMode()._lastRenderedMessage = finalText;
        }
      } else {
        // SSE path: LLM-generated intro via Haiku content plan.
        //
        // KNOWN ARCHITECTURAL DEBT — "Option B" (deferred):
        // The content plan below contains only the LLM intro. The
        // QuestionnaireLauncher is then rendered *imperatively* further down
        // (see `new QuestionnaireLauncher(...)` + `addInteractiveComponent`)
        // AFTER awaiting `responseHandler.handleResponse(job)`. This bypasses
        // the ordering guarantee that ContentPlanExecutor was built to
        // provide — its JSDoc explicitly documents the canonical pattern as:
        //   [
        //     { type: 'llm', intent: 'discovery_intro', ... },
        //     { type: 'component', component: 'questionnaire-launcher', ... },
        //     { type: 'state', updates: { ... } }
        //   ]
        // Because we render the launcher client-side instead, any transport
        // hiccup that lets the await resolve early causes the launcher to
        // appear before the intro text (out-of-order chat content). A
        // tactical patch in ResponseHandler.onerror + ChatManager.fallbackToPoll
        // now awaits the polling fallback before resolving, which closes the
        // immediate race. The architecturally correct fix is to move the
        // launcher (and its state update) into the content plan so the
        // backend's sequential executor owns ordering end-to-end.
        //
        // Why deferred:
        //   - Requires rewiring loop resumption from the onComplete closure
        //     (currently resolves the enclosing promise at ~line 1662) to an
        //     event-based handoff via `questionnaire:completed`.
        //   - Bespoke discovery-loop behaviors around the launcher (Skip
        //     button, lastSubmitResult → scoreResult forwarding into
        //     getPathNext, waitForPanelClose, _lastCompletedQuestionnaireName
        //     tracking) need to move into the event handler.
        //   - Has a hard sequencing dependency on SSE/polling reliability work
        //     (worker.on('failed')/'stalled' → DB FAILED, poll-to-terminal
        //     with ceiling, removal of unconditional .close() + no-message
        //     watchdog). Without those landed first, Option B would regress
        //     a cosmetic "out-of-order" bug into a blocker ("launcher never
        //     appears on SSE drop").
        //
        // Revisit when: the reliability work above ships AND we have
        // appetite for the control-flow refactor.
        const contentPlan = [];

        // Decline bridge is now composed into the unified Haiku message via the
        // `decline_bridge` signal above (no separate static-text bubble anymore).
        contentPlan.push({ type: 'llm', intent: 'discovery_intro', intentContext: { signals } });

        const sessionId = this._getSessionId();
        const job = await discoveryAPI.queueContentPlan(sessionId, contentPlan);

        if (!window.responseHandler) {
          throw new Error('ResponseHandler not initialized');
        }
        try {
          const streamResult = await window.responseHandler.handleResponse(job);
          if (this._discoveryMode()) {
            this._discoveryMode()._lastRenderedMessage = streamResult?.response || null;
          }
        } catch (err) {
          console.error('[ModeOrchestrator] Discovery intro streaming failed:', err);
          const qLabel = stepData.questionnaire?.name || 'the next questionnaire';
          this.chatManager.displayAssistantMessage(
            `Let's continue with ${qLabel}.`,
            { messageType: 'system' }
          );
        }
      }

      // Mark topic as entered after first intro streams
      this._topicEntered = true;
      if (stepTopicId) this._discoveryVisitedTopics.add(stepTopicId);

      // 6. Bottom row narrates the launcher state: "Introducing <name>" while
      // the intro card sits in chat awaiting the user's click. Cleared by
      // questionnaire:opened (→ setQuestionnaireStage) when the user clicks in,
      // or by the next loop iteration's setThinkingStage if they Skip.
      this._discoveryMode()?.setIntroducingQuestionnaireStage(qName);

      // 7. Create and render launcher in the same turn container (below intro text)
      const { QuestionnaireLauncher } = await import('../components/QuestionnaireLauncher.js');

      // Captured during onSubmit so onComplete can forward the real
      // backend scoreResult to the orchestrator. Without this the
      // orchestrator stores an empty stub and any composeIntermediary
      // composer downstream sees undefined fields.
      let lastSubmitResult = null;

      const launcher = new QuestionnaireLauncher({
        questionnaireData,
        pathMetadata,
        eventBus: this.eventBus,
        onSubmit: async (responses, deactivatedQuestionIds) => {
          lastSubmitResult = await discoveryAPI.submitBatchResponses(questionnaireId, responses, deactivatedQuestionIds);
        },
        onComplete: async (completionData) => {
          // Track for transition messages
          this._lastCompletedQuestionnaireName =
            questionnaireData.questionnaire?.name || questionnaireData.questionnaire?.title || null;

          // Hide the questionnaire action buttons and clear the active-button
          // registry — completed normally (terminal transition).
          this._currentQuestionnaireButtonsHandle?.hide?.();
          window.actionButtons?.clearActiveButtons();
          // Clear skip metadata (skip and transition are mutually exclusive)
          this._lastSkippedQuestionnaireName = null;
          this._lastSkippedHadProgress = false;

          // Start listening for panel close BEFORE async work.
          // QuestionnaireLauncher emits right-panel:close before calling this callback,
          // so the 300ms close animation is already running.
          const panelClosed = this.chatManager.waitForPanelClose();

          // Build the completion action payload for the loop's next iteration
          // to fire. Firing it here directly would cause the subsequent poll
          // to drop completion-only response fields (e.g. summary.understandings
          // when the final questionnaire completes the path).
          const action = stepData.isPrerequisite ? 'prerequisite_completed' : 'questionnaire_completed';
          // Forward the backend's deterministic scoreResult so downstream
          // composeIntermediary composers receive real data. Falls back to
          // the legacy summary/score stub only if scoreResult is missing
          // (non-clinical questionnaires).
          const realScoring = lastSubmitResult?.data?.scoreResult
            || lastSubmitResult?.scoreResult
            || completionData?.scoringResults
            || { summary: completionData?.summary, score: completionData?.score };

          // Panel slides out while the completed artifact floats to the top;
          // resolves once the panel is gone and layout is stable.
          await this.chatManager.settleArtifact(questionnaireId, panelClosed);

          // Revert header to path-level view (progress bar updates via completed event)
          this._discoveryMode()?.clearStage();

          // Emit completion event for header progress updates
          this.eventBus.emit('questionnaire:completed', {
            pathId,
            pathMetadata,
            questionnaireId
          });

          // Update DiscoveryMode state
          if (mode?.name === 'DISCOVERY') {
            mode.questionnaireActive = false;
            mode.currentQuestionnaireId = null;
          }

          // Resume orchestrator loop, threading the completion action through
          // the Promise so the loop's next iteration fires it via getPathNext
          // in a single call.
          resolve({
            action,
            actionData: { questionnaireId, scoringResults: realScoring }
          });
        }
      });

      // Render launcher in chat (appends to active turn container from streamed intro)
      this.chatManager.messageList.addInteractiveComponent(
        launcher.render(),
        { type: 'questionnaire-launcher', data: { questionnaireId } }
      );

      // 9. Store the launcher + metadata UNCONDITIONALLY so begin_questionnaire /
      //    resume_questionnaire (and the non-skippable "Move on" path) work even when
      //    there is no Skip button. Only the Skip button is gated by skippability.
      this._currentQuestionnaireLauncher = launcher;
      this._currentSkipQuestionnaireId = questionnaireId;
      this._currentSkipQuestionnaireName =
        questionnaireData.questionnaire?.name || questionnaireData.questionnaire?.title || null;

      // Render the questionnaire action buttons (Begin + optional Skip) in the chat
      // INPUT region. The artifact card stays in the transcript (still clickable);
      // these buttons are the input-region affordance shown in the mock.
      this._showQuestionnaireInputButtons({ resumed: false, skippable: stepData.skippable !== false });

      // Update DiscoveryMode state
      if (mode?.name === 'DISCOVERY') {
        mode.questionnaireActive = true;
        mode.currentQuestionnaireId = questionnaireId;
      }

      // No auto-open — user clicks the launcher artifact

    } catch (error) {
      console.error('[ModeOrchestrator] Error serving orchestrator questionnaire:', error);
      resolve();
    }
  }

  /**
   * Build and mount the questionnaire action buttons in the chat INPUT region.
   * Begin/Resume is always present; the second button is Skip (skippable) or
   * "Exit discovery" (non-skippable → return_to_dashboard). Stores the returned
   * cleanup handle on `_currentQuestionnaireButtonsHandle`. `allowTextEntry` is true
   * so a free-typed reply still flows through Discovery's hybrid classifier.
   * @param {Object} [opts]
   * @param {boolean} [opts.resumed=false] - true → "Resume questionnaire" label/action
   * @param {boolean} [opts.skippable=true]
   * @private
   */
  _showQuestionnaireInputButtons({ resumed = false, skippable = true } = {}) {
    if (!window.actionButtons) return;
    const buttons = [
      {
        label: resumed ? 'Resume questionnaire' : 'Begin questionnaire',
        action: resumed ? 'resume_questionnaire' : 'begin_questionnaire',
        metadata: { recommended: true }
      },
      skippable
        ? { label: 'Skip', action: 'skip_questionnaire', className: 'action-btn--skip' }
        : { label: 'Exit discovery', action: 'return_to_dashboard', className: 'action-btn--skip' }
    ];
    const group = window.actionButtons.createButtonGroup(buttons, { groupId: 'questionnaire-actions' });
    this._currentQuestionnaireButtonsHandle = this.chatManager.messageList.addInteractiveComponent(
      group,
      {
        type: 'action-buttons',
        renderLocation: 'input',
        allowTextEntry: true,
        data: { groupId: 'questionnaire-actions' }
      }
    );
  }

  /**
   * Build the assistant message string for a deterministic intermediary
   * payload. Emits an <insight> structured block (same renderer that powers
   * orientation's affectionpatterns side panel — clickable card + rich
   * detail panel via right-panel:open).
   *
   * Composer payload shape (see project-docs/components/08-discovery-framework.md):
   *   {
   *     label,                              // pill-row label e.g. "Your Attachment Profile"
   *     pills?: { primary, secondary? },    // optional pill tags
   *     opener,                             // standalone-coherent prose (also used in L2 prior turn)
   *     items: [{ header, subtitle }, ...], // 2-4 inline rows
   *     closer,                             // standalone-coherent prose (also used in L2 prior turn)
   *     closingQuestion?,                   // optional — invitational pivot to action buttons,
   *                                         //   matches the LLM intermediary convention.
   *                                         //   Display only — NOT forwarded to L2 prior turn.
   *     detail?: {                          // optional — drives the side panel
   *       format: 'structured',
   *       introHeading,                     // panel disclosure label (per-questionnaire — not hard-coded)
   *       intro,                            // markdown framework intro
   *       sections: [{ type, tag, title, lede?, content, gentleNote? }],
   *       rankings?: [{ name, score, maxScore, level }],
   *       questionnaireId?,                 // runtime-enriched (resolveComposedTemplates), never authored —
   *       answeredCount?                    //   together they gate the plates' count-link + "Your answers"
   *     }
   *   }
   *
   * Plain-ASCII contract: composers must not author <, >, & in any text
   * field. JSON detail payload is base64-encoded so its content can never
   * collide with </detail> or any XML control char.
   *
   * @private
   * @param {Object} pm - Composer payload
   * @returns {string}
   */
  _buildPrebuiltIntermediaryString(pm) {
    const itemsXml = pm.items
      .map(it => `<item><header>${it.header}</header><subtitle>${it.subtitle}</subtitle></item>`)
      .join('\n');
    const titleXml = pm.pills?.primary ? `<title>${pm.pills.primary}</title>` : '';
    // pills.secondary may be a string (single secondary, legacy) or an array
    // (multiple secondaries, used by composite composers like BFI-20). Emit
    // one <secondary> tag per entry; the renderer parses all of them.
    const secondaryValues = pm.pills?.secondary == null
      ? []
      : (Array.isArray(pm.pills.secondary) ? pm.pills.secondary : [pm.pills.secondary]);
    const secondaryXml = secondaryValues
      .filter(s => typeof s === 'string' && s.length > 0)
      .map(s => `<secondary>${s}</secondary>`)
      .join('\n');
    let detailXml = '';
    if (pm.detail) {
      // Base64-encode so JSON content can never collide with </detail>
      // or any XML control char. Renderer decodes via atob().
      const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(pm.detail))));
      detailXml = `<detail encoding="base64">${encoded}</detail>`;
    }

    // Clinical path sibling <exploration> block (Phase 4).
    //
    // Composers may return `exploration: Array<{text: string}>` (2-3 items)
    // or `exploration: null` (opt out). Any other value — including `[]` or
    // `undefined` — is treated as opt-out. The block is emitted as a SIBLING
    // after </insight>, NOT wrapped in <intermediary-observations> (that
    // parent is reserved for the LLM path so clinical <insight>'s base64
    // detail payload and pills render untouched).
    let explorationXml = '';
    if (Array.isArray(pm.exploration) && pm.exploration.length > 0) {
      const explorationItems = pm.exploration
        .map(e => (typeof e?.text === 'string' ? e.text.trim() : ''))
        .filter(t => t.length > 0)
        .map(t => `<item>${t}</item>`)
        .join('\n');
      if (explorationItems) {
        explorationXml = `\n\n<exploration>\n${explorationItems}\n</exploration>`;
      }
    }

    const closingQuestionBlock = pm.closingQuestion ? `\n\n${pm.closingQuestion}` : '';
    return `${pm.opener}\n\n<insight>\n<label>${pm.label}</label>\n${titleXml}\n${secondaryXml}\n${itemsXml}\n${detailXml}\n</insight>${explorationXml}\n\n${pm.closer}${closingQuestionBlock}`;
  }

  /**
   * Handle intermediary response — Sonnet narrates from raw Q&A pairs, then programmatic buttons.
   * Uses content plan for LLM narration, then renders discovery-intermediary buttons deterministically.
   *
   * @private
   * @param {string} pathId - Path ID
   * @param {Object} intermediaryData - Orchestrator intermediary data with scoringResults, qaPairs, subcategory
   */
  async _handleIntermediary(pathId, intermediaryData) {
    // Stash the current intermediary section name so ActionRouter can reference
    // it in the `decline_bridge` signal if the user chooses "Not Right Now".
    this._currentIntermediarySectionName = intermediaryData.subcategory?.name || null;

    // L2 re-serve: use Haiku welcome-back instead of Sonnet narration
    if (intermediaryData.isL2ReServe) {
      return this._handleL2ReServeIntermediary(pathId, intermediaryData);
    }

    const { DiscoveryAPI } = await import('../core/discoveryApi.js');
    const discoveryAPI = new DiscoveryAPI();

    // Deterministic path: clinical questionnaire composer pre-built the
    // intermediary card. Render directly via streamProgrammaticMessage —
    // the same convention used for the discovery intro hardcoded path
    // (ModeOrchestrator.js around L1544) and the intake transition message.
    //
    // No persistence: matches the rest of the programmatic-content
    // convention. Refresh handling comes from the orchestrator state
    // machine (still in INTERMEDIARY phase → re-renders the card on next
    // visit). The clinical content itself is already persisted as a
    // MemoryInsight by questionnaireService at scoring time.
    if (intermediaryData.prebuiltMessage) {
      const pm = intermediaryData.prebuiltMessage;
      const messageString = this._buildPrebuiltIntermediaryString(pm);
      const summaryForL2 = `${pm.opener}\n\n${pm.closer}`;

      this.chatManager.activateThinkingIndicator();
      // Brief perceptual delay so the thinking indicator is visible
      // (mirrors the discovery intro hardcoded path).
      await new Promise(resolve => setTimeout(resolve, 300));

      try {
        await this.chatManager.streamProgrammaticMessage(messageString, {
          chunkDelay: 400,
          messageType: 'system'
        });
        if (this._discoveryMode()) {
          this._discoveryMode()._lastRenderedMessage = messageString;
          // L2 hand-off uses opener+closer only — Sonnet sees clean prose
          // as its prior turn instead of the rendered <insight> XML or the
          // closing question (which would read as a redundant bid).
          this._discoveryMode()._lastIntermediaryL2Summary = summaryForL2;
        }
      } catch (err) {
        console.error('[ModeOrchestrator] Prebuilt intermediary rendering failed:', err);
      }

      const group = window.buttonGroupRegistry?.get('discovery-intermediary');
      if (group && window.actionButtons) {
        const buttonGroup = window.actionButtons.createButtonGroup(group.buttons, { groupId: 'discovery-intermediary' });
        this.chatManager.messageList.addInteractiveComponent(buttonGroup, {
          type: 'action-buttons',
          data: { groupId: 'discovery-intermediary' }
        });
      }
      return;
    }

    // 1. Build content plan — Sonnet narrates from raw Q&A pairs
    // Detect returning user: _lastCompletedQuestionnaireName is null when
    // the orchestrator loop started fresh (return visit), set when inline
    const isReturning = !this._lastCompletedQuestionnaireName;

    const intentContext = {
      scoringResults: intermediaryData.scoringResults,
      qaPairs: intermediaryData.qaPairs,
      subcategory: intermediaryData.subcategory,
      isClinical: intermediaryData.isClinical || false,
      frameworkInterpretation: intermediaryData.frameworkInterpretation || null,
      l2Guidance: intermediaryData.l2Guidance || null,
      intermediaryDepth: intermediaryData.intermediaryDepth || 'heavy'
    };

    // Add returning-user context for welcome/path/questionnaire framing
    if (isReturning) {
      const pathMetadata = this._discoveryMode()?.currentPathMetadata || {};
      intentContext.isReturning = true;
      intentContext.pathName = pathMetadata.name || null;
      intentContext.questionnaireName = intermediaryData.questionnaireName || null;
    }

    const contentPlan = [{
      type: 'llm',
      intent: 'intermediary_narration',
      intentContext
    }];

    // 2. Stream Sonnet narration
    if (!window.responseHandler) {
      throw new Error('ResponseHandler not initialized');
    }
    // REVERT: waiting-phase arming site — see frontend-user/docs/thinking-indicator-revert.md
    // Content-plan pause: the only chat output from here is handleResponse (SSE).
    this.chatManager.activateThinkingIndicator(undefined, { waiting: true });
    const sessionId = this._getSessionId();
    try {
      const job = await discoveryAPI.queueContentPlan(sessionId, contentPlan);
      const streamResult = await window.responseHandler.handleResponse(job);
      if (this._discoveryMode()) {
        this._discoveryMode()._lastRenderedMessage = streamResult?.response || null;
      }
    } catch (err) {
      console.error('[ModeOrchestrator] Intermediary narration failed:', err);
      // The fallback below renders instantly (no stream), so nothing else
      // would ever flip a stranded waiting-phase logo — tear it down first.
      this.chatManager.deactivateThinkingIndicator();
      this.chatManager.displayAssistantMessage(
        'I\'ve reviewed your responses — let me know if you\'d like to explore these themes further, or we can move on.',
        { messageType: 'system' }
      );
    }

    // 3. Programmatically render discovery-intermediary buttons (deterministic — always shown)
    const group = window.buttonGroupRegistry?.get('discovery-intermediary');
    if (group && window.actionButtons) {
      const buttonGroup = window.actionButtons.createButtonGroup(group.buttons, { groupId: 'discovery-intermediary' });
      this.chatManager.messageList.addInteractiveComponent(buttonGroup, {
        type: 'action-buttons',
        data: { groupId: 'discovery-intermediary' }
      });
    }

    // User clicks button → ActionRouter 'discovery_intermediary' handler → _runOrchestratorLoop resumes.
  }

  /**
   * Handle a session-type prerequisite (Phase 1: only INTAKE for Foundation onboarding).
   * Renders the screening → intake transition message and a "Let's begin" button.
   * The button click is dispatched via ActionRouter → start_intake handler, which
   * calls switchMode('INTAKE', ...). On close_intake, control returns here via
   * ActionRouter → close_intake handler.
   *
   * Transition message copy is authoritative in:
   *   project-docs/temporary/IMMEDIATE/CURRENT - issue-driven-therapy.md
   *   → "Screening → Intake Transition Message"
   *
   * @private
   * @param {string} pathId
   * @param {Object} sessionData - { type:'session', sessionType, entryType, resumeSessionId, ... }
   */
  async _handleIntakeSessionPrereq(pathId, sessionData) {
    // No discovery-header stage change during the prereq window — the intake
    // header takes over when switchMode('INTAKE') deactivates discovery.

    // Resume path: the backend told us there's an existing ACTIVE INTAKE
    // session within the 12h window that never completed (otherwise the
    // backend would have skipped intake entirely). We auto-attach and
    // silently trigger the agent's welcome-back turn — no transition copy,
    // no button. The agent has full prior history via SonnetGenerator.loadMessageHistory
    // and the INTAKE_RESUME prompt block tells it to open with a contextual greeting.
    if (sessionData.resumeSessionId) {
      console.log('[ModeOrchestrator] Intake resume — auto-attaching to', sessionData.resumeSessionId);
      try {
        // Snapshot DISCOVERY's current top-row payload before the mode switch
        // so onboarding INTAKE can render the Foundation header while resuming.
        // Mirrors the same snapshot done in ActionRouter.start_intake for the
        // fresh-path entry.
        const inheritedLeftBlock = this._discoveryMode()?.getCurrentLeftBlock?.() || null;
        const inheritedPathContext = this._discoveryMode()?.getPathContextSnapshot?.() || null;

        await this.switchMode('INTAKE', {
          entryType: sessionData.entryType || 'onboarding',
          returnTo: 'DISCOVERY',
          pathId,
          resumeSessionId: sessionData.resumeSessionId,
          inheritedLeftBlock,
          inheritedPathContext
        });
        await this.handleUserMessage('[resume]', {
          metadata: {
            source: 'auto_resume',
            silent: true,
            intakeAction: 'resume'
          }
        });
      } catch (err) {
        console.error('[ModeOrchestrator] Intake resume failed:', err);
      }
      return;
    }

    // Fresh path: render the screening → intake transition copy + "Let's begin" button.
    const hadWelcomeSignal = !!this._welcomeSignalData;
    this._welcomeSignalData = null; // consumed

    let opener;
    if (hadWelcomeSignal) {
      const firstName = window.app?.currentUser?.firstName || 'there';
      opener = `Welcome back, ${firstName}.\n\nLast time you completed the **About Your Relationship** questionnaire, which gave me a helpful snapshot of where things stand.`;
    } else {
      opener = "Thanks. That gives me a good sense of where things stand.";
    }

    const transitionMessage = [
      opener,
      "",
      "Next, let's talk about what's on your mind right now — the specific issue or concern that brought you here. It gives our work together a starting point.",
      "",
      "And it's just that — a starting point. There will be plenty of time to explore other topics later. For now, let's focus on what feels most pressing."
    ].join('\n');

    // Stream the transition message block-by-block via the programmatic streaming
    // helper. This is hardcoded copy — no LLM call, no SSE round-trip — but it
    // still feels like the agent is typing because each markdown block is fed
    // to the renderer on a timer.
    this.chatManager.activateThinkingIndicator();
    await new Promise(resolve => setTimeout(resolve, 300));
    await this.chatManager.streamProgrammaticMessage(transitionMessage, {
      chunkDelay: 400,
      messageType: 'system'
    });

    // Render the "Let's begin" / "Skip for now" buttons. start_intake opens
    // the intake conversation; skip_intake bypasses it permanently by
    // advancing the Foundation prerequisite chain past intake (see
    // ActionRouter.js skip_intake handler). Both dispatched via ActionRouter
    // which carries the session params in data.
    if (window.actionButtons) {
      const buttonGroup = window.actionButtons.createButtonGroup([
        {
          label: "Let's begin",
          action: 'start_intake',
          metadata: { recommended: true },
          data: {
            entryType: sessionData.entryType || 'onboarding',
            pathId,
            resumeSessionId: sessionData.resumeSessionId || null
          }
        },
        {
          label: 'Skip for now',
          action: 'skip_intake',
          data: { pathId }
        }
      ], { groupId: 'intake-launch' });

      this.chatManager.messageList.addInteractiveComponent(buttonGroup, {
        type: 'action-buttons',
        renderLocation: 'input',
        allowTextEntry: true,
        data: { groupId: 'intake-launch' }
      });
    }
  }

  /**
   * Handle L2 re-serve intermediary — Haiku welcome-back after abandoned L2.
   * Shows a warm message referencing the section topic, then Go Deeper / Not Right Now buttons.
   *
   * @private
   * @param {string} pathId - Path ID
   * @param {Object} intermediaryData - Orchestrator intermediary data with isL2ReServe flag
   */
  async _handleL2ReServeIntermediary(pathId, intermediaryData) {
    const { DiscoveryAPI } = await import('../core/discoveryApi.js');
    const discoveryAPI = new DiscoveryAPI();

    // Build intent context with questionnaire name, section description, and path context
    const hasWelcome = !!this._welcomeSignalData;
    const intentContext = {
      questionnaireName: intermediaryData.questionnaireName || null,
      sectionDescription: intermediaryData.sectionDescription || null,
      subcategoryName: intermediaryData.subcategory?.name || null,
      welcomeBack: hasWelcome,
      pathName: hasWelcome ? (this._welcomeSignalData.recommendedPath?.name || null) : null,
      pathDescription: hasWelcome ? (this._welcomeSignalData.recommendedPath?.description || null) : null,
      partnerName: hasWelcome ? (this._welcomeSignalData.partnerName || null) : null
    };
    this._welcomeSignalData = null; // consumed

    const contentPlan = [{
      type: 'llm',
      intent: 'l2_reserve_intermediary',
      intentContext
    }];

    // Stream Haiku message
    if (!window.responseHandler) {
      throw new Error('ResponseHandler not initialized');
    }
    // REVERT: waiting-phase arming site — see frontend-user/docs/thinking-indicator-revert.md
    // Content-plan pause: the only chat output from here is handleResponse (SSE).
    this.chatManager.activateThinkingIndicator(undefined, { waiting: true });
    const sessionId = this._getSessionId();
    try {
      const job = await discoveryAPI.queueContentPlan(sessionId, contentPlan);
      const streamResult = await window.responseHandler.handleResponse(job);
      if (this._discoveryMode()) {
        this._discoveryMode()._lastRenderedMessage = streamResult?.response || null;
      }
    } catch (err) {
      // A queue/stream failure must not strand a spinning logo (live RAF clock).
      this.chatManager.deactivateThinkingIndicator();
      throw err;
    }

    // Render same Go Deeper / Not Right Now buttons as normal intermediary
    const group = window.buttonGroupRegistry?.get('discovery-intermediary');
    if (group && window.actionButtons) {
      const buttonGroup = window.actionButtons.createButtonGroup(group.buttons, { groupId: 'discovery-intermediary' });
      this.chatManager.messageList.addInteractiveComponent(buttonGroup, {
        type: 'action-buttons',
        data: { groupId: 'discovery-intermediary' }
      });
    }
  }

  /**
   * Handle path completion — LLM-generated acknowledgment, decision guidance, and action buttons.
   * Follows the same content-plan streaming pattern as _handleIntermediary.
   *
   * @private
   * @param {string} pathId - Path ID
   * @param {Object} completeData - Orchestrator complete data with summary
   * @param {DiscoveryAPI} discoveryAPI - API instance
   */
  async _handlePathComplete(pathId, completeData, discoveryAPI) {
    // In guided mode, path completion is handled by the guided loop.
    // The orchestrator returns 'complete' for a path that was being walked in guided mode
    // (all its sections finished). Just return — the guided loop will call ranking engine.
    if (this._isGuidedMode) {
      // Renders nothing on this branch, and its caller returns straight out of
      // the orchestrator loop, so no finish() follows to hide the indicator.
      this.chatManager?.deactivateThinkingIndicator?.();
      // Nothing renders here, so a pending L2/L3 payload has no bubble to ride.
      // Drop it rather than let it surface against an unrelated later intro.
      this._l2Understanding = null;
      this._l3Understanding = null;
      this._l2SectionName = null;
      return;
    }

    const mode = this._discoveryMode();
    const pathMetadata = mode?.currentPathMetadata || {};
    const summary = completeData.summary || {};
    const guideCompletion = completeData.guideCompletion || null;
    const primaryGuide = guideCompletion?.guides?.find(guide => guide.status !== 'locked')
      || guideCompletion?.guides?.[0]
      || null;

    // The card producer is the SINGLE guide-announcement predicate: the message
    // (prompt `guide` field / Foundation pre-artifact sentence) may mention a
    // guide only when this returned a block, so message and card cannot disagree.
    // The backing path's card artwork rides along for the tile.
    const guideBlockText = buildGuideAnnouncementBlock(primaryGuide, {
      image: completeData.pathMetadata?.cardContent?.image || null
    });
    const announcedGuide = guideBlockText
      ? { title: primaryGuide.title, isReady: primaryGuide.status === 'ready' }
      : null;

    // Whether a sharing invitation is owed is an ENVELOPE field
    // (`completeData.sharingInvitation`), computed server-side at completion —
    // it replaced a client-side `getSharingStatus()` race that gated the
    // completion message's own copy on a network read. The invitation is now a
    // second beat that lands after the reward, so nothing about the message,
    // the guide card, the options menu or the completion buttons waits on it.
    const sharingInvitation = completeData.sharingInvitation || null;

    // 1. Show thinking state
    this._discoveryMode()?.setThinkingStage('completing');
    this.chatManager.activateThinkingIndicator();

    // 2. entryPath comes from the orchestrator, which is the only side that can
    //    tell a fresh completion (`after_l2`/`after_decline`) from a re-entry
    //    into an already-finished path (`after_return`). Deriving it here from
    //    `completionTrigger` is what made every first completion read as a
    //    returning-user greeting.
    const entryPath = completeData.entryPath || 'after_l2';

    // 3. Determine scenario via ranking engine (single authority for "what's next").
    //    Mirrors _handleTopicComplete: anything but `journeyComplete` — including
    //    an `l3_session` handoff — means there is somewhere to go next.
    let scenario = 'journey_complete';
    let nextTopicLabel = null;
    let nextTopicDescription = null;
    // Assigned in the generic branch (before the content plan), read again at
    // the button render below it — the else block's scope doesn't reach there.
    let resolvedButtons = null;
    let optionsText = null;
    /** @type {Object|null} Catalog entry the completion group mounted from (its `allowTextEntry`/`textEntryLabel` are mirrored onto the invitation beat). */
    let completionGroup = null;

    try {
      const nextResult = await discoveryAPI.getNextTopic();
      if (nextResult?.data && !nextResult.data.journeyComplete) {
        scenario = 'next_available';
        nextTopicLabel = nextResult.data.label || null;
        nextTopicDescription = nextResult.data.description || null;
      }
    } catch (err) {
      if (err.status !== 404) {
        console.error('[ModeOrchestrator] Error fetching next topic:', err);
      }
    }

    // 5. Build flat intentContext with rich signals
    const showConfetti = entryPath !== 'after_decline';

    // Capture closing text for the reflection paragraph before clearing L2 state.
    const closingText = this._l2ClosingText || null;
    this._l2ClosingText = null;

    // The final section's memory-update card belongs to THIS moment — render it
    // before the completion message, the same beat the user gets when a section
    // closes mid-path. Runs ahead of the branch because both completion paths
    // (programmatic Foundation, Haiku everything-else) owe the user that card.
    // On the Haiku branch this delays queueContentPlan (and its confetti) by the
    // card's duration; that ordering is the point of the sequence.
    const understandingShown = await this._streamPendingUnderstanding();

    // Foundation uses a hardcoded completion message (exact copy control,
    // zero Haiku latency). All other paths fall through to the Haiku content plan.
    // TODO: when additional paths migrate to programmatic completions, convert
    // this branch into a resolver registry (e.g. `completionResolvers[pathId]`)
    // and rename foundationCompletion.js → pathCompletionMessages.js.
    if (pathId === 'foundation') {
      // Confetti is intentionally disabled for the Foundation completion flow —
      // the new message is more reflective than celebratory. Uncomment to re-enable.
      // if (showConfetti) {
      //   this.actionRouter.executeAction('trigger_confetti', {}).catch(err =>
      //     console.error('[ModeOrchestrator] Confetti trigger failed:', err)
      //   );
      // }

      this._discoveryMode()?.setCompleteStage({ pathComplete: true });
      const firstName = window.app?.currentUser?.firstName || 'there';

      // Whether a partner is linked drives the one conditional bullet+button
      // pair (see resolveFoundationCompletionPostArtifact). Read from the
      // session user rather than re-fetching: a link established mid-path would
      // already have refreshed currentUser, and a stale miss costs only a
      // suppressed nudge the dashboard row will repeat.
      const isCoupled = Boolean(window.app?.currentUser?.coupleId);

      // Brief perceptual delay so the thinking indicator (set at the top of
      // this function) is visible before the message starts streaming.
      // (Skipped when the understanding card already held the beat.)
      if (!understandingShown) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      try {
        // Single bubble, sibling text blocks. BlockStreamBuffer splits on
        // \n\n — each block fades in in sequence with uniform
        // `.message__block` spacing.
        //
        // The guide announcement rides between the congratulation and the menu,
        // and drops out entirely when no guide is owed. `primaryGuide` is
        // resolved above from the completion envelope, so this costs no extra
        // fetch. Same card every completion surface renders — see
        // components/guideAnnouncementCard.js.
        const preText = resolveFoundationCompletionPreArtifact({ firstName, guide: announcedGuide });
        const guideText = guideBlockText;
        const postText = resolveFoundationCompletionPostArtifact({ isCoupled });
        const fullMessage = [preText, guideText, postText].filter(Boolean).join('\n\n');
        await this.chatManager.streamProgrammaticMessage(fullMessage, {
          chunkDelay: 400,
          messageType: 'system'
        });

        if (this._discoveryMode()) {
          this._discoveryMode()._lastRenderedMessage = fullMessage;
        }
      } catch (err) {
        console.error('[ModeOrchestrator] Foundation completion stream failed:', err);
        this.chatManager.displayAssistantMessage(
          `You've completed the ${summary.pathName || 'Starting Your Journey'} path! Great work.`,
          { messageType: 'system' }
        );
      }

      // 4. Render Foundation-specific action buttons and short-circuit the
      //    shared scenario-based button render below (which targets
      //    discovery-complete-next / discovery-complete-done for other paths).
      const foundationGroup = window.buttonGroupRegistry?.get('foundation-complete');
      let foundationButtons = null;
      if (foundationGroup && window.actionButtons) {
        // Splices live beside the bullet resolver so the 1:1 mapping cannot
        // drift; the helper clones before mutating.
        foundationButtons = resolveFoundationCompletionButtons({
          catalogButtons: foundationGroup.buttons,
          isCoupled
        });
        const buttonGroup = window.actionButtons.createButtonGroup(foundationButtons, { groupId: 'foundation-complete' });
        this.chatManager.messageList.addInteractiveComponent(buttonGroup, {
          type: 'action-buttons',
          data: { groupId: 'foundation-complete' }
        });
      }

      this.eventBus.emit('message:complete');
      this._clearDiscoveryScopeState();

      // Foundation can never actually carry an invitation — `foundation-2`
      // unlocks only once `foundation` is complete, and the envelope's gate
      // requires BOTH paths — so this call early-returns on the null
      // invitation and the buttons mounted above stand as they always have.
      // It is here because the entry point is shared: a theoretical re-walk
      // must not reach a deleted method.
      //
      // Runs AFTER `_clearDiscoveryScopeState()`, which calls
      // `clearSharingDecisionPhase()` — assigning the phase before that clear
      // would null it and strand the user with no forward action.
      await this._runSharingDecisionPhase({
        invitation: sharingInvitation,
        pathId,
        optionsText: null,
        buttons: foundationButtons,
        groupId: 'foundation-complete',
        groupData: { groupId: 'foundation-complete' },
        partnerName: pathMetadata.partnerName || null,
        sourceGroup: foundationGroup,
        discoveryAPI
      });
      return;
    } else {
      // Buttons and options menu resolve BEFORE the content plan: the menu's
      // ≥2 predicate feeds `optionsFollow`, which the prompt needs to know
      // whether a menu follows the message. Fail-soft — a missing registry or
      // group degrades to the prose completion (today's behavior), never a
      // throw that aborts the message; the render below skips with it.
      completionGroup = window.buttonGroupRegistry?.get(
        scenario === 'next_available' ? 'discovery-complete-next' : 'discovery-complete-done'
      );
      if (Array.isArray(completionGroup?.buttons) && window.actionButtons) {
        resolvedButtons = resolvePathCompletionButtons({
          catalogButtons: completionGroup.buttons,
          nextTopicLabel: scenario === 'next_available' ? nextTopicLabel : null,
          betaGateActive: completeData.betaPathGate === true
        });
        optionsText = resolvePathCompletionOptionsList({
          resolvedButtons,
          nextTopicDescription
        });
      }

      const plan = [
        // Confetti fires immediately while Haiku generates — animation overlaps with message appearance
        ...(showConfetti ? [{ type: 'action', action: 'trigger_confetti' }] : []),
        {
          type: 'llm',
          intent: 'topic_complete',
          intentContext: {
            topicLabel: summary.pathName || pathMetadata.name || 'this area',
            engagedSections: summary.engagedSections || [],
            permanentlyDeclinedSections: summary.permanentlyDeclinedSections || [],
            hadL2Conversations: (summary.tier2Sections || []).length > 0,
            closingText,
            entryPath,
            scenario,
            nextTopicLabel,
            partnerName: pathMetadata.partnerName || null,
            // Present iff the guide card below will render (same producer
            // predicate) — the prompt writes its GUIDE SENTENCE only then.
            guide: announcedGuide,
            // True iff the options-menu bubble below will render (same
            // resolver output) — the prompt drops its forward pitch only then.
            optionsFollow: optionsText !== null
          }
        }
      ];

      const sessionId = this._getSessionId();
      const job = await discoveryAPI.queueContentPlan(sessionId, plan);

      // Update header to complete state
      this._discoveryMode()?.setCompleteStage({ pathComplete: true });

      if (!window.responseHandler) {
        throw new Error('ResponseHandler not initialized');
      }

      try {
        const streamResult = await window.responseHandler.handleResponse(job);
        if (this._discoveryMode()) {
          this._discoveryMode()._lastRenderedMessage = streamResult?.response || null;
        }
      } catch (err) {
        console.error('[ModeOrchestrator] Path complete message failed:', err);
        this.chatManager.displayAssistantMessage(
          `You've completed the ${summary.pathName || 'path'}! Great work exploring these topics together.`,
          { messageType: 'system' }
        );
      }
    }

    // The same guide announcement card the Foundation branch renders. It rides
    // as its own bubble here because Haiku owns the message above it — we
    // cannot splice a block into a stream we do not author. `displayAssistantMessage`
    // routes through MessageRenderer → BlockStreamBuffer, so the tag renders as
    // the card rather than as literal text. (The message itself already
    // introduced the guide via intentContext.guide — same predicate.)
    if (guideBlockText) {
      this.chatManager.displayAssistantMessage(guideBlockText, { messageType: 'system' });
    }

    // 4. The options menu and the scenario-specific action buttons together
    //    are PHASE 3. With a sharing invitation owed they are deferred whole,
    //    so phase 2's decision stands alone with nothing competing for the
    //    input region; `_runSharingDecisionPhase` carries them and mounts them
    //    the moment that decision resolves (approved, abandoned, or declined).
    //    With no invitation this is today's beat, byte for byte.
    //
    //    `resolvedButtons` / `optionsText` were resolved BEFORE the content
    //    plan (the menu's >=2 predicate feeds `optionsFollow`) and are read
    //    here unchanged — deferring the render changes nothing the prompt saw.
    //    The resolver owns every per-user transform (topic relabel, beta
    //    dead-end swap) — see foundationCompletion.js.
    const completionGroupId = scenario === 'next_available'
      ? 'discovery-complete-next'
      : 'discovery-complete-done';
    if (!sharingInvitation) {
      // Options menu — the same bullets-from-buttons block the Foundation
      // branch bakes into its post-artifact text. Non-null exactly when the
      // message was told `optionsFollow: true`, so prose and menu can never
      // disagree.
      if (optionsText) {
        this.chatManager.displayAssistantMessage(optionsText, { messageType: 'system' });
      }
      if (resolvedButtons && window.actionButtons) {
        const buttonGroup = window.actionButtons.createButtonGroup(resolvedButtons, { groupId: completionGroupId });
        this.chatManager.messageList.addInteractiveComponent(buttonGroup, {
          type: 'action-buttons',
          data: { groupId: completionGroupId, scenario }
        });
      }
    }

    // No guide button: a generating guide has nothing to open, and a ready one
    // is opened by the card itself. (The old unconditional button rendered even
    // for a locked guide, offering an empty Guides tab with no explanation.)

    this.eventBus.emit('message:complete');
    this._clearDiscoveryScopeState();

    // ORDERING IS LOAD-BEARING: `_clearDiscoveryScopeState()` calls
    // `clearSharingDecisionPhase()`, and `_runSharingDecisionPhase` assigns
    // `this._completionPhase` as its first statement. Starting the phase
    // before the clear nulls the deferred phase-3 payload and leaves the user
    // with a resolved decision and no forward action at all.
    await this._runSharingDecisionPhase({
      invitation: sharingInvitation,
      pathId,
      optionsText,
      buttons: resolvedButtons,
      groupId: completionGroupId,
      groupData: { groupId: completionGroupId, scenario },
      partnerName: pathMetadata.partnerName || null,
      sourceGroup: completionGroup,
      discoveryAPI
    });
  }

  /**
   * PHASE 2 of the completion ceremony: the partner-sharing decision, alone.
   *
   * One short message, the Partner Sharing card, and exactly two input
   * buttons — `[Review what {partner} sees] [Not now]`. Nothing else is
   * mounted beside it: phase 3 (the options menu + the completion buttons) is
   * carried in `_completionPhase` and mounted by
   * {@link ModeOrchestrator#mountCompletionPhase} once this decision resolves.
   * The beat behaves exactly like a discovery questionnaire offer — an input
   * group, a clickable transcript card, and a resolution that hands the input
   * region back.
   *
   * THE FAILURE THIS METHOD IS BUILT AROUND is leaving a user who just
   * finished a path with no forward action. `_runOrchestratorLoop` has no
   * try/catch around `_handlePathComplete`, so the `finally` here is the
   * convergence guarantee: unless the phase-2 row actually mounted (or this
   * run was superseded, in which case another context owns the screen),
   * phase 3 mounts before this returns.
   *
   * @private
   * @param {Object} params
   * @param {{groupId?: string, state?: string, count?: number}|null} params.invitation -
   *   The completion envelope's `sharingInvitation`; null ⇒ no phase 2 at all,
   *   and phase 3 has already been rendered inline by the caller.
   * @param {string} params.pathId - Completing path; `foundation-2` is the
   *   first-offer discriminator for the copy.
   * @param {string|null} params.optionsText - Phase 3's options-menu bubble.
   * @param {Array<Object>|null} params.buttons - Phase 3's resolved button array.
   * @param {string} params.groupId - Phase 3's button-group id.
   * @param {Object} [params.groupData] - Phase 3's `addInteractiveComponent` data.
   * @param {string|null} [params.partnerName]
   * @param {Object|null} [params.sourceGroup] - Phase 3's catalog entry; its
   *   `allowTextEntry`/`textEntryLabel` are mirrored onto both rows.
   * @param {DiscoveryAPI} params.discoveryAPI - The instance already in scope
   *   at the call site (deliberately passed, not statically imported).
   * @returns {Promise<boolean>} Whether the phase-2 button row mounted.
   */
  async _runSharingDecisionPhase({
    invitation,
    pathId,
    optionsText = null,
    buttons = null,
    groupId,
    groupData = null,
    partnerName = null,
    sourceGroup = null,
    discoveryAPI
  }) {
    // The Foundation branch's real path (and any completion owing no consent):
    // the caller has ALREADY rendered its options menu and completion buttons
    // inline, so returning before `_completionPhase` is assigned is what keeps
    // this from double-mounting them.
    if (!invitation) return false;

    // Assigned FIRST so every exit below — including the `finally` — has a
    // phase 3 to converge on.
    this._completionPhase = {
      optionsText,
      buttons,
      groupId,
      groupData: groupData || { groupId },
      allowTextEntry: sourceGroup?.allowTextEntry === true,
      textEntryLabel: sourceGroup?.textEntryLabel ?? null,
      partnerName
    };
    this._sharingCeremonyGroupId = invitation.groupId || 'gestalt';

    // Identity for THIS run, re-checked after every await. Every teardown
    // bumps it (see `clearSharingDecisionPhase`).
    // `|| 0` rather than a bare `++`: the field is nominally constructor-set,
    // but a NaN token would make every post-await check read as superseded and
    // silently abandon the beat.
    this._sharingPhaseToken = (this._sharingPhaseToken || 0) + 1;
    const token = this._sharingPhaseToken;
    const sessionAtStart = sessionManager.getCurrentSessionId?.() ?? null;
    const superseded = () => this._sharingPhaseToken !== token
      || (sessionManager.getCurrentSessionId?.() ?? null) !== sessionAtStart;

    let mounted = false;
    let abandoned = false;
    try {
      // Every prerequisite is checked BEFORE anything streams, so a missing
      // host converges to phase 3 without leaving a half-rendered beat.
      if (!window.actionButtons || !this.chatManager?.messageList) {
        console.error('[ModeOrchestrator] Sharing decision phase: no action-button host — skipping to the completion actions');
        return false;
      }

      // LIVE readiness check (D1): if the registers are not ready RIGHT NOW,
      // skip phase 2 entirely rather than offer a decision the launcher would
      // refuse. Raced against a timeout because `DiscoveryAPI` uses raw fetch
      // with no AbortController — a hung request would otherwise block
      // convergence forever.
      let view = null;
      try {
        let timer = null;
        const res = await Promise.race([
          discoveryAPI.getSharingStatus(),
          new Promise((_, reject) => {
            timer = setTimeout(() => reject(new Error('sharing-status timed out')), SHARING_READINESS_TIMEOUT_MS);
          })
        ]).finally(() => clearTimeout(timer));
        view = res?.data || null;
      } catch (err) {
        // Silent to the user, per D1: no notice panel, no "yes → sorry, not
        // ready" beat. The dashboard nudge is the standing carrier.
        console.error('[ModeOrchestrator] Sharing readiness check failed — skipping the decision beat:', err);
        return false;
      }
      if (superseded()) { abandoned = true; return false; }

      const ledger = ledgerFromStatus(view);
      const scopeId = ledger && ledger.groups.some(g => g.id === this._sharingCeremonyGroupId)
        ? this._sharingCeremonyGroupId
        : gestaltGroupId(ledger?.groups || []);
      if (!ledger || NOT_ACTIONABLE_STATES.has(view?.state) || !queueScope(ledger.units, scopeId).length) {
        console.error('[ModeOrchestrator] Sharing registers are not actionable — skipping the decision beat:', view?.state ?? 'no ledger');
        return false;
      }

      // The LIVE count is display authority; the envelope's is a snapshot from
      // completion time and may already be stale.
      const count = needsReviewCount(ledger.units);
      const who = partnerName || view?.partnerName || null;

      const cardText = buildSharingInvitationCardBlock(count);
      const message = [
        resolveSharingInvitationMessage({ partnerName: who, firstOffer: pathId === 'foundation-2' }),
        cardText
      ].filter(Boolean).join('\n\n');

      const streamed = await this.chatManager.streamProgrammaticMessage(message, {
        chunkDelay: 400,
        messageType: 'system'
      });
      if (superseded()) { abandoned = true; return false; }
      const messageEl = streamed?.element || null;

      // Scoped to THIS message's own element, not a global query — a
      // long-lived transcript can hold more than one invitation card across
      // completions, and only this run's card may flip state later.
      this._sharingInvitationCardEl = cardText
        ? messageEl?.querySelector?.('.artifact--sharing-invitation') || null
        : null;

      mounted = this._showSharingDecisionButtons({ resumed: false });
      return mounted;
    } catch (err) {
      console.error('[ModeOrchestrator] Sharing decision phase failed:', err);
      return false;
    } finally {
      // THE convergence guarantee. Skipped only when this run was superseded —
      // a teardown or a newer completion owns the screen, and mounting phase 3
      // would push a dead path's buttons into the live chat.
      //
      // Wrapped in its own try/catch because `_handlePathComplete` is awaited
      // with NO try/catch in `_runOrchestratorLoop`: a throw escaping HERE is
      // the one remaining way a user could finish a path and be left with no
      // forward action. `mountCompletionPhase` does synchronous DOM work
      // (`createButtonGroup`, `addInteractiveComponent`), so its awaited half
      // being guarded is not sufficient on its own.
      if (!mounted && !abandoned) {
        try {
          await this.mountCompletionPhase();
        } catch (err) {
          console.error('[ModeOrchestrator] Completion-phase convergence failed:', err);
        }
      }
    }
  }

  /**
   * Mount the phase-2 binary row in the chat INPUT region — the sharing
   * decision's only two answers. Modelled on
   * {@link ModeOrchestrator#_showQuestionnaireInputButtons}: recommended
   * affirmative + `action-btn--skip` decline, `renderLocation: 'input'`, a
   * handle kept for programmatic teardown.
   *
   * `resumed` is the questionnaire launcher's paused/resume distinction: the
   * user opened the queue and came back without submitting, so the affirmative
   * offers a restart rather than a first look. (We say `unfinished`, not
   * `paused`, because no partial progress is kept — see
   * {@link ModeOrchestrator#resolveSharingDecision}.)
   *
   * `allowTextEntry`/`textEntryLabel` mirror the deferred completion group, so
   * the generic branch keeps its typed-reply fallback and Foundation stays
   * modal.
   *
   * @private
   * @param {Object} [opts]
   * The affirmative is deliberately NOT partner-named ("Choose what to share",
   * not "Choose what {partner} sees"): the message and the card directly above
   * both name them, and the label completes the sentence the message opened —
   * "you can choose to share some of it with {partner}".
   *
   * @param {Object} [opts]
   * @param {boolean} [opts.resumed=false] - true → "Begin again", for a queue
   *   the user opened and left without submitting.
   * @returns {boolean} Whether the row mounted IN THE INPUT REGION.
   */
  _showSharingDecisionButtons({ resumed = false } = {}) {
    if (!window.actionButtons || !this.chatManager?.messageList) return false;
    const buttons = [
      {
        id: 'sharing-invitation-open',
        label: resumed ? 'Begin again' : 'Choose what to share',
        action: 'open_sharing_ceremony',
        metadata: { recommended: true }
      },
      {
        id: 'sharing-invitation-later',
        label: 'Not now',
        action: 'dismiss_sharing_invitation',
        className: 'action-btn--skip'
      }
    ];
    const group = window.actionButtons.createButtonGroup(buttons, { groupId: 'sharing-invitation' });
    const handle = this.chatManager.messageList.addInteractiveComponent(group, {
      type: 'action-buttons',
      renderLocation: 'input',
      allowTextEntry: this._completionPhase?.allowTextEntry === true,
      textEntryLabel: this._completionPhase?.textEntryLabel ?? null,
      data: { groupId: 'sharing-invitation' }
    });

    // `addInteractiveComponent` falls back to the TRANSCRIPT when the input
    // host is unavailable, returning a bare container with no `.hide()`. That
    // row can never be retired programmatically, so a teardown would strand it
    // on screen — treat the fallback as "did not mount" and let the caller's
    // `finally` converge to phase 3 instead.
    if (typeof handle?.hide !== 'function') {
      console.error('[ModeOrchestrator] Sharing decision row could not mount in the input region');
      this._sharingDecisionButtonsHandle = null;
      return false;
    }

    this._sharingDecisionButtonsHandle = handle;
    this._sharingDecisionOpen = true;
    return true;
  }

  /**
   * PHASE 3 — "what's next": the deferred options-menu bubble and the
   * completion button group. The SINGLE convergence point every phase-2
   * resolution (and every phase-2 failure) routes through.
   *
   * Two ordering rules, both load-bearing:
   *   - **Buttons first, acknowledgement second.** The acknowledgement is a
   *     cosmetic stream and `streamProgrammaticMessage` can reject; streaming
   *     first would strand the user behind a failed message. The buttons live
   *     in the input region, so mounting them first does not disturb the
   *     transcript order the user reads (acknowledgement, then options menu).
   *   - **`_completionPhase` is NOT consumed here.** Input groups mount with
   *     `replaceChildren`, so re-mounting is idempotent; one-shot clearing
   *     creates a real strand ("Not now" → phase 3 → the user clicks the
   *     still-live card → `ActionRouter` tears the group down → the resolution
   *     finds a null phase → no input buttons, ever).
   *
   * @param {Object} [params]
   * @param {string|null} [params.acknowledgement] - Approve-success only.
   * @returns {Promise<boolean>} Whether phase 3 mounted.
   */
  async mountCompletionPhase({ acknowledgement = null } = {}) {
    const phase = this._completionPhase;
    if (!phase) return false;
    if (!window.actionButtons || !this.chatManager?.messageList) {
      console.error('[ModeOrchestrator] Cannot mount the completion actions: no action-button host');
      return false;
    }

    if (Array.isArray(phase.buttons) && phase.buttons.length) {
      const buttonGroup = window.actionButtons.createButtonGroup(phase.buttons, { groupId: phase.groupId });
      this.chatManager.messageList.addInteractiveComponent(buttonGroup, {
        type: 'action-buttons',
        data: phase.groupData,
        allowTextEntry: phase.allowTextEntry,
        textEntryLabel: phase.textEntryLabel
      });
    }

    if (acknowledgement) {
      try {
        await this.chatManager.streamProgrammaticMessage(acknowledgement, {
          chunkDelay: 400,
          messageType: 'system'
        });
      } catch (err) {
        // The buttons are already up; a failed acknowledgement costs a line of
        // copy, never the forward action.
        console.error('[ModeOrchestrator] Sharing acknowledgement stream failed:', err);
      }
    }

    if (phase.optionsText) {
      this.chatManager.displayAssistantMessage(phase.optionsText, { messageType: 'system' });
    }
    // The decision is settled: the user has their forward actions, so no exit
    // may re-offer it.
    this._sharingDecisionOpen = false;
    return true;
  }

  /**
   * Resolve the phase-2 decision — the ONE place the approve/abandon branch
   * lives, so `ActionRouter`'s `onResolved` callback calls this and nothing
   * else.
   *
   *   - succeeded → the card completes, the settle choreography runs, and
   *     phase 3 opens with an acknowledgement.
   *   - !succeeded → the panel closed with nothing submitted: the card reverts
   *     to `unfinished` ("Not finished — begin again"), the abandon message
   *     says so, and the binary row is re-offered. **Phase 3 deliberately does
   *     NOT mount** — the decision is still live, exactly like a paused
   *     questionnaire. "Not now" remains the only way past it.
   *
   * `unfinished` rather than the questionnaire's `paused` because no partial
   * progress is kept (D4): "paused" would imply a resumable position the
   * sharing queue does not have.
   *
   * @param {Object} [params]
   * @param {boolean} [params.succeeded=false] - True only for a real submit success.
   * @param {boolean} [params.bothConfirmed=false] - Whether the partner has
   *   already confirmed their own side (drives the acknowledgement's variant —
   *   the false form never over-promises a synthesis that has not been built).
   * @param {Promise<void>|null} [params.panelClosed] - The `waitForPanelClose()`
   *   promise `ActionRouter` armed BEFORE the launch.
   * @returns {Promise<boolean>} Whether the resolution was applied.
   */
  async resolveSharingDecision({ succeeded = false, bothConfirmed = false, panelClosed = null } = {}) {
    // A navigation teardown clears the phase BEFORE closing its panel, and
    // `returnToDashboard`'s bare `right-panel:close` resolves a live ceremony
    // (succeeded: false) mid-navigation. A null phase IS that case: the chat
    // this decision belonged to is gone, so resolve nothing into it.
    if (!this._completionPhase) return false;

    const cardEl = this._sharingInvitationCardEl;

    if (succeeded) {
      cardEl?.__sharingInvitationCard?.setState?.('completed');
      // Terminal: nulled so no later state call can un-complete the card.
      this._sharingInvitationCardEl = null;
      await this.chatManager?.settleArtifact?.(cardEl, panelClosed);
      await this.mountCompletionPhase({
        acknowledgement: resolveSharingAcknowledgement({
          partnerName: this._completionPhase?.partnerName || null,
          bothConfirmed
        })
      });
      return true;
    }

    cardEl?.__sharingInvitationCard?.setState?.('unfinished');
    try {
      await this.chatManager?.streamProgrammaticMessage?.(resolveSharingAbandonMessage(), {
        chunkDelay: 400,
        messageType: 'system'
      });
    } catch (err) {
      // Cosmetic; the re-offer below is the forward action and must still land.
      console.error('[ModeOrchestrator] Sharing abandon message failed:', err);
    }
    this._showSharingDecisionButtons({ resumed: true });
    return true;
  }

  /**
   * Close and release every panel chat owns itself.
   *
   * Chat is not a PageManager section, so nothing tears its panels down on its
   * own — this is the one cleanup all three chat exits (`switchMode`,
   * `returnToDashboard`, `clearModeDisplay`) route through. Teardown also
   * invalidates a ceremony launch still waiting on its status read, so a late
   * response cannot open a panel after the exit.
   *
   * @private
   * @returns {boolean} Whether anything was torn down.
   */
  _teardownChatOwnedPanels() {
    return destroySharingCeremony();
  }

  /**
   * Drop the deferred phase 3, the consent-group id, AND the card element ref
   * without mounting anything — the NAVIGATION-side clear, and the ONLY place
   * `_completionPhase` is cleared.
   *
   * Called from every chat teardown — BEFORE its panel close — so a phase can
   * never leak into a later session, a teardown-driven close can never mount
   * into a chat the user has left, and a resolution callback firing during the
   * teardown finds a null phase (and a null card ref) and no-ops.
   *
   * Bumps `_sharingPhaseToken` last: a phase-2 run still awaiting its
   * readiness read or its message stream re-checks the token after every await
   * and aborts without mounting anything.
   */
  clearSharingDecisionPhase() {
    this._completionPhase = null;
    this._sharingCeremonyGroupId = null;
    this._sharingInvitationCardEl = null;
    this._sharingDecisionButtonsHandle?.hide?.();
    this._sharingDecisionButtonsHandle = null;
    this._sharingDecisionOpen = false;
    this._sharingPhaseToken = (this._sharingPhaseToken || 0) + 1;
  }

  /**
   * Drive this run's `<sharing-invitation-card>` through a state it does not
   * own itself. The one live caller is `ActionRouter`'s `open_sharing_ceremony`
   * ('open' — dimmed, click target revoked, while the queue panel is up); the
   * 'completed' / 'unfinished' flips belong to
   * {@link ModeOrchestrator#resolveSharingDecision}, and `clearModeDisplay`
   * drives its own conditional revert. Keeps the ref; a no-op whenever no card
   * is held.
   *
   * @param {'pending'|'open'|'completed'|'unfinished'} next
   */
  setSharingInvitationCardState(next) {
    this._sharingInvitationCardEl?.__sharingInvitationCard?.setState?.(next);
  }

  /**
   * Reset the discovery scope-tracking fields to null. Call after any terminal
   * state (path/topic complete, foundation complete) so the next entry starts
   * with a clean slate.
   *
   * The transient L2/L3 handoff fields are cleared here too: they are set from
   * the next-step response whenever an L2 closes (including when the next step
   * is `complete`), and anything still pending at a terminal state belongs to a
   * scope the user has left. Leaving them set makes the next intro render a
   * stale memory card and a false `conversation_complete` bridge.
   *
   * The deferred completion phase goes with them: it belongs to ONE completion
   * moment, and mounting a previous path's buttons would be worse than
   * mounting none. (Phase 2 is started AFTER this call in both branches — that
   * ordering is what lets THIS completion's phase 3 survive; assigning it
   * first would null it here and strand the user.)
   *
   * @private
   */
  _clearDiscoveryScopeState() {
    this.clearSharingDecisionPhase();
    this._currentDiscoveryPathId = null;
    this._currentDiscoveryScopeId = null;
    this._currentDiscoveryEntryMode = null;
    this._discoveryVisitedTopics = null;
    this._currentIntroTopicId = null;
    this._l2Understanding = null;
    this._l3Understanding = null;
    this._l2SectionName = null;
  }

  /**
   * Start an L2 follow-up session for a section.
   * Creates a new DISCOVERY session with L2 metadata and auto-sends first message.
   *
   * @private
   * @param {string} pathId - Path ID
   * @param {Object} orchestratorData - L2 session data from orchestrator
   */
  async _startL2Session(pathId, orchestratorData) {
    // Capture the intermediary narration that preceded this L2 entry (if any).
    // Passed as session metadata so the L2 agent sees it as its own prior message.
    // Prefer the deterministic L2 summary (opener+closer only) when the
    // intermediary came from a clinical composer, so Sonnet's prior turn
    // is clean prose rather than rendered intermediary XML. Falls back
    // to the LLM-rendered message string for the legacy narration path.
    const dm = this._discoveryMode();
    const intermediaryMessage = dm?._lastIntermediaryL2Summary || dm?._lastRenderedMessage || null;
    if (dm) dm._lastIntermediaryL2Summary = null;

    // Persist scopeId on the session record so orphan/abandoned-L2 cleanup
    // (orphanCleanupService → orchestratorService.onL2SessionCompleted) can
    // resolve the scope when the user never reaches explicit completion.
    const scopeId = orchestratorData.scopeId ?? orchestratorData.topicId ?? pathId;

    await this.switchMode('DISCOVERY', {
      skipWelcome: true,
      force: true,
      pathId,
      scopeId,
      l2SectionCode: orchestratorData.sectionCode,
      schemaContext: orchestratorData.schemaContext,
      organicInsights: orchestratorData.organicInsights,
      priorL2Takeaways: orchestratorData.priorL2Takeaways || [],
      priorScoringOutput: orchestratorData.priorScoringOutput,
      qaPairs: orchestratorData.qaPairs,
      l2Guidance: orchestratorData.l2Guidance,
      frameworkContext: orchestratorData.frameworkContext,
      intermediaryMessage,
      maxTurns: orchestratorData.sessionConfig?.maxTurns || 8
    });

    // Set conversation stage BEFORE sending the message so getRoutingStrategy()
    // returns 'llm' (not 'hybrid'). Also clear leftover intermediary action buttons
    // to prevent the hybrid escalation path from triggering a goDeeper loop.
    // Prime dot state before the stage event fires so the first header render
    // picks up _l2TurnCount=1 and paints the initial ◉ ○ ○ state.
    this._discoveryMode()?.startL2Progress();
    this._discoveryMode()?.setConversationStage(orchestratorData.questionnaireName || orchestratorData.sectionName);
    window.actionButtons?.clearActiveButtons();

    // Auto-send first message to start the L2 conversation
    // silent: true suppresses the user bubble — the blue "Go Deeper" action button is the visible user turn
    await this.handleUserMessage('I\'d like to explore this further', {
      metadata: {
        source: 'l2_entry',
        silent: true,
        pathId,
        // Also write scopeId so the backend orchestrator (keyed by scopeId)
        // can look up state for this L2 session.
        scopeId,
        sectionCode: orchestratorData.sectionCode
      }
    });

    // Descriptor polling happens via stream:complete → DiscoveryMode header strategy

    // Track the last assistant response during L2 for seamless transition
    this._l2ClosingText = null;

    // Validation-loop state — used by the end_discovery handler to intercept
    // Sonnet's close and show Yes/No validation buttons. Reset on every L2
    // start so a fresh session never inherits reopen state from a prior one.
    this._l2ValidationState = {
      reopenCount: 0,
      maxReopens: 4,
      previousTakeaways: null,
      awaitingValidation: false
    };
    const l2StreamHandler = (data) => {
      if (data.response) this._l2ClosingText = data.response;
    };
    this.eventBus.on('stream:complete', l2StreamHandler);
    // Expose the handler so _reopenL2Session can keep it subscribed across cycles
    this._l2StreamHandler = l2StreamHandler;

    // When L2 session ends (via endDiscovery action), report back to orchestrator.
    // l2_session_completed is folded into the loop's first iteration so the same
    // call records the L2 end and returns the next step — including any
    // completion-only fields (summary.understandings) that the path-complete
    // early-return would drop on a second poll.
    this._pendingDiscoveryCallback = async () => {
      this.eventBus.off('stream:complete', l2StreamHandler);
      this._l2StreamHandler = null;

      // Show transition thinking state while next step loads
      this._discoveryMode()?.setThinkingStage('next_assessment');
      this.chatManager.activateThinkingIndicator();

      // Capture L2 session ID before orchestrator loop potentially changes context
      this._l2SessionId = this._getSessionId();

      await this._runOrchestratorLoop(pathId, {
        initialAction: 'l2_session_completed',
        initialActionData: { sessionId: this._l2SessionId }
      });
    };
  }

  /**
   * Start an L3 deep-dive session. L3 sits outside the path orchestrator —
   * it's a post-path cross-topic conversation picked by the backend ranker.
   *
   * The guided/next endpoint returns an `l3_session` payload with the full
   * L3 config inlined (narrative, priorities, coverage gaps, open threads,
   * Q&A pairs, subcategory digest, organic insights). We spread that config
   * onto the DISCOVERY session metadata so the backend DiscoveryStrategy can
   * read it and build the L3 prompt.
   *
   * On completion (via endDiscovery), _pendingDiscoveryCallback — renamed and
   * consolidated as the generic discovery callback — posts to /l3/completed
   * and re-enters the guided loop.
   *
   * @private
   * @param {Object} l3Config - Payload from GET /guided/next (type: 'l3_session')
   */
  async _startL3Session(l3Config) {
    await this.switchMode('DISCOVERY', {
      skipWelcome: true,
      force: true,
      l3SectionCode: l3Config.sectionCode,
      l3SectionName: l3Config.sectionName,
      subcategoryCode: l3Config.subcategoryCode,
      subcategoryName: l3Config.subcategoryName,
      narrativeInsight: l3Config.narrativeInsight,
      explorationPriorities: l3Config.explorationPriorities,
      clinicalNotes: l3Config.clinicalNotes,
      coverageGaps: l3Config.coverageGaps,
      openThreads: l3Config.openThreads,
      qaPairs: l3Config.qaPairs,
      organicInsights: l3Config.organicInsights,
      subcategoryDigest: l3Config.subcategoryDigest,
      l3SessionCount: l3Config.l3SessionCount,
      sessionConfig: l3Config.sessionConfig || { maxTurns: 30 },
      maxTurns: l3Config.sessionConfig?.maxTurns || 30
    });

    // Clear any residual intermediary action buttons so the hybrid escalation
    // path doesn't interpret the deep-dive opener as a button response.
    window.actionButtons?.clearActiveButtons();

    // Auto-send the first message to start the L3 conversation. silent:true
    // suppresses the user bubble — the entry is triggered programmatically,
    // not by the user typing.
    await this.handleUserMessage("I'd like to explore this area more deeply", {
      metadata: {
        source: 'l3_entry',
        silent: true,
        sectionCode: l3Config.sectionCode,
        l3: true
      }
    });

    // L3 does not use the L2 validation loop (no takeaways card). Callback
    // fires directly from endDiscovery.
    this._pendingDiscoveryCallback = async () => {
      this._discoveryMode()?.setThinkingStage('next_assessment');
      this.chatManager.activateThinkingIndicator();

      const sessionId = this._getSessionId();

      try {
        const { DiscoveryAPI } = await import('../core/discoveryApi.js');
        const discoveryAPI = new DiscoveryAPI();
        const l3Result = await discoveryAPI.completeL3Session(l3Config.sectionCode, sessionId);
        // Capture the L3 understanding delta (tier 2 — line bolster) so the next
        // questionnaire intro renders an understanding card, mirroring L2.
        // _request returns the full envelope, so understanding is under .data.
        if (l3Result?.data?.understanding) this._l3Understanding = l3Result.data.understanding;
      } catch (err) {
        console.error('[ModeOrchestrator] L3 completion POST failed:', err);
      }

      // Re-enter the guided loop so the user flows into the next best action
      // (another L3 on a different section or journey complete). The async
      // narrative pipeline for the section we just closed has not written
      // yet, so exclude it from the L3 ranker for one query — otherwise
      // stale fields would re-pick the same section and loop the user.
      const { DiscoveryAPI } = await import('../core/discoveryApi.js');
      const discoveryAPI = new DiscoveryAPI();
      await this._runGuidedLoop(discoveryAPI, {
        continuation: true,
        excludeL3Section: l3Config.sectionCode
      });
    };
  }

  /**
   * Build the L2/L3 memory-update card segment (short intro sentence +
   * <topic-understanding> XML v2 + trailing blank line) and clear
   * `_l2Understanding` so it only renders once. Returns `null` only when there
   * is no payload at all.
   *
   * Shared by the SSE (Haiku) and hardcoded (Foundation) intro paths in
   * `_serveOrchestratorQuestionnaire` so Foundation transitions also get the
   * animated card after L2 validation confirm.
   *
   * @private
   * @returns {string|null}
   */
  _consumeUnderstandingSegment() {
    const u = this._l2Understanding || this._l3Understanding;
    // Consume both so a stale payload can't leak into a later intro.
    this._l2Understanding = null;
    this._l3Understanding = null;
    // The ONLY suppression: no payload at all, which means the section's
    // subcategory maps to no topic. Everything else renders — a no-op close gets
    // the card's "already saved" state, because no session close is visually
    // silent. (A section whose region mapping has drifted also renders now; the
    // card no longer reads region/bucket.)
    if (!u) return null;

    const tier = u.tier ?? 1;
    const changed = u.changed !== false;
    // The L1 flash (tier 0) structurally carries `changed: false` — the tier is
    // persisted before the payload is computed — so it is a real write, not a
    // no-op, and keeps the ordinary intro line.
    const alreadySaved = !changed && tier !== 0;

    const variants = [
      'Let me save what we\'ve learned:',
      'Saving what we\'ve learned to your profile:',
      'Let me update my understanding:',
      'Let me save these insights:'
    ];
    const text = alreadySaved
      ? 'Let me check this against what I have saved:'
      : variants[Math.floor(Math.random() * variants.length)];

    // The card dramatizes its own sweep, so emit the TRUE before value.
    const from = (u.before / 100).toFixed(2);
    const progress = (u.after / 100).toFixed(2);
    // `topics` rides along RAW — this XML is built here and consumed by regex +
    // JSON.parse + textContent, never by an XML/HTML parser, so escaping it (or
    // any other field) would surface literal `&amp;` in the topic labels that
    // contain `&` and would break JSON.parse on quote entities.
    const topics = JSON.stringify(u.topics || []);
    const xml = `<topic-understanding>\n` +
      `<topic>${u.topicId || ''}</topic><tier>${tier}</tier><changed>${changed}</changed>` +
      `<label>${u.topicLabel}</label><from>${from}</from><progress>${progress}</progress>\n` +
      `<topics>${topics}</topics>\n` +
      `</topic-understanding>`;
    return `${text}\n\n${xml}\n\n`;
  }

  /**
   * Render the pending L2/L3 understanding card as its own bubble, then hold
   * the thinking indicator until the card has finished animating.
   *
   * Shared by the two moments a section closes: the next questionnaire intro
   * (`_serveOrchestratorQuestionnaire`) and path completion
   * (`_handlePathComplete`). The card belongs to the section the user JUST
   * finished, so whichever of those comes first must consume it — a payload
   * left pending surfaces one beat late, glued to the following section's intro.
   *
   * The card is decorative: a stream failure is logged and swallowed so the
   * message it precedes still renders (streamProgrammaticMessage re-throws).
   *
   * @private
   * @returns {Promise<boolean>} True when a bubble was shown. A pending payload
   *   suppressed by `_consumeUnderstandingSegment` is still consumed but
   *   returns false — callers use this only to decide whether they still owe
   *   the user a perceptual delay.
   */
  async _streamPendingUnderstanding() {
    const segment = this._consumeUnderstandingSegment();
    if (!segment) return false;

    try {
      // Brief perceptual delay so the thinking indicator is visible before the
      // memory-update card bubble appears.
      await new Promise(resolve => setTimeout(resolve, 300));

      const { animationDuration } = await this.chatManager.streamProgrammaticMessage(segment, {
        chunkDelay: 400,
        messageType: 'system'
      });

      // Re-activate the thinking indicator for the between-bubbles pause so the
      // user sees a clear "processing → what's next" beat. Floored at 1500ms for
      // pacing (e.g. under reduced motion, where the card completes instantly
      // and animationDuration is negligible); otherwise driven by the card's
      // real completion time so the next message never appears while it's still
      // animating.
      this.chatManager.activateThinkingIndicator();
      await new Promise(resolve => setTimeout(resolve, Math.max(1500, animationDuration || 0)));
      return true;
    } catch (err) {
      console.error('[ModeOrchestrator] Understanding card stream failed:', err);
      return false;
    }
  }

  /**
   * Build the topic-complete prelude bubble — renders before the Haiku
   * celebration. Two branches:
   *   - Engaged path: "That wraps up our work on **{topicLabel}** for now.
   *     {variant}:" followed by the animated <topic-understanding> card.
   *     Consumes the pending L2/L3 understanding via _consumeUnderstandingSegment.
   *   - Declined path (no L2 understanding payload, e.g. strike-2 decline
   *     of final section): "That wraps up **{topicLabel}** for now." No card.
   *
   * @private
   * @param {string} topicLabel
   * @returns {string}
   */
  _buildTopicCompletePrelude(topicLabel) {
    const understandingSegment = this._consumeUnderstandingSegment();
    if (understandingSegment) {
      return `That wraps up our work on **${topicLabel}** for now. ${understandingSegment}`;
    }
    return `That wraps up **${topicLabel}** for now.`;
  }

  /**
   * Extract the raw <l2-closing-summary>…</l2-closing-summary> XML block from
   * an L2 closing message. Returns null if the block is missing.
   *
   * @private
   * @param {string|null} closingText - Sonnet's final assistant response
   * @returns {string|null}
   */
  _extractL2ClosingSummary(closingText) {
    if (!closingText) return null;
    const match = closingText.match(/<l2-closing-summary>[\s\S]*?<\/l2-closing-summary>/);
    return match ? match[0] : null;
  }

  /**
   * Reopen the current L2 session for a clarification round.
   * - Removes the validation buttons from the DOM
   * - Increments reopenCount and clears awaitingValidation
   * - PATCHes session metadata so the backend prompt picks up reopen state
   * - Resets _l2ClosingText so the next stream:complete captures fresh takeaways
   *
   * @private
   * @returns {Promise<void>}
   */
  async _reopenL2Session() {
    const state = this._l2ValidationState;
    if (!state) return;

    state.awaitingValidation = false;
    this._discoveryMode()?.setL2DotsFinal(false);
    state.reopenCount += 1;

    // Clear the l2-validation buttons. They render in the chat input
    // (renderLocation:'input'), so hide them there; clearActiveButtons only clears
    // the internal registry. (The previous transcript DOM-removal referenced a
    // non-existent messageList.container and was dead code.)
    window.app?.modules?.input?.hideButtons?.({ groupId: 'l2-validation' });
    window.actionButtons?.clearActiveButtons();

    // PATCH session metadata so the backend prompt builder picks up
    // reopenCount, maxReopens, and the previous takeaways on the next turn.
    // maxReopens is included so the FINAL REOPEN banner fires on the correct
    // cycle even if the cap ever changes mid-session or differs from the
    // builder default.
    const sessionId = this._getSessionId();
    if (sessionId) {
      await API.updateSessionMetadata(sessionId, {
        reopenCount: state.reopenCount,
        maxReopens: state.maxReopens,
        previousTakeaways: state.previousTakeaways
      });
    }

    // Reset closing-text capture for the next round. The stream:complete
    // listener registered in _startL2Session is still subscribed (we do not
    // fire _pendingDiscoveryCallback during a reopen, so its unsubscribe has not
    // run) and will naturally populate _l2ClosingText again.
    this._l2ClosingText = null;
  }

  /**
   * Display mode transition feedback to user
   * Shows a visual divider in the chat when switching between modes
   *
   * @private
   */
  async _showModeTransitionFeedback(previousMode, nextMode, transitionData) {
    if (!previousMode) {
      return;
    }

    const displayName = nextMode.name.charAt(0).toUpperCase() + nextMode.name.slice(1).toLowerCase();
    const prev = previousMode.name.toUpperCase();
    const next = nextMode.name.toUpperCase();

    // Same-mode: only show divider for Advice "New Topic" restart
    if (prev === next) {
      if (next === 'ADVICE' && (transitionData.newSession || transitionData.topicTransition)) {
        this.messageList.addDividerMessage('Discussing New Topic');
      }
      return;
    }

    // Cross-mode: show divider for Discovery ↔ Advice, Orientation → Advice, and Orientation → Discovery transitions
    // User-facing modes are "Session" (advice, treatment, insight) and "Discovery"
    if ((prev === 'DISCOVERY' && next === 'ADVICE') || (prev === 'ADVICE' && next === 'DISCOVERY') || (prev === 'ORIENTATION' && next === 'ADVICE') || (prev === 'ORIENTATION' && next === 'DISCOVERY')) {
      // The orientation → advice handoff names both halves of the seam. Every
      // other divider announces only what is starting, but this is the one edge
      // where something the user has been working through for several minutes
      // also ends, and the transcript is the only place that survives to say so
      // (orientation is frontend-only, so the header's completion copy is gone
      // the moment the advice strategy activates).
      const orientationAdvice = prev === 'ORIENTATION' && transitionData.entryType === 'orientation_advice';
      const label = next === 'DISCOVERY'
        ? 'Starting Discovery'
        : (orientationAdvice ? 'Orientation Complete · Starting Session' : 'Starting Session');
      const suppressScroll = orientationAdvice;
      this.messageList.addDividerMessage(label, { scroll: !suppressScroll });
    }
  }

  /**
   * Build context object for message processing
   */
  buildContext(additionalContext = {}) {
    return {
      mode: this.currentMode?.name,
      modeState: this.currentMode?.state,
      userId: this.userId,
      sessionId: this._getSessionId(), // Get dynamically
      messageHistory: this.messageList.getMessageHistory(),
      ...additionalContext
    };
  }

  /**
   * Display a programmatic message (not triggered by user input)
   * Used for welcome messages, system notifications, mode transitions, etc.
   *
   * @param {string} text - Message text to display
   * @param {Object} options - Display options
   * @param {string} options.messageType - Message type for UI behavior (e.g., 'system' to suppress action bar)
   *
   * Note: Thinking indicator is handled separately:
   * - For mode switches: activation in switchMode(), deactivation in MessageRenderer.finish()
   */
  async displayProgrammaticMessage(text, options = {}) {
    const { messageType } = options;

    try {
      // Display message
      // Note: MessageRenderer.finish() handles thinking indicator deactivation
      if (text) {
        this.chatManager.displayAssistantMessage(text, { messageType });
      }

      this.eventBus.emit('message:complete');

    } catch (error) {
      console.error('[ModeOrchestrator] Error displaying programmatic message:', error);
      this.chatManager.displayError('Failed to display message');
    }
  }



  /**
   * Get current mode name
   */
  getCurrentMode() {
    return this.currentMode?.name;
  }

  /**
   * Get mode history
   */
  getModeHistory() {
    return [...this.modeHistory];
  }

  /**
   * Return to dashboard after session ends.
   * Resets mode state and emits dashboard:show event.
   *
   * State after calling:
   * - currentMode: null (ready for new intent detection)
   * - Session: null (cleared via SessionManager)
   * - Header: Hidden
   * - Dashboard: Visible
   */
  returnToDashboard() {
    // Before any state is torn down: a report conversation the user never typed
    // their report in is discarded outright rather than left behind as an
    // opener-plus-greeting row. Must run FIRST — the predicate reads the live
    // session id and transcript, both of which this method clears below.
    this.discardEmptyCheckinSession();

    // Abort any in-flight streaming response — the session is ending
    this.chatManager?.abandonStream();

    // Drop any pending discovery callback (L2 takeaways-validation or L3 post-
    // completion hook). If the user navigates home mid-flow, the callback must
    // not fire against a dead session or stale section context.
    this._pendingDiscoveryCallback = null;

    // Clear any active input overlay (questionnaire may have been open)
    this.eventBus.emit('input:enabled');

    // Dropped BEFORE the panel close, not after: `clearChat()` below wipes the
    // transcript the completion group belonged to, so navigating away is never a
    // ceremony resolution and a close fired on the way out must mount nothing.
    // With the phase already null AND the card ref nulled, a resolution
    // callback reaching `resolveSharingDecision` by any route is a no-op (it
    // returns early on a null phase) — the bare `right-panel:close` below
    // resolves a live ceremony (succeeded: false) while we're mid-navigation,
    // and this ordering is what keeps that resolution inert.
    this.clearSharingDecisionPhase();

    // Close any open right panel. Chat is not a routed section, so neither the
    // per-section deactivate() nor the router's pathname-change backstop fires
    // on chat → dashboard (both live at '/'). returnToDashboard() is chat's
    // teardown hook, so close the panel here using the same event sections use.
    this.eventBus.emit('right-panel:close');

    // A chat-owned panel INSTANCE outlives that emit — drop the sharing-ceremony
    // launcher (listener + panel + module reference).
    this._teardownChatOwnedPanels();

    // Reset mode and session state. Use the helper so listeners deriving
    // UI state from mode events (header label, input placeholder, button
    // visibility) reset to dashboard state instead of staying stale on the
    // previous mode.
    this._setCurrentMode(null, { name: 'mode:cleared' });
    sessionManager.resetSession();

    // Clear stale chat messages so re-entry starts fresh
    this.chatManager?.clearChat();

    // Hide the header
    if (this.headerContentService) {
      this.headerContentService.hide();
    }

    console.log('[ModeOrchestrator] Returned to dashboard, mode and chat reset');
  }

  /**
   * Abandoned-report policy: discard a check-in (report) session the user
   * navigated away from without typing their report.
   *
   * Entering a check-in auto-sends the opener (`TreatmentMode.CHECKIN_OPENER`)
   * and streams the assistant's greeting, so an abandoned entry holds one user
   * row and at most one assistant row. Left alive, that row is restored as a
   * report-less chat on return and shows up in the sessions list; the next
   * session the user starts has to close it. Deleting it makes both moot.
   *
   * Predicate: fewer than two `role:'user'` rows in the live history ⇒ discard.
   * A count, not authorship metadata (history entries carry none): the opener
   * is exactly one plain user row both live and on replay
   * (`messageReplayPolicy.classifyUserReplayMessage` shows it), turn 0 exposes
   * no tools so no button/echo row can precede the typed report, and silent
   * sends push no history entry (`MessageList.addUserMessage` returns null on
   * empty text). The daily-limit and Stop paths remove the opener bubble,
   * leaving zero rows — still a discard, which is correct. Any second user row
   * is therefore the typed report and the session is kept.
   *
   * The reference card is not a message: it is prepended to the first assistant
   * reply, so a card pinned without a reply cannot exist, and interactive
   * components (button groups) do not count as either voice speaking.
   *
   * Client state is reset SYNCHRONOUSLY, before the network delete, so a caller
   * mid-navigation (the router's save-for-return) cannot observe — and stash —
   * a session that is on its way out. The greeting's stream is aborted first:
   * the router-middleware exit (`router.js`) calls this method alone, and
   * without the abort the SSE keeps streaming onto a cleared chat and the
   * backend's STOPPED_BY_USER job surfaces as a retry banner on a page the user
   * has left.
   *
   * Disarmed after restore by design: `_treatmentSubMode` is set only by
   * `switchMode`, so a reloaded check-in is never discarded client-side — the
   * backend's check-in-scoped ABANDONED close status covers reload-then-leave.
   *
   * Scoped to `subMode === 'checkin'`: a BUILD treatment session opens with a
   * welcome turn, and every other mode's empty session is either resumable or
   * already handled by its own lifecycle.
   *
   * @returns {boolean} True when a session was discarded
   */
  discardEmptyCheckinSession() {
    if (this.currentMode?.name !== 'TREATMENT') return false;
    if (this._treatmentSubMode !== 'checkin') return false;

    const sessionId = sessionManager.getCurrentSessionId();
    if (!sessionId) return false;

    const history = this.messageList?.getMessageHistory?.() || [];
    const userEntries = history.filter(e => e.role === 'user').length;
    if (userEntries >= 2) return false;

    console.log('[ModeOrchestrator] Discarding abandoned check-in session:', sessionId);

    this.chatManager?.abandonStream();
    this._setCurrentMode(null, { name: 'mode:cleared' });
    sessionManager.resetSession();
    this.chatManager?.clearChat();

    // Fire-and-forget: the client has already let go of the session, and
    // `API.deleteSession` swallows network errors. A row that survives a failed
    // delete is still harmless — the backend's orphan cleanup collects it.
    API.deleteSession(sessionId).then((result) => {
      if (result?.deleted) {
        this.eventBus.emit('session:deleted', { sessionId });
        return;
      }
      console.warn('[ModeOrchestrator] Empty check-in session delete did not confirm:', sessionId);
    });

    return true;
  }

  /**
   * Clear mode display (header) without switching mode or creating session.
   * Used when navigating to dashboard - preserves session for potential return.
   *
   * State after calling:
   * - currentMode: Still set (e.g., 'ADVICE')
   * - Session: Still active
   * - Header: Hidden
   * - Dashboard: Visible
   */
  clearModeDisplay() {
    // The THIRD chat exit, and the one with no backstop of its own: chat and
    // the dashboard share pathname '/', so neither a section `deactivate()` nor
    // the router's pathname-change close fires here. Without this a ceremony
    // panel opened in chat stays on screen over the dashboard.
    //
    // The deferred phase 3 deliberately SURVIVES this exit (unlike switchMode /
    // returnToDashboard): the session and the transcript are preserved, so the
    // sharing decision is still live and must still be answerable — and
    // answering it must still produce the completion actions.
    this._teardownChatOwnedPanels();

    // The teardown above force-retires a live ceremony WITHOUT firing its
    // resolution callback, and this exit keeps the transcript — so this is the
    // one exit that has to re-offer the decision itself. Two halves:
    //
    //   1. Revert the card ONLY if it is currently 'open'. A 'pending' card
    //      belongs to a decision never begun; relabelling it "Not finished"
    //      beside a row that still reads "Choose what to share" would be a
    //      lie. A 'completed' one already nulled its ref.
    //   2. Re-mount the binary row. Opening the queue hid it (the input group
    //      is torn down on click), so without this the user comes back from
    //      the dashboard to a card, an empty composer, and no forward action.
    //      `resumed` mirrors the card for the same reason it is gated above:
    //      a decision never begun must come back offering "Choose what to
    //      share", not "Begin again".
    const decisionWasOpened = this._sharingInvitationCardEl?.dataset?.state === 'open';
    if (decisionWasOpened) {
      this.setSharingInvitationCardState('unfinished');
    }
    if (this._completionPhase && this._sharingDecisionOpen) {
      this._showSharingDecisionButtons({ resumed: decisionWasOpened });
    }

    // Hide the header without triggering mode switch
    if (this.headerContentService) {
      this.headerContentService.hide();
    }

    console.log('[ModeOrchestrator] Mode display cleared, session preserved:',
      this.currentMode?.name, sessionManager.getCurrentSessionId());
  }

  /**
   * Restore a mode without creating a new session.
   * Used when returning from dashboard to an existing session.
   *
   * Unlike switchMode(), this:
   * - Does NOT create a backend session
   * - Does NOT call full lifecycle hooks
   * - Emits mode:restored (not mode:changed)
   *
   * @param {string} modeName - Mode to restore
   * @param {Object|null} [sessionMeta] - Session metadata already fetched by
   *   the caller (e.g. resumeSession's broadened fetch). When omitted and
   *   modeName is TREATMENT/ADVICE, fetched here instead — those modes'
   *   getModeLabel() needs subMode/variant/entryType, which live in session
   *   metadata and (unlike switchMode's transitionData) aren't otherwise
   *   available across a restore.
   */
  async restoreMode(modeName, sessionMeta = null) {
    let mode = this.modes.get(modeName);
    if (!mode) {
      // Defensive degrade (Stage 2 §B-3): a persisted returnSession or history
      // row can name a mode this build no longer registers. Fall back to ADVICE
      // — the general-purpose mode — rather than throwing; the caller's own
      // fallback (router.restoreReturnSession → switchMode('ADVICE')) would land
      // in the same place, but without the header ever being restored.
      console.warn(`[ModeOrchestrator] Unknown mode '${modeName}' on restore — degrading to ADVICE`);
      modeName = 'ADVICE';
      mode = this.modes.get('ADVICE');
      if (!mode) {
        // No ADVICE registration either: nothing safe to restore. Leave the
        // display as-is (the caller drops back to dashboard) instead of throwing.
        console.error('[ModeOrchestrator] ADVICE fallback unavailable — restore skipped');
        return;
      }
    }

    if (sessionMeta === null && (modeName === 'TREATMENT' || modeName === 'ADVICE')) {
      const sessionId = sessionManager.getCurrentSessionId();
      if (sessionId) {
        try {
          const info = await API.getSessionInfo(sessionId);
          sessionMeta = info?.metadata || null;
        } catch (error) {
          console.warn('[ModeOrchestrator] Failed to fetch session metadata for restore:', error.message);
        }
      }
    }

    // Update current mode reference
    this.currentMode = mode;

    // Call onRestore BEFORE activating the header (thinking-lines-header-plan
    // §2.3 Critical #3 — matches switchMode's onEnter-before-activate
    // ordering): onRestore threads subMode/variant/entryType — and, for
    // AdviceMode, stashes sessionMeta?.descriptor as the title — into the
    // mode instance so the header strategy's `mode:enter` render (fired by
    // activateMode below) sees it immediately instead of falling back to
    // stale/fallback text. Verified safe: TreatmentMode's onRestore only sets
    // local fields, no header/DOM reads.
    if (mode.onRestore) {
      await mode.onRestore(sessionMeta);
    }

    // Re-activate header display for this mode
    if (this.headerContentService) {
      this.headerContentService.activateMode(mode);
    }

    // Emit restoration event (distinct from mode:changed)
    this.eventBus.emit('mode:restored', {
      mode: modeName,
      sessionId: sessionManager.getCurrentSessionId()
    });

    console.log('[ModeOrchestrator] Mode restored:', modeName);
  }

  /**
   * Resume a past session into the live chat on a cold boot.
   *
   * Orthogonal to `switchMode` (creates a new backend session) and
   * `restoreMode` (re-activates the mode header without loading messages).
   * This method does both: attaches local sessionManager state to the
   * existing session, restores the mode header, and replays the session's
   * messages into the chat UI. Used after a Stripe Checkout roundtrip wipes
   * in-memory JS state, and available for any flow that needs literal-resume
   * semantics (e.g. a future "Continue exact conversation" button).
   *
   * Emits `session:resumed` on success so downstream services (analytics,
   * usage banner, header shimmer) can react.
   *
   * @param {string} mode - Session type ('ADVICE' | 'TREATMENT' | 'DISCOVERY')
   * @param {string} sessionId - Existing session ID to hydrate
   * @returns {Promise<{ mode: string, sessionId: string, messageCount: number }>}
   *   `mode` is the mode actually resumed, which may be the ADVICE degrade below.
   * @throws {Error} When the session can't be loaded or isn't ACTIVE
   *   (err.code === 'SESSION_NOT_ACTIVE').
   */
  async resumeSession(mode, sessionId) {
    if (!this.modes.has(mode)) {
      // Defensive degrade (Stage 2 §B-3): a checkout-return snapshot or session
      // card can name a mode this build no longer registers. The session id is
      // still resumable — the backend serves its messages by id regardless of
      // type — so resume it under ADVICE (the widest read path: metadata fetch,
      // pill hydration, input re-arm) instead of throwing. Reassigning `mode`
      // keeps the emitted `session:resumed` payload honest about what was
      // resumed. If ADVICE isn't registered either, restoreMode's own degrade
      // below skips the header restore rather than throwing.
      console.warn(`[ModeOrchestrator] Unknown mode '${mode}' on resume — degrading to ADVICE`);
      mode = 'ADVICE';
    }
    if (!sessionId) {
      throw new Error('resumeSession requires a sessionId');
    }

    // `handleResponse` already unwraps { success, data } → data — so `result`
    // IS { messages, sessionStatus }, not { data: { ... } }.
    const result = await API.getSessionMessages(sessionId);
    if (!result?.messages) {
      throw new Error(`Failed to load session ${sessionId}`);
    }
    // Private session (Privacy Mode V1, P5 marker): the messages array is
    // ALWAYS empty for a private session, so the `!result?.messages` guard
    // above passes right through it. Treat as SESSION_NOT_ACTIVE-equivalent —
    // resuming would otherwise open a blank ACTIVE session with no privacy UI
    // (P9-pre reset would immediately strip any privacy affordance anyway).
    if (result.privacyMode === true) {
      const err = new Error(`Session ${sessionId} is private and cannot be resumed`);
      err.code = 'SESSION_NOT_ACTIVE';
      throw err;
    }
    // Guard against a closed/abandoned session (e.g. the session was closed
    // between the resume affordance rendering and the click, an admin close, or
    // a race). We CAN replay the messages, but the user can't send into a
    // non-ACTIVE session, so bail out early and let the caller fall back.
    if (result.sessionStatus !== 'ACTIVE') {
      const err = new Error(`Session ${sessionId} is ${result.sessionStatus}`);
      err.code = 'SESSION_NOT_ACTIVE';
      throw err;
    }

    // For ADVICE/TREATMENT, fetch session metadata on resume so the
    // header's static mode label (subMode/variant) is correct instead of
    // falling back to unset; ADVICE also seeds the resumed stance into
    // sessionManager. Non-fatal: on failure the label simply falls back until
    // the next turn. (Standard reload drops to dashboard; this resume path is
    // the Stripe return and the "resume active session" card.)
    let sessionMeta = null;
    // Backend-derived transient Advice input-rearm projection (advice-unified-
    // terminus §3.8 / D5): the single due input group, a pending-intent recovery
    // descriptor, or null — computed server-side (coupling + route facts +
    // transcript supersession). The SPA NEVER derives cascade membership/coupling;
    // it only consumes this after history replay (below).
    let adviceInputRearm = null;
    // Sibling transient projection on the same fetch: what the chat-input Create
    // menu may offer right now, or null.
    let adviceCreateMenu = null;
    if (mode === 'ADVICE' || mode === 'TREATMENT') {
      try {
        const info = await API.getSessionInfo(sessionId);
        sessionMeta = info
          ? {
              ...(info.metadata || {}),
              starred: info.starred === true,
              sessionNoteId: info.sessionNoteId || null
            }
          : null;
        adviceInputRearm = info?.adviceInputRearm || null;
        adviceCreateMenu = info?.adviceCreateMenu || null;
      } catch (error) {
        console.warn('[ModeOrchestrator] Session metadata fetch failed during resume:', error);
        sessionMeta = null;
      }
    }
    if (mode === 'ADVICE' && sessionMeta === null) {
      console.warn('[ModeOrchestrator] proposal re-arm skipped: no session metadata');
    }

    // Attach sessionManager to the existing session (pure-local; no backend
    // call). Uses explicit mode arg — `attachToSession`'s JSDoc defaults to
    // INTAKE but the body is mode-agnostic.
    await sessionManager.attachToSession(sessionId, mode, sessionMeta);

    // Rehydrate agent-surfaced persistent pills (chat-input-redesign Phase 3, Option A):
    // the durable set lives in session.metadata.activePills. This resume path is the ONLY
    // place pills are restored across a reload (a plain refresh drops to dashboard; the
    // SPA holds them in memory across turns). ChatManager stays for live SSE/poll
    // pendingAction dispatch. Non-fatal — a bad/empty set just leaves the row without
    // agent pills.
    // NOTE: this hydration runs BEFORE loadHistoryMessages below — load-bearing
    // ordering: the replayed completion cards (takeaway/practice/memo) live-retire
    // their pill only when it is actually standing, so hydrate-then-replay is the
    // resume self-heal for a pill whose durable retire was lost.
    const MEMO_ENTRY_EXCLUDED_PILLS = new Set(['build-a-practice']);
    const isMemoEntry = sessionMeta?.entryType === 'memo_entry';
    // Defensive voice-correction filter (advice-unified-terminus B1.6): the
    // backend derivation is authoritative (it durably clears stale rows), but a
    // legacy/stale correction row must never render for a session whose window is
    // closed — the retired marker is set, or the stance is already sticky
    // (user_explicit/agent_offer), or it is a memo_entry (never gets correction).
    const CORRECTION_PILL_GROUPS = new Set(['voice-correction-explore', 'voice-correction-act']);
    const stanceSource = sessionMeta?.stanceSource;
    const correctionClosed = sessionMeta?.adviceVoiceCorrectionRetiredAtMessageCount != null
      || isMemoEntry
      || (stanceSource != null && stanceSource !== 'entry_default' && stanceSource !== 'inferred');
    const activePills = (Array.isArray(sessionMeta?.activePills) ? sessionMeta.activePills : [])
      .filter((pill) => {
        if (isMemoEntry && MEMO_ENTRY_EXCLUDED_PILLS.has(pill?.groupId)) return false;
        if (correctionClosed && CORRECTION_PILL_GROUPS.has(pill?.groupId)) return false;
        return true;
      });
    this.actionRouter?.syncPillRowFromGroups?.(activePills.map((pill) => pill?.groupId).filter(Boolean));

    // Re-derive the memo_entry suppression flag (terminus D8) for resumed
    // sessions — switchMode sets it on entry, but resume bypasses switchMode.
    this._memoEntrySession = sessionMeta?.entryType === 'memo_entry';

    // Restore mode header + fire mode.onRestore hook.
    await this.restoreMode(mode, sessionMeta);

    // Replay messages into the chat.
    this.chatManager?.loadHistoryMessages?.(result.messages);

    // Consume the backend adviceInputRearm projection (D5). This is the ONE resume
    // rearm path for ADVICE — it folds the former live practice/memo proposal
    // rearm (precedence step 3 of deriveAdviceInputRearm, guarded server-side by
    // the "card still last" transcript fact) together with the post-takeaway,
    // complement, and conclusion groups, and the pending-intent recovery. The
    // frontend derives nothing: it mounts the projected group or fires ONE
    // idempotent recovery turn.
    // Restore the Create menu from the same projection. After restoreMode, whose
    // `mode:changed`-family reset would otherwise clear it.
    if (mode === 'ADVICE') {
      applyAdviceCreateMenu(adviceCreateMenu);
    }

    if (mode === 'ADVICE' && adviceInputRearm) {
      if (adviceInputRearm.kind === 'group' && adviceInputRearm.groupId) {
        await this.actionRouter?.rearmButtonGroup?.(
          adviceInputRearm.groupId,
          adviceInputRearm.input || {}
        );
      } else if (
        adviceInputRearm.kind === 'intent_recovery'
        && adviceInputRearm.intentId
        // Guard against a double-fire within this session view; cross-tab /
        // duplicate safety comes from backend re-validation (a stale intent id
        // falls through to an ordinary turn). Never resurrects the consumed pill.
        && !this._adviceIntentRecoveryFiredKeys.has(`${sessionId}:${adviceInputRearm.intentId}`)
      ) {
        const recoveryKey = `${sessionId}:${adviceInputRearm.intentId}`;
        const recoveryAccepted = await this.handleUserMessage('[artifact_intent_recovery]', {
          // Deterministic key (plan §3.2): a refresh or a second tab that fires
          // this same recovery dedupes to the same ChatJob server-side rather
          // than minting a duplicate turn. The in-memory composite-key set
          // guard is the cheap first check within one view; this is the durable
          // cross-tab/refresh dedupe.
          idempotencyKey: `advice-intent-recovery:${sessionId}:${adviceInputRearm.intentId}`,
          metadata: {
            source: 'button_click',
            silent: true,
            systemTrigger: true,
            skipCounting: true,
            artifactIntentRecovery: adviceInputRearm.intentId
          }
        });
        // Test doubles historically resolve undefined; only an explicit false
        // means submission failed and should remain retryable on the next resume.
        if (recoveryAccepted !== false) {
          this._adviceIntentRecoveryFiredKeys.add(recoveryKey);
        }
      }
    }

    // Note: page visibility is the router's concern. Callers (currently
    // handleBaseRoute Priority 1.5) must `showChatPage(app)` BEFORE awaiting
    // resumeSession. Reaching into `window.app` from here used to race
    // initRouter on cold checkout return — the optional chain would silently
    // no-op if `window.app` wasn't yet bound, leaving the user on dashboard
    // with chat state in memory.

    const payload = {
      mode,
      sessionId,
      messageCount: result.messages.length
    };
    this.eventBus.emit('session:resumed', payload);

    console.log(
      `[ModeOrchestrator] Resumed session ${sessionId} (${mode}) with ${payload.messageCount} messages`
    );

    return payload;
  }

  /**
   * Check if mode is currently being displayed (not on dashboard).
   * Useful for components that need to know display state vs logical mode.
   *
   * @returns {boolean} True if mode header is visible
   */
  isDisplayingMode() {
    return !window.app?.modules?.pageManager?.isDashboardVisible();
  }
}
